<template>
  <section class="view-container">
      <portal :to="headerPortal" :disabled="headerPortal === ''">
        <a @click.stop.prevent="onStockNoClick(currentInventory)">
          <h1 class="title is-size-5 has-text-link has-text-weight-semibold">{{getTitleOverview}}</h1>
        </a>
      </portal>
      <div>
        <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage"></error-display-component>
        <div v-if="currentInventory.id !== 0">
          <carfax-process-status-header :vinNo="currentInventory.vinNo" :fullMode="false" :inventoryId="currentInventory.id" :carfaxRequested="carfaxRequested" :topPosition="''"></carfax-process-status-header>
          <div class="box" :class="{'watermarked': isObsoleteStatus}" :data-watermark="obsoleteWatermarkText" v-if="!isLocating">
            <section-header :title="buildInventorySectionTitle(currentInventory)" :collapsed="isDetailsCollapsed" :overview="getInventoryOverview" style="position: relative; z-index:2000" v-on:collapsed-status-changed="(val) => isDetailsCollapsed = val">
            </section-header>
            <div class="columns" v-show="false">
              <div class="column is-12">
                <div class="field">
                  <label class="label">Dealer</label>
                  <div class="field">
                    {{currentInventory.dealerName}}
                  </div>
                </div>
              </div>
            </div>
            <div v-if="currentInventory.arrivalSkipped && currentInventory.possessionStatus === 'Inbound'" class="notification is-danger is-flex">
              <span>
                <i class="fas fa-exclamation-triangle fa-2x"></i>
              </span>
              <span class="ml-10">
                WARNING: Trade vehicle arrival was skipped by <span class="has-text-weight-semibold">{{ currentInventory.arrivalSkippedBy }}</span>. This vehicle requires arrival and inspection.
              </span>
            </div>
            <div class="columns is-mobile" style="position:relative; z-index:2000">
              <div class="column is-vcentered" v-if="isTypePpaAppraisal">
                <i class="fal fa-eye fa-2x" :title="getPpaExaminedTitle" :class="getPpaExaminedColourClass"></i>
              </div>
              <div class="column is-vcentered" v-if="hasPpaRecord">
                <a @click="onViewOriginalPpa()" title="Redirect to original pre-purchase appraisal record">
                  <i class="fal fa-arrow-alt-circle-left fa-2x has-text-success" />
                </a>
              </div>
              <div class="column is-vcentered" v-if="isTypePpaAppraisal && isArchived && convertedInventoryId">
                <a @click="onViewCurrentInventory()" title="Redirect to converted inventory record">
                  <i class="fal fa-arrow-alt-circle-right fa-2x has-text-success" />
                </a>
              </div>
              <div class="column is-vcentered">
                <!-- ⚠ on-new-carfax was UNBOUND here, which is why refreshing CARFAX from the quick-view
                     was a dead end: the toast fired and nothing could ever follow it. The handler
                     below acts only on a refresh the operator asked for - :autoUpdateInventory is
                     true, so the mount path emits on any record whose report has expired. -->
                <car-fax-control :key="currentInventory.id" :inventory="currentInventory" :autoUpdateInventory="true" :autoRequestWhenMissing="true" :carFaxRating="currentInventory.carFaxRating" :vinNo="currentInventory.vinNo" :hideIfEmpty="false"  :purchasedFromType="currentInventory.purchasedFromType" :inventoryType="selectedInventoryStatusType" v-on:on-carfax-requested="onCarFaxRequested" v-on:on-new-carfax="onCarFaxVehicleInformation" :resolveActionDisabled="false"></car-fax-control>
              </div>
              <div class="column is-vcentered">
                <a v-if="hasVautoAppraisal" :href="currentInventory.vAutoAppId" target="_vauto" title="Click to view the latest vAuto report for this vehicle."><img src="@/assets/static/vauto/vauto_logo_small.png" style="width:90px; height:auto;"></a>
                <img v-else title="vAuto report is pending" src="@/assets/static/vauto/vauto_logo_small.png" style="width:90px; height:auto;" class="img-disabled">
              </div>
              <div class="column is-vcentered">
                <auto-check-control
                v-if="autoCheckEnabled"
                :key="currentInventory.id"
                :inventory="currentInventory"
                :autoUpdateInventory="true"
                :autoCheckRating="currentInventory.autoCheckRating"
                :vinNo="currentInventory.vinNo"
                :purchasedFromType="currentInventory.purchasedFromType"
                :inventoryType="selectedInventoryStatusType"
                ></auto-check-control>
              </div>
              <div class="column is-vcentered" v-if="isPreEntry && canShowPreEntry">
                <i class="fal fa-car-tilt fa-2x" title="Pre-Entry"></i>
              </div>
            </div>
            <div class="columns is-mobile is-multiline" v-if="currentInventory">
              <div class="column is-6">
                <div class="field">
                  <label class="label">
                    Vehicle 
                    <span v-if="currentInventory.cpoStatus" v-tooltip="getCpoStatusTooltip(currentInventory)" :class="getColorClassByCpoStatus(currentInventory)">
                      - {{ cpoStatusLabel }}
                      <i v-if="currentInventory.cpoStatus" class="icon fal fa-lg status-icon" :class="getIconByCpoStatus(currentInventory)" style="margin-right: 4px;"/>
                    </span> 
                    <inventory-sales-description-component :currentInventory="currentInventory"></inventory-sales-description-component>
                  </label>
                  <div class="field">
                    <span class="is-inline-block" v-html="makeOverview(currentInventory)"></span>
                  </div>
                </div>
              </div>
              <div class="column is-6" style="position: relative; z-index: 2000; display: inline-block;" :key="currentInventory.id">
                <div class="field">
                  <label class="label">Preview</label>
                  <figure class="image is-100x100" :class="{ clickable: currentInventory.hasVehicleImages }" style="padding: 0, 0; margin: 0, 0; float: left;" @click="openInventoryImageViewer(currentInventory)">
                    <img :src="getImage(currentInventory)"/>
                  </figure>
                  <div class="quick-action" style="max-width: 50px; float: right; border: none; padding: 0, 0; margin: 0, 0;" v-if="currentInventory.hasVehicleImages">
                    <button type="button" class="button" @click="removeAllImages()" v-tooltip="'Remove all uploaded images.'">
                      <i data-type="D" class="icon fal fa-trash-alt fa-lg has-text-danger"/>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" v-if="currentInventory && (isPreEntry || isTypePpaAppraisal)">
              <div class="column is-6">
                <div class="field">
                  <label class="label">Proposed Purchased Cost</label>
                  <div class="field">
                    <span v-if="currentInventory.proposedCost">{{ currentInventory.proposedCost | currency }}</span>
                    <span v-else>-</span>
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <label class="label">Proposed Purchased Cost Expiration Date</label>
                <div class="field">
                  {{ currentInventory.proposedCostExpirationDate | formatDate }}
                </div>
              </div>
            </div>

            <div class="columns is-mobile" v-if="currentInventory && isTypePpaAppraisal">
              <div class="column is-6">
                <div class="field">
                  <label class="label">PPA Intention</label>
                  <div class="field">
                    <span v-if="currentInventory.ppaIntention">{{ currentInventory.ppaIntention }}</span>
                    <span v-else>-</span>
                  </div>
                </div>
              </div>
              <div class="column is-6" v-if="currentInventory.ppaIntention === 'Trade'">
                <label class="label">Proposed Trade Allowance</label>
                <div class="field">
                  <span v-if="currentInventory.proposedTradeAllowance">{{ currentInventory.proposedTradeAllowance | currency }}</span>
                  <span v-else>-</span>
                </div>
              </div>
            </div>

            <div class="columns is-mobile clickable" v-if="currentInventory" @click="onViewToPricingPod" style="position:relative; z-index:2000">
              <div class="column is-6">
                <div class="field">
                  <label class="label">Asking Price</label>
                  <div class="field">
                    <span v-if="currentInventory.askingPrice">{{ currentInventory.askingPrice | currency }}</span>
                    <span v-else>-</span>
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label">Lease Price</label>
                  <div class="field">
                    <span v-if="currentInventory.leasePrice">{{currentInventory.leasePrice | currency}}</span>
                    <span v-else>-</span>
                  </div>
                </div>
              </div>
            </div>

            <div v class="columns is-mobile" v-show="false">
              <div class="column is-6">
                <div class="field">
                  <label class="label">Vin No</label>
                  <div class="field">
                    {{currentInventory.vinNo}}
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label">Stock No</label>
                  <div class="field">
                    <a @click.stop.prevent="onStockNoClick(currentInventory)">
                      <span class="has-text-weight-semibold" :title="currentInventory.stockNo">{{ currentInventory.stockNo }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile">
              <div class="column is-6">
                <div class="field">
                  <label class="label">Status</label>
                  <div class="field" :class="{'striked': isObsoleteStatus}">
                    {{ getSoldDeliveredStatus }}
                  </div>
                </div>
              </div>
              <div class="column is-6" v-if="currentInventory.returnedAt">
                <div class="field">
                  <label class="label">Returned At</label>
                  <div class="field">
                    {{currentInventory.returnedAt | formatDate}}
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" style="position:relative; z-index:2000">
              <div class="column is-6" ref="usage">
                <div class="is-pulled-left" v-if="currentInventory.vinUsageHistory.length > 0">
                  <label class="label is-inline-block is-size-7">Mileage</label>
                  <br/>
                  <usage-history-component
                    :showEditIcon="!isPpaClosed"
                    :inventoryId="currentInventory.id"
                    :vinNo="currentInventory.vinNo"
                    :vinUsageHistory="currentInventory.vinUsageHistory"
                    :usage="currentInventory.usage"
                    :usageRequired="usageRequired"
                    :minimumUsageAllowed="currentInventory.mileageEstimate ? 0 : minimumUsageAllowed"
                    v-on:usage-updated="usageComponentUpdateHandler">
                  </usage-history-component>
                </div>
              </div>
              <div class="column is-2">
                <div class="is-pulled-left">
                  <label class="label is-inline-block is-size-7">Damage</label>
                  <br/>
                  <i class="icon fal fa-lg status-icon" :class="getIconByLastDamageStatus(currentInventory)" :title="'Damage History: ' + getLastDamageDescription(currentInventory)"/>
                </div>
              </div>
              <div class="column is-2">
                <div class="is-pulled-left">
                  <label class="label is-inline-block is-size-7">Sticker</label>
                  <br/>
                  <window-stick-status-capture :currentInventory="currentInventory" :key="currentInventory.id" :inventoryQuickViewMode="true"></window-stick-status-capture>
                </div>
              </div>
              <div class="column is-2">
                <div class="is-pulled-left">
                  <label class="label is-inline-block is-size-7">Publish</label>
                  <br/>
                  <i class="icon fal fa-lg status-icon" :class="getIconByPublishStatus(currentInventory)"/>
                </div>
              </div>
            </div>

            <div class="columns is-mobile">
              <div class="column is-6">
                <field-rotator v-model="locationList" :enableSlotForIndex="1" >
                  <i class="fal fa-pencil fa-lg clickable is-pulled-right" title="Click to edit the current location" style="margin-right: 10px; margin-top: 3px;" @click.stop.prevent="onEditLocation" v-if="hasFeatureAccess(updateInventoryLocationPermission)"></i>
                </field-rotator>
              </div>
              <div class="column is-6" v-if="canShowPickupLocation">
                <div class="field">
                  <label class="label">Pickup Location</label>
                  <div class="field">
                    {{ currentInventory.pickupLocation || "-" }}
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" style="position: relative; z-index: 3000;">
              <div class="column is-6">
                <div class="field">
                  <label class="label">
                    Purchased From
                    <dealer-peak-control v-if="isIndividual" :key="currentInventory.purchasedFromId" :customerId="currentInventory.purchasedFromId"></dealer-peak-control>
                    <dp-resync-component v-if="(currentInventory.purchasedFromId > 0) && isIndividual" :id="currentInventory.purchasedFromId" :mobile="currentInventory.purchasedFromContactMobile" :email="currentInventory.purchasedFromContactEmail"></dp-resync-component>
                  </label>
                  <div class="field">
                    <a class="is-link" @click="onViewPurchasedFrom">
                      {{currentInventory.purchasedFromName || "-"}}
                    </a>
                    <customer-pop-component :key="currentInventory.purchasedFromId" v-if="isIndividual" v-model="currentInventory.purchasedFromId"></customer-pop-component>
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label">Purchased By</label>
                  <div class="field">
                    <span class="icon-text">
                      <click-to-contact :key="'ctc-' + currentInventory.buyerId" :value="buildBuyerInfo(currentInventory)"></click-to-contact>  
                      <span class="icon" v-if="showDealCompletedIcon({dealStatus: primaryDealStatusName, dealGenericStatusName: primaryDealGenericStatusName, isDealBookingFinalised: primaryDealIsBookingFinalised})" v-tooltip="'Booking finalized'">
                        <i style="text-shadow: 0 0 3px #000;" class="grid-icon fa-fw fal fa-check fa-lg has-text-primary is-pulled-right" />
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" style="position:relative; z-index:2000">
              <div class="column is-6">
                <field-rotator v-model="daysList"></field-rotator>
              </div>
              <div class="column is-6">
                <field-rotator v-model="expirationList"></field-rotator>
              </div>
            </div>

            <div class="columns is-mobile" style="position:relative; z-index:2000">
              <div class="column is-12" v-if="showDispositionIntention">
                <div class="field" :title="originalDispositionIntentionDisplayValue">
                  <label class="label">Current Disp. Intention</label>
                  <div class="field is-grouped" style="align-items: baseline; justify-content: space-between;">
                    <div class="field">
                    <i v-show="isCurrentDispositionIntentionExpired" class="fas fa-exclamation-triangle fa-lg has-text-danger"></i>
                    <span>{{ currentDispositionIntentionFormatted}}</span>
                    <inventory-disposition-intention-history-component :currentInventory="currentInventory"></inventory-disposition-intention-history-component>
                    </div>
                    <div class="field">
                      <i v-if="isSavingNewDispositionIntention" class="fas fa-spinner fa-spin fa-lg has-text-success" title="Saving..."></i>
                    </div>
                    <div class="field">
                      <b-field class="dispositionIntentionSwitcher">
                        <b-radio-button :disabled="!hasFeatureAccess('inventory.modify.disposition-intention', false)" v-model="dispositionIntentionValue" size="is-small" native-value="Retail" type="is-success" @input="onDispositionIntentionChanged"><span>Retail</span></b-radio-button>
                        <b-radio-button :disabled="!hasFeatureAccess('inventory.modify.disposition-intention', false)" v-model="dispositionIntentionValue" size="is-small" native-value="Wholesale" type="is-success" @input="onDispositionIntentionChanged"><span>Wholesale</span></b-radio-button>
                      </b-field>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <span v-if="!showDispositionIntention"><br /></span>

            <div class="columns is-mobile" style="position:relative; z-index:2000" v-if="hasFeatureAccess('inventory.publishstatus.modify', false)">
              <div class="column is-6">
                <label class="label">Is Published?</label>
                <div class="field">
                  <b-field class="publishStatusSwitcher">
                    <b-radio-button v-model="publishStatusValue" size="is-small" :native-value="true" type="is-success" @input="isPublishStatusChangeActive = true"><span>Yes</span></b-radio-button>
                    <b-radio-button v-model="publishStatusValue" size="is-small" :native-value="false" type="is-success" @input="isPublishStatusChangeActive = true"><span>No</span></b-radio-button>
                  </b-field>
                </div>
              </div>
            </div>
            <span v-else><br /></span>

            <div class="columns is-mobile">
              <div class="column is-12" v-if="currentInventory.archivedAt">
                <div class="field">
                  <label class="label">Returned</label>
                  <div class="field">
                    <span class="has-text-danger">{{currentInventory.archivedAt | formatDate}}</span>
                  </div>
                </div>
              </div>
            </div>
            <br />
            <div class="columns is-mobile" v-if="isTypePpaAppraisal">
              <div class="column is-6">
                <div class="field">
                  <label class="label">PPA Approved</label>
                  <div class="field">
                    {{ currentInventory.ppaApproved ? "YES" : "NO" }}
                  </div>
                </div>
              </div>
              <div class="column is-6" v-if="currentInventory.ppaApproved">
                <div class="field">
                  <label class="label">PPA Approved At</label>
                  <div class="field">
                    {{ currentInventory.ppaApprovedAt | formatDateOnly }}
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" v-if="isTypePpaAppraisal">
              <div class="column is-6" v-if="currentInventory.ppaApproved">
                <div class="field">
                  <label class="label">PPA Approved By</label>
                  <div class="field">
                    {{ currentInventory.ppaApprovedByName }}
                  </div>
                </div>
              </div>
              <div class="column is-6" v-if="isTypePpaAppraisal">
                <div class="field" v-if="hasPpaReceipt">
                  <file-selector v-model="getPpaReceipt" label="Pre Purchase Receipt" :required="false" :multiple="false" :isBoxed="false" :enabled="false" displaySize="64"></file-selector>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" v-if="isTypePpaAppraisal && currentInventory.ppaExamined">
              <div class="column is-6">
                <div class="field">
                  <label class="label">PPA Examined By</label>
                  <div class="field">
                    {{ currentInventory.ppaExaminedByName }}
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label">PPA Examined At</label>
                  <div class="field">
                    {{ currentInventory.ppaExaminedAt | formatDateOnly }}
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" v-if="currentInventory.sold && currentInventory.soldAt">
              <div class="column is-6">
                <div class="field">
                  <label class="label">Sold At</label>
                  <div class="field">
                    {{ currentInventory.soldAt | formatDateOnly }}
                  </div>
                </div>
              </div>
            </div>

            <div class="columns is-mobile" style="position:relative; z-index:2000">
              <div class="column is-6">
                <div class="is-pulled-left">
                  <label class="label is-inline-block is-size-7">Status</label>
                  <br/>
                  <div class="is-inline-block">
                    <a @click.ctrl.exact.stop.prevent="onViewPrimaryDeal()" @click.exact.stop.prevent="onDealStatusClick()" v-if="showDealCompletedIcon({dealStatus: primaryDealStatusName, dealGenericStatusName: primaryDealGenericStatusName, isDealBookingFinalised: primaryDealIsBookingFinalised})">
                      <i class="far fa-dollar-sign fa-lg fa-fw status-icon" :class="getColorClassByDealItemStatus({dealStatus: primaryDealStatusName, dealGenericStatusName: primaryDealGenericStatusName})" />
                      <i style="text-shadow: 0 0 3px #000; margin-left: -2rem;" class="grid-icon fa-fw fal fa-check fa-lg has-text-white" v-tooltip="getDealStatusTitleForView(primaryDealStatusName, primaryDeal)"/>
                    </a>
                    <a @click.ctrl.exact.stop.prevent="onViewPrimaryDeal() " @click.exact.stop.prevent="onDealStatusClick()" v-else><i class="far fa-dollar-sign fa-lg fa-fw status-icon" :class="getColorClassByDealItemStatus({dealStatus: primaryDealStatusName, dealGenericStatusName: primaryDealGenericStatusName})" v-tooltip="getDealStatusTitleForView(primaryDealStatusName, primaryDeal)"/></a>
                    <a @click.stop.prevent="onServiceRequestStatusClick()"><i class="fal fa-wrench fa-lg fa-fw status-icon" :class="getColorClassByGetReadyStatus(currentInventory.getReadyStatusTypeId)" v-tooltip="buildServiceRequestTitle(currentInventory)"/></a>
                    <a @click.stop.prevent="onAccessoriesStatusClick()"><i class="fal fa-exclamation-circle fa-lg fa-fw status-icon" :class="getColorClassByDetailsStatus(currentInventoryaccessoryDetailStatusType)" v-tooltip="buildAccessoryStatusTitle(currentInventory)"/></a>
                    <i class="fal fa-badge-percent fa-lg fa-fw status-icon" v-tooltip="buildConsignmentStatusTitle(currentInventory)" v-if="isTypeConsigned"/>
                    <i class="fal fa-badge-dollar fa-lg fa-fw status-icon" :class="getColorClassByTitlePayoffStatus(currentInventory)" title="Payoff Status"/>
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="is-pulled-left">
                  <label class="label is-inline-block is-size-7">Title</label>
                  <br/>
                  <title-status-capture :currentInventory="currentInventory" :isListMode="false" :key="'qv' + currentInventory.id"></title-status-capture>
                </div>
                <div class="is-pulled-left" v-if="canShowFleet && currentInventory.isFleet">
                  <label class="label is-inline-block is-size-7">Fleet</label>
                  <br/>
                  <i class="icon fal fa-lg status-icon" :class="getIconByFleetStatus(currentInventory)"/>
                </div>
                <div class="is-pulled-left ml-5" v-if="canShowDemo && currentInventory.isDemo">
                  <label class="label is-inline-block is-size-7">Demo</label>
                  <br/>
                  <i class="icon fal fa-lg status-icon" :class="getIconByDemoStatus(currentInventory)"/>
                </div>
                <div class="is-pulled-left ml-5" v-if="canShowMarketable">
                  <label class="label is-inline-block is-size-7">Marketable</label>
                  <br/>
                  <i class="icon fal fa-lg status-icon" :class="getIconByReadyToMarketStatus(currentInventory)"/>
                </div>
              </div>
            </div>
            <quick-action-container>
              <inventory-sellers-agreement :currentInventory="currentInventory" :key="'S' + currentInventory.id"></inventory-sellers-agreement>
              <inventory-consignment-agreement :currentInventory="currentInventory" :key="'C' + currentInventory.id"></inventory-consignment-agreement>
              <inventory-batch-payment-request :ref="batchPaymentRequestRef" :currentInventory="currentInventory" :key="'P' + currentInventory.id"></inventory-batch-payment-request>
              <inventory-vehicle-verification :currentInventory="currentInventory" :key="'V' + currentInventory.id"></inventory-vehicle-verification>
              <send-pickup-location-control :deal="primaryDeal" :inventory="currentInventory"></send-pickup-location-control>
              <inventory-lost-title v-if="titleIsLost" ref="titleLostAction" :currentInventory="currentInventory" v-on:close-lost-title="onCloseLostTitle" :showDialog="showLostTitleAction" :key="'L' + currentInventory.id" v-on:show-lost-title="onShowLostTitle"></inventory-lost-title>
              <send-trade-in-title-reassignment-documentation :inventoryItem="currentInventory" :key="'TT' + currentInventory.id" :isListMode="false" v-if="isTypeTrade"></send-trade-in-title-reassignment-documentation>
              <service-loaner-out-of-service-action :inventory="currentInventory" :isView="true" v-if="currentInventory" v-on:inventory-service-loaner-updated="reloadInventory"></service-loaner-out-of-service-action>
              <inventory-desk-deal v-if="currentInventory.crmInventoryItemId" :crmInventoryId="currentInventory.crmInventoryItemId"></inventory-desk-deal>
              <lease-return-status :inventoryItem="currentInventory" v-if="currentInventory && isLeaseReturn"></lease-return-status>
              <inventory-recall-resolve-action :currentInventory="currentInventory" :usePortal="true" :isListMode="false"></inventory-recall-resolve-action>
              <inventory-transfer-action :currentInventory="currentInventory" :key="'TR' + currentInventory.id"></inventory-transfer-action>
              <!-- ⚠ THE FREE MATCH CHECK, restored to this screen. Compares the record against the
                   report ALREADY on it, so it asks CARFAX for nothing - where the refresh on the
                   control above costs a fetch, two extractions and twenty to thirty seconds, and
                   cannot compare at all with no live CARFAX session or on a report no longer owned,
                   where it raises the payment prompt instead. Removed as redundant in 4414299be1,
                   which recorded exactly that cost; the other three hosts got it back, this one did
                   not.
                   ⚠ pack="fas" is required - Buefy is registered with defaultIconPack 'fal'.
                   ⚠ The same icon and colours as the edit screen, so it is learned once - the rule
                   itself lives in carfaxMatchIconClass and is deliberately not restated here.
                   ⚠ The TOOLTIP follows canCorrectCarfaxModel: it offers the model choice only to a
                   reader who holds inventory.modify, the same right that shows the comparison's Edit. -->
              <div class="is-inline-block" v-if="canCheckCarfaxMatch">
                <div class="quick-action">
                  <button class="button" type="button" data-test="carfax-match-check" @click="onCarfaxMatchIconClick" v-tooltip="carfaxMatchHoverText">
                    <b-icon pack="fal" icon="car-side" custom-size="fa-lg" :class="carfaxMatchIconClass"></b-icon>
                  </button>
                </div>
              </div>
            </quick-action-container>
          </div>

          <div class="box" v-else>
            <section-header :collapsed="isDetailsCollapsed" title="Overview" v-on:collapsed-status-changed="(val) => isDetailsCollapsed = val"></section-header>
            <div class="columns is-mobile">
              <div class="column is-12">
                <div class="field">
                  <label class="label">Required Vehicle</label>
                  <div class="field">
                    {{ currentInventory.name }}
                  </div>
                </div>
              </div>
            </div>
            <inventory-attribute-view :inventory="currentInventory"></inventory-attribute-view>
          </div>

          <div class="box" ref="pricingPod">
            <section-header title="Pricing" :overview="getPricingOverview" :collapsed="isPricingCollapsed" v-on:collapsed-status-changed="onPricingCollapsedStatusChanged"></section-header>
            <template v-if="isEditLocked">
              <div class="columns is-mobile is-multiline">
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Msrp</label>
                    <div class="field">
                      <span v-if="currentInventory.msrp">{{currentInventory.msrp | currency}}</span>
                      <span v-else>-</span>
                      <inventory-pricing-history-component :currentInventory="currentInventory" pricingType="msrp"></inventory-pricing-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Asking Price</label>
                    <div class="field">
                      <span v-if="currentInventory.askingPrice && hasFeatureAccess('inventory.asking-price.view')">{{currentInventory.askingPrice | currency}}</span>
                      <span v-else>-</span>
                      <inventory-pricing-history-component :currentInventory="currentInventory" pricingType="askingPrice"></inventory-pricing-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Lease Price</label>
                    <div class="field">
                      <span v-if="currentInventory.leasePrice && hasFeatureAccess('inventory.lease-price.view')">{{currentInventory.leasePrice | currency}}</span>
                      <span v-else>-</span>
                      <inventory-pricing-history-component :currentInventory="currentInventory" pricingType="leasePrice"></inventory-pricing-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">GL Balance</label>
                    <div class="field">
                      <span v-if="currentInventory.generalLedgerBalance && hasFeatureAccess('inventory.gl-balance.view')">{{currentInventory.generalLedgerBalance | currency}}</span>
                      <span v-else>-</span>
                      <inventory-pricing-history-component :currentInventory="currentInventory" pricingType="generalLedgerBalance"></inventory-pricing-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-if="!isTypePpaAppraisal && !isPreEntry">
                  <div class="field">
                    <label class="label">{{purchaseCostLabel}}</label>
                    <div class="field">
                      <span v-if="currentInventory.purchaseCost && hasFeatureAccess('inventory.purchase-cost.view')">{{currentInventory.purchaseCost | currency}}</span>
                      <span v-else>-</span>
                      <inventory-pricing-history-component :currentInventory="currentInventory" pricingType="purchaseCost"></inventory-pricing-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-else>
                  <div class="field">
                    <label class="label">Proposed Purchase Cost</label>
                    <div class="field">
                      <span v-if="currentInventory.proposedCost">{{currentInventory.proposedCost | currency}}</span>
                      <span v-else>-</span>
                    </div>
                  </div>
                </div>

                <div class="column is-4">
                  <div class="field">
                    <label class="label">Recon Cost</label>
                    <div class="field">
                      <span v-if="currentInventory.reconCost && hasFeatureAccess('inventory.recon-cost.view')">{{currentInventory.reconCost | currency}}</span>
                      <span v-else>-</span>
                      <inventory-pricing-history-component :currentInventory="currentInventory" pricingType="reconCost"></inventory-pricing-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-if="isTypePpaAppraisal || isTypeTrade">
                  <div class="field">
                    <label class="label">ACV</label>
                    <div class="field">
                      <span v-if="currentInventory.purchaseCost">{{currentInventory.purchaseCost | currency}}</span>
                      <span v-else>-</span>
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-if="isTypePpaAppraisal || isTypeTrade">
                  <div class="field">
                    <label class="label">Trade Allowance</label>
                    <div class="field">
                      <span v-if="currentInventory.proposedTradeAllowance">{{currentInventory.proposedTradeAllowance | currency}}</span>
                      <span v-else>-</span>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Exp. GL Balance</label>
                    <div class="field">
                      <span v-if="expectedGeneralLedgerBalance && hasFeatureAccess('inventory.exp-gl-balance.view')">{{expectedGeneralLedgerBalance | currency}}</span>
                      <span v-else>-</span>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Spiff</label>
                    <div class="field">
                      <span v-if="currentInventory.spiff && hasFeatureAccess('inventory.spiff.view')">{{currentInventory.spiff | currency}}</span>
                      <span v-else>-</span>
                      <inventory-pricing-history-component :currentInventory="currentInventory" pricingType="spiff"></inventory-pricing-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Spiff Expiration Date</label>
                    <div class="field">
                      {{ currentInventory.spiffExpirationDate | formatDateOnly }}
                    </div>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="is-pulled-left">
                    <label class="label is-inline-block is-size-7">On Special</label>
                    <br/>
                    <i class="icon fal fa-lg status-icon" :class="getIconByOnSpecialStatus(currentInventory)"/>
                  </div>
                </div>
                <div class="column is-4">
                  <div class="field">
                    <label class="label">Special Expiration Date</label>
                    <div class="field">
                      {{ currentInventory.specialExpirationDate | formatDateOnly }}
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-if="currentInventory.cpoStatus">
                  <div class="field">
                    <label class="label">CPO Intention</label>
                    <div class="field">
                      {{ currentInventory.cpoStatus }}
                    </div>
                  </div>
                </div>
                <div class="column is-4" v-if="currentInventory.cpoInspectionStatus">
                  <div class="field">
                    <label class="label">Cpo Inspection Status</label>
                    <div class="field">
                      {{ currentInventory.cpoInspectionStatus }}
                    </div>
                  </div>
                </div>
                <div class="column is-6" v-show="false">
                  <div class="field">
                    <label class="label">Pay Off Amount</label>
                    <div class="field">
                      <span v-if="currentInventory.payoffAmount">{{currentInventory.payoffAmount | currency}}</span>
                      <span v-else>-</span>
                    </div>
                  </div>
                </div>
                <div class="column is-6" v-if="hasCurrentCommissionRequest && currentCommissionRequest.commission > 0">
                  <div class="field">
                    <label class="label">{{ purchaseCommissionTitle }}<b-icon :class="getCommissionIconClassByStatus(currentCommissionRequestStatus)" v-tooltip="getCommissionRequestStatusInformation(currentCommissionRequest)"></b-icon></label>
                    <span v-if="commissionAmount">{{commissionAmount | currency}}</span>
                    <span v-else>-</span>
                    <div style="padding-top: 1em; display: flex; justify-content: flex-start;max-width: 420px; flex-wrap: wrap; line-height: 42px;">
                      <inventory-commission-history-component :currentInventory="currentInventory" :isListMode="true"></inventory-commission-history-component>
                      <inventory-commission-processing-action :isListMode="true" :saveStatusChange="true" v-on:commission-request-status-change="commissionRequestStatusChange" :commissionRequest="currentCommissionRequest" v-if="!commissionAmountChanged" v-on:commission-request-approved="commissionRequestApproved"></inventory-commission-processing-action>
                    </div>
                  </div>
                </div>
                <div class="column is-6" v-else>
                  <div class="field">
                    <label class="label">Purchase Commission</label>
                    <div class="field">
                      <span v-if="currentInventory.commission">{{currentInventory.commission | currency}}</span>
                      <span v-else>-</span>
                      <inventory-commission-history-component :currentInventory="currentInventory" :isListMode="true"></inventory-commission-history-component>
                    </div>
                  </div>
                </div>
                <div class="column is-12">
                  <div class="field">
                    <label class="label">Lease Note</label>
                    <div class="field">
                      {{currentInventory.leaseNote ? (currentInventory.leaseNote) : '-'}}
                    </div>
                  </div>
                </div>
                <div class="column is-12">
                  <div class="field">
                    <label class="label">Special Note</label>
                    <div class="field">
                      {{currentInventory.specialNote ? (currentInventory.specialNote) : '-'}}
                    </div>
                  </div>
                </div>
                <div class="column is-12" v-if="currentInventory.ppaPriceAmendmentReason">
                  <div class="field">
                    <label class="label">Proposed price re-evaluation ({{currentInventory.ppaPriceAmendmentReasonDescription}})</label>
                    <div class="field">
                      {{currentInventory.ppaPriceAmmendmentNote ? (currentInventory.ppaPriceAmmendmentNote) : '-'}}
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="currentInventory.type === 'Consigned'">
                <div class="columns is-mobile is-multiline" >
                  <div class="column is-6" v-if="currentInventory.saleProceedsType === 'Flat'">
                    <div class="field">
                      <label class="label">Minimum Sale Price</label>
                      <div class="field">
                        <span v-if="currentInventory.consignmentMinimum">{{ currentInventory.consignmentMinimum | currency }}</span>
                        <span v-else>-</span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-6" v-else>
                    <div class="field">
                      <label class="label">Sale Price</label>
                      <div class="field">
                        <span v-if="currentInventory.askingPrice">{{ currentInventory.askingPrice | currency }}</span>
                        <span v-else>-</span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="field">
                      <label class="label">Consignment Type</label>
                      <div class="field">
                        {{currentInventory.saleProceedsType ? currentInventory.saleProceedsType : '-'}}
                      </div>
                    </div>
                  </div>
                  <div class="column is-6" v-if="currentInventory.saleProceedsType === 'Percentage'">
                    <div class="field">
                      <label class="label">Consignment Percentage</label>
                      <div class="field">
                        {{currentInventory.consignmentPercentage ? (currentInventory.consignmentPercentage + '%') : '-'}}
                      </div>
                    </div>
                  </div>
                  <div class="column is-6" v-if="currentInventory.saleProceedsType === 'Split' || currentInventory.saleProceedsType === 'Flat'">
                    <div class="field">
                      <label class="label" v-if="currentInventory.saleProceedsType === 'Split'">Split Value</label>
                      <label class="label" v-else>Delaer Flat Value</label>
                      <div class="field">
                        {{currentInventory.consignmentAgreementValue ? ('$' + currentInventory.consignmentAgreementValue) : '-'}}
                      </div>
                    </div>
                  </div>
                  <div class="column is-6" v-if="currentInventory.saleProceedsType === 'Split'">
                    <div class="field">
                      <label class="label">Split Ratio</label>
                      <div class="field">
                        {{currentInventory.percentageSplitFirst + '% / ' + currentInventory.percentageSplitSecond + '%'}}
                      </div>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="field">
                      <label class="label">Consignment Terms</label>
                      <div class="field">
                        {{currentInventory.consignmentTerms ? (currentInventory.consignmentTerms) : '-'}}
                      </div>
                    </div>
                  </div>
                  <div class="column is-12">
                    <div class="field">
                      <label class="label">Consignment Note</label>
                      <div class="field">
                        {{currentInventory.consignmentNote ? (currentInventory.consignmentNote) : '-'}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="currentInventory.commitmentFromName !== ''">
                <div class="columns is-mobile is-multiline">
                  <div class="column is-6">
                    <div class="field">
                      <label class="label">Commitment From</label>
                      <div class="field">
                        {{currentInventory.commitmentFromName ? (currentInventory.commitmentFromName) : '-'}}
                      </div>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="field">
                      <label class="label">Firm Commitment</label>
                      <div class="field">
                        {{currentInventory.firmCommitment ? (currentInventory.firmCommitment ? "Yes" : "No") : '-'}}
                      </div>
                    </div>
                  </div>
                  <div class="column is-12">
                    <div class="field">
                      <label class="label">Commitment Value</label>
                      <div class="field">
                        <span v-if="currentInventory.commitmentValue">{{currentInventory.commitmentValue | currency}}</span>
                        <span v-else>-</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <form class="view-container" @change="onFormChange">
                <div class="columns is-mobile is-multiline">
                  <div class="column is-4">
                  <div class="field">
                    <label class="label">Msrp</label>
                    <div class="control has-icons-left">
                      <money name="msrp" data-vv-as="msrp" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('msrp') }" type="text" placeholder="0.00" v-model="currentInventory.msrp" v-bind="$globalMoneyFormat"></money>
                      <span v-show="errors.has('msrp')" class="help is-danger"><i v-show="errors.has('msrp')" class="fas fa-exclamation-triangle"></i> {{ errors.first('msrp') }}</span>
                      <span class="icon is-small is-left">
                        <i class="fas fa-dollar-sign"></i>
                      </span>
                    </div>
                  </div>
                </div>
                  <div class="column is-4">
                    <div class="field">
                      <label class="label">Asking Price</label>
                      <div class="control has-icons-left">
                        <money name="askingPrice" data-vv-as="asking price" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('askingPrice') }" type="text" placeholder="0.00" v-model="currentInventory.askingPrice" v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('askingPrice')" class="help is-danger"><i v-show="errors.has('askingPrice')" class="fas fa-exclamation-triangle"></i> {{ errors.first('askingPrice') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-4">
                    <div class="field">
                      <label class="label">Lease Price</label>
                      <div class="control has-icons-left">
                        <money name="leasePrice" data-vv-as="lease price" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('leasePrice') }" type="text" placeholder="0.00" v-model="currentInventory.leasePrice" v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('leasePrice')" class="help is-danger"><i v-show="errors.has('leasePrice')" class="fas fa-exclamation-triangle"></i> {{ errors.first('leasePrice') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-4">
                    <div class="field">
                      <label class="label">GL Balance</label>
                      <div class="control has-icons-left">
                        <money name="generalLedgerBalance" data-vv-as="GL balance" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('generalLedgerBalance') }" type="text" placeholder="0.00" v-model="currentInventory.generalLedgerBalance" disabled v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('generalLedgerBalance')" class="help is-danger"><i v-show="errors.has('generalLedgerBalance')" class="fas fa-exclamation-triangle"></i> {{ errors.first('generalLedgerBalance') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-4">
                    <div class="field">
                      <label class="label">{{purchaseCostLabel}}</label>
                      <div class="field">
                        <span class="read-only-field" v-if="currentInventory.purchaseCost">{{currentInventory.purchaseCost | currency}}</span>
                        <span class="read-only-field" v-else>-</span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-4">
                    <div class="field">
                      <label class="label">Recon Cost</label>
                      <div class="control has-icons-left">
                        <money name="reconCost" data-vv-as="recon cost" v-validate="'decimal|required'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('reconCost') }" type="text" placeholder="0.00" v-model="currentInventory.reconCost" v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('reconCost')" class="help is-danger"><i v-show="errors.has('reconCost')" class="fas fa-exclamation-triangle"></i> {{ errors.first('reconCost') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="field">
                      <label class="label">Exp. GL Balance</label>
                      <div class="field">
                        <span class="read-only-field" v-if="expectedGeneralLedgerBalance">{{expectedGeneralLedgerBalance | currency}}</span>
                        <span class="read-only-field" v-else>-</span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-6" v-if="hasCurrentCommissionRequest && currentCommissionRequest.commission > 0">
                    <div class="field">
                      <label class="label">{{ purchaseCommissionTitle }}<b-icon :class="getCommissionIconClassByStatus(currentCommissionRequestStatus)" v-tooltip="getCommissionRequestStatusInformation(currentCommissionRequest)"></b-icon></label>
                      <div class="control has-icons-left">
                        <money name="commission" data-vv-as="purchase commission" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('commission') }" type="text" placeholder="0.00" v-model="commissionAmount" v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('commission')" class="help is-danger"><i v-show="errors.has('commission')" class="fas fa-exclamation-triangle"></i> {{ errors.first('commission') }}</span>
                        <span class="icon is-small is-left" :class="getCommissionClassByStatus(currentCommissionRequestStatus)">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                      <div style="padding-top: 1em;">
                        <inventory-commission-processing-action :isListMode="false" :saveStatusChange="false" v-on:commission-request-status-change="commissionRequestStatusChange" :commissionRequest="currentCommissionRequest" v-if="!commissionAmountChanged"></inventory-commission-processing-action>
                      </div>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="field">
                      <label class="label">Spiff</label>
                      <div class="control has-icons-left">
                        <money name="spiff" data-vv-as="spiff" v-validate="'decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('spiff') }" type="text" placeholder="0.00" v-model="currentInventory.spiff" v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('spiff')" class="help is-danger"><i v-show="errors.has('spiff')" class="fas fa-exclamation-triangle"></i> {{ errors.first('spiff') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-6">
                    <div class="field">
                      <label class="label">Spiff Expiration Date<b-icon pack="fas" :icon="isSpiffExpirationDateRequired === 'required' ? 'star' : ''" class="fa-ss"></b-icon></label>
                      <el-date-picker
                        class="is-width-120"
                        @change="onFormChange()"
                        v-model="currentInventory.spiffExpirationDate"
                        type="date"
                        placeholder="Click to select..."
                        popper-class="datepicker-container"
                        data-vv-validate-on="input|change"
                        v-validate="isSpiffExpirationDateRequired"
                        :class="{'is-danger': errors.has('spiffExpirationDate') }"
                        data-vv-as="spiff expiration date">
                      </el-date-picker>
                      <span v-show="errors.has('spiffExpirationDate')" class="help is-danger">
                        <i v-show="errors.has('spiffExpirationDate')" class="fas fa-exclamation-triangle"></i> {{ errors.first('spiffExpirationDate') }}
                      </span>
                    </div>
                  </div>
                  <div class="column is-6">
                    <switch-selector type="is-info" label="On Special" v-model="currentInventory.isOnSpecial" extraStyles="margin-top:12px;"></switch-selector >
                  </div>
                  <div class="column is-6" v-if="currentInventory.isOnSpecial">
                    <div class="field">
                      <label class="label">Special Expiration Date<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                      <el-date-picker
                        class="is-width-120"
                        @change="onFormChange()"
                        v-model="currentInventory.specialExpirationDate"
                        type="date"
                        placeholder="Click to select..."
                        popper-class="datepicker-container"
                        data-vv-validate-on="input|change"
                        v-validate="currentInventory.isOnSpecial"
                        :class="{'is-danger': errors.has('specialExpirationDate') }"
                        data-vv-as="spiff expiration date">
                      </el-date-picker>
                      <span v-show="errors.has('specialExpirationDate')" class="help is-danger">
                        <i v-show="errors.has('specialExpirationDate')" class="fas fa-exclamation-triangle"></i> {{ errors.first('specialExpirationDate') }}
                      </span>
                    </div>
                  </div>
                  <div class="column is-4" v-show="false && currentInventory.payoffAmount">
                    <div class="field">
                      <label class="label no-content-wrap">Payoff Amount<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                      <div class="control has-icons-left">
                        <money name="payoffAmount" data-vv-as="payoff amount" v-validate="'decimal|required'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('payoffAmount') }" type="text" placeholder="0.00" v-model="currentInventory.payoffAmount" v-bind="$globalMoneyFormat"></money>
                        <span v-show="errors.has('payoffAmount')" class="help is-danger"><i v-show="errors.has('payoffAmount')" class="fas fa-exclamation-triangle"></i> {{ errors.first('payoffAmount') }}</span>
                        <span class="icon is-small is-left">
                          <i class="fas fa-dollar-sign"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="column is-12">
                    <div class="field">
                      <label class="label">Lease Notes</label>
                      <div class="control">
                        <textarea name="leaseNote" :class="{'textarea': true}" type="text" placeholder="" v-model="currentInventory.leaseNote"></textarea>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="currentInventory.type === 'Consigned'">
                  <div class="columns is-mobile is-multiline" >
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Consignment Percentage<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-left">
                          <input name="consignmentPercentage" data-vv-as="consignment percentage" v-validate="'required|decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('consignmentPercentage') }" type="text" placeholder="0.00" v-model="currentInventory.consignmentPercentage">
                          <span v-show="errors.has('consignmentPercentage')" class="help is-danger"><i v-show="errors.has('consignmentPercentage')" class="fas fa-exclamation-triangle"></i> {{ errors.first('consignmentPercentage') }}</span>
                          <span class="icon is-small is-left">
                            <i class="fas fa-percent"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Consignment Minimum<b-icon pack="fas" icon="star" class="fa-ss"></b-icon></label>
                        <div class="control has-icons-left">
                          <money name="consignmentMinimum" data-vv-as="consignment minimum" v-validate="'required|decimal'" data-vv-delay="250" :class="{'input': true, 'is-danger': errors.has('consignmentMinimum') }" type="text" placeholder="0.00" v-model="currentInventory.consignmentMinimum" v-bind="$globalMoneyFormat"></money>
                          <span v-show="errors.has('consignmentMinimum')" class="help is-danger"><i v-show="errors.has('consignmentMinimum')" class="fas fa-exclamation-triangle"></i> {{ errors.first('consignmentMinimum') }}</span>
                          <span class="icon is-small is-left">
                            <i class="fas fa-dollar-sign"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Consignment Terms</label>
                        <div class="control">
                          <input name="consignmentTerms" :class="{'input': true}" type="text" placeholder="" v-model="currentInventory.consignmentTerms">
                        </div>
                      </div>
                    </div>
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Consignment Notes</label>
                        <div class="control">
                          <textarea name="consignmentNote" :class="{'textarea': true}" type="text" placeholder="" v-model="currentInventory.consignmentNote"></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="currentInventory.commitmentFromName !== ''">
                  <div class="columns is-mobile is-multiline">
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Commitment From</label>
                        <div class="field">
                          {{currentInventory.commitmentFromName ? (currentInventory.commitmentFromName) : '-'}}
                        </div>
                      </div>
                    </div>
                    <div class="column is-6">
                      <div class="field">
                        <label class="label">Firm Commitment</label>
                        <div class="field">
                          {{currentInventory.firmCommitment ? (currentInventory.firmCommitment ? "Yes" : "No") : '-'}}
                        </div>
                      </div>
                    </div>
                    <div class="column is-12">
                      <div class="field">
                        <label class="label">Commitment Value</label>
                        <div class="field">
                          <span class="read-only-field" v-if="currentInventory.commitmentValue">{{currentInventory.commitmentValue | currency}}</span>
                          <span class="read-only-field" v-else>-</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </template>
            <inventory-vauto-control v-if="currentInventory.latestVAutoReport" :key="'va' + currentInventory.id" :vAutoRating="currentInventory.latestVAutoReport" :inventory="currentInventory" displayMode="Compact"></inventory-vauto-control>
            <inventory-appraisal-control :key="'app' + currentInventory.id" v-if="hasFeatureAccess('inventory.appraisal.view')" :appraisedValue="mostRecentAppraisal" displayMode="Compact" :inventory="inventoryAppraisalParams" v-on:on-new-appraisal="onAppraised" :inventoryType="currentInventory.type"></inventory-appraisal-control>
            <inventory-black-book-control :key="'bb' + currentInventory.id" :blackBookInformation="mostRecentBlackBookInformation" :inventory="inventoryAppraisalParams" v-on:on-new-blackbook-information="onBlackBookInformationRetrieved" v-if="blackBookEnabled"></inventory-black-book-control>
            <inventory-payoff-control v-if="currentInventory && (currentInventory.isPayOff || currentInventory.titleStatus === 'PayOff')" displayMode="Compact" :inventory="currentInventory" :inventoryType="currentInventory.type"></inventory-payoff-control>
          </div>
          <div class="box offers-leads" ref="offersPod">
            <section-header :collapsed="isOffersCollapsed" title="Offers & Leads" :forInventoryView="true" :overview="getHighestOfferOverview" v-on:collapsed-status-changed="onOffersCollapsedStatusChanged" :inventoryUpdateHeader="true" :infoHeader="hasOfferAmount"></section-header>
            <div class="columns">
              <div class="column is-12 no-h-padding">
                <b-tabs size="is-small" v-model="offersAndLeadsTabIndex" :animated="false" type="is-toggle">
                  <b-tab-item label="Offers" icon="fal fa-hand-holding-usd" icon-pack="fal">
                    <div class="columns is-multiline">
                      <div class="column is-12" v-if="!offersLoading">
                        <div style="margin-top: 1em; margin-bottom: 1em;">
                          <b-field class="offerHistoryType">
                            <b-radio-button v-model="selectedOfferType" size="is-small" native-value="All" type="is-default-selected"><span>All</span></b-radio-button>
                            <b-radio-button v-model="selectedOfferType" size="is-small" native-value="Customer" type="is-success"><span>Customer</span></b-radio-button>
                            <b-radio-button v-model="selectedOfferType" size="is-small" native-value="Sales" type="is-success"><span>Sales</span></b-radio-button>
                          </b-field>
                        </div>
                        <b-table
                          :data="this.filteredPurchaseOffers"
                          :sortable="false"
                          :mobile-cards="false"
                          :selecteable="false"
                          :row-class="() => 'accesory-event-row'">
                          <template slot-scope="props">

                            <b-table-column field="customerFullName" label="Customer" class="accesory-event-column" :title="props.row.customerFullName">
                              <a @click="onViewLeadCustomer(props.row.customerId)" class="has-text-weight-semibold clickable" :title="props.row.customerFullName">
                                {{ props.row.customerFullName }}
                              </a>
                            </b-table-column>

                            <b-table-column field="offerMadeBy" label="Offer Made By" class="accesory-event-column">
                              {{ props.row.offerMadeBy }}
                            </b-table-column>

                            <b-table-column field="offer" label="Offer" class="accesory-event-column">
                              {{ props.row.offerReceived | currency('$', 2) }}
                            </b-table-column>

                            <b-table-column field="offerType" label="Type Of Offer" class="accesory-event-column">
                              {{ props.row.offerType }}
                            </b-table-column>

                            <b-table-column field="createdAt" label="Date" class="accesory-event-column">
                              {{ props.row.offerReceivedOn | formatDateOnly }}
                            </b-table-column>

                          </template>
                          <template slot="empty">
                            <section class="section">
                              <div class="has-text-centered">
                                <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                                <p>No offers recorded for this vehicle.</p>
                              </div>
                            </section>
                          </template>
                        </b-table>
                        <br />
                      </div>
                      <div class="column is-12 no-h-padding subtitle is-7" style="text-align: center;" v-else>
                        <i class="fas fa-spinner fa-spin fa-lg has-text-success" title="Loading..."></i>
                        Loading all offers ...
                      </div>
                    </div>
                  </b-tab-item>
                  <b-tab-item label="Leads"  icon="fal fa-user-plus" icon-pack="fal">
                    <div class="columns is-multiline">
                      <div class="column is-12" v-if="!leadsLoading">
                        <div style="margin-bottom: 1em;">
                          <lead-type-selector :selectedLeadTypes="selectedLeadTypes" v-on:selection-changed="onLeadTypeSelectionChanged"></lead-type-selector>
                        </div>
                        <b-table
                          :data="filteredLeads"
                          :sortable="false"
                          :mobile-cards="false"
                          :selecteable="false"
                          :row-class="() => 'accesory-event-row'">
                          <template slot-scope="props">

                            <b-table-column field="customerFullName" label="Customer" class="accesory-event-column" :title="props.row.customerFullName">
                              <a @click="onViewLeadCustomer(props.row.customerId)" class="has-text-weight-semibold clickable" :title="props.row.customerFullName">{{ props.row.customerFullName }}</a>
                            </b-table-column>

                            <b-table-column field="leadStatus" label=" Status" class="accesory-event-column">
                              {{ props.row.leadStatus }}
                            </b-table-column>

                            <b-table-column field="salesAgent" label="Agent" class="accesory-event-column">
                              {{ props.row.salesAgent}}
                            </b-table-column>

                            <b-table-column field="costItem" label="Source" class="accesory-event-column">
                              {{ props.row.costItem }}
                            </b-table-column>

                            <b-table-column field="createdAt" label="Date" class="accesory-event-column">
                              {{formatDate(props.row.utcEnabled, props.row.dateCreated) }}
                            </b-table-column>

                          </template>
                          <template slot="empty">
                            <section class="section">
                              <div class="has-text-centered">
                                <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                                <p>No leads recorded for this vehicle.</p>
                              </div>
                            </section>
                          </template>
                        </b-table>
                        <br />
                      </div>
                      <div class="column is-12 no-h-padding subtitle is-7" style="text-align: center;" v-else>
                        <i class="fas fa-spinner fa-spin fa-lg has-text-success" title="Loading..."></i>
                        Loading all leads ...
                      </div>
                    </div>
                  </b-tab-item>
                </b-tabs>
              </div>
            </div>
          </div>

          <div class="box"  ref="tagsPod">
            <section-header :collapsed="true" title="Tags"></section-header>
            <tag-view v-model="this.tagsList"></tag-view>
            <quick-action-container>
              <inventory-tags-capture v-if="!isPpaClosed" :inventoryItem="currentInventory" :isListMode="false" :key="currentInventory.id"></inventory-tags-capture>
            </quick-action-container>
            <br/>
            <div class="columns is-multiline">
              <div class="column is-12 is-size-7" v-if="currentInventory && currentInventory.saleDescription && currentInventory.saleDescription !== ''">
                <label class="label is-inline-block">Sales Description</label><i class="fal fa-copy is-pulled-right" @click.stop.prevent="onCopy('Description', currentInventory.saleDescription)"></i>
                <div class="side-padding-sm" v-html="currentInventory.saleDescription"></div>
              </div>
              <div class="column is-12 is-size-7" v-if="currentInventory && currentInventory.features">
                <label class="label is-inline-block">Features</label><i class="fal fa-copy is-pulled-right" @click.stop.prevent="onCopy('Features', currentInventory.features)"></i>
                <ul style="margin-left: 1.5em;">
                  <li type="disc" v-for="feature in features" :key="feature">{{feature}}</li>
                </ul>
              </div>
            </div>
          </div>

          <div id="supplement-cost-section" class="box" v-if="false">
            <section-header
              title="Additional Costs"
              parentSelector="#supplement-cost-section"
              :collapsed="true"
              :overview="getTotalAdditionalCostsHeader"></section-header>
            <div class="columns is-multiline">
              <div class="column is-12 no-h-padding">
                <table class="table is-bordered is-narrow is-hoverable is-fullwidth">
                  <thead v-if="supplementalCosts.length > 0">
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Cost</th>
                      <th>Selling Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tr class="clickable" v-for="(supplementalCost, index) in supplementalCosts" :key="supplementalCost.id" @click="editSupplementalCost(supplementalCost, index)">
                    <td>{{index + 1}}</td>
                    <td>{{supplementalCost.name + ' - ' + supplementalCost.description}}</td>
                    <td>{{supplementalCost.cost | currency}}</td>
                    <td>{{supplementalCost.sellingPrice | currency}}</td>
                    <td>
                      <supplemental-cost-view-component :value="supplementalCost"></supplemental-cost-view-component>
                      <span class="icon"><i class="fal fa-lg fa-minus-circle" v-if="hasFeatureAccess(deleteSupplementalCostAccessPermission)" @click.stop.prevent="onDeleteSupplementalCostClick(supplementalCost, index)" title="Delete"></i></span>
                    </td>
                  </tr>
                  <tr v-if="supplementalCosts.length === 0">
                    <td>None specified</td>
                  </tr>
                </table>
              </div>
              <div class="column is-12 no-h-padding">
                <quick-action-container>
                  <div class="is-inline-block" v-if="showAddSupplementalCost">
                    <div class="quick-action">
                      <button class="button" type="button" @click="addSupplementalCost()" title="Click to add a new supplement cost." >
                        <b-icon pack="fal" icon="plus" custom-size="fa-lg"></b-icon>
                      </button>
                    </div>
                  </div>
                </quick-action-container>
              </div>
            </div>
          </div>

          <inventory-supplemental-cost-component
            v-if="showCostsSection"
            :isCollapsed="true"
            v-model="supplementalCosts"
            :restoreByDefault="requiresDefaultCostsSetting"
            :isReadOnly="!mustUnlockCosts"
            :selectedInventory="currentInventory"
            :selectedInventoryId="currentInventory.id"
            :selectedMakeModel="null"
            :selectedTypeStatus="selectedTypeStatus"
            :selectedDispositionIntentionStatus="null"
            :slimView="true"
            :usePortal="true"
            :autoSaveChanges="true">
            <a class="button is-danger mr-5" @click="onCostsLockStatusChanged" v-if="!requiresDefaultCostsSetting">
              <span class="icon">
                <i class="fas fa-lock" v-if="isCostsUnlocked"></i>
                <i class="fas fa-unlock" v-else></i>
              </span>
            </a>
          </inventory-supplemental-cost-component>

          <div class="box">
            <section-header title="Notes" :overview="buildNotesSectionOverview(currentInventory, 50)" :collapsed="isNotesCollapsed"></section-header>
            <note-timeline-view v-model="currentInventory.notes" entity="inventory"></note-timeline-view>
            <quick-action-container>
              <inventory-note-capture v-if="!isPpaClosed" :inventoryItem="currentInventory" :isListMode="false" :key="currentInventory.id"></inventory-note-capture>
            </quick-action-container>
          </div>

          <vehicle-reports-capture :currentInventory="currentInventory" v-if="hasFeatureAccess('inventory.view.vehicle.reports')"></vehicle-reports-capture>

          <div class="box" v-if="this.currentInventory.deals.length === 0">
            <section-header :collapsed="isDealsCollapsed" title="Deals"></section-header>
            <div class="column is-12 is-size-7 has-text-info">No deals available at this time</div>
          </div>

          <div :ref="'deal-' + deal.id" class="box" v-for="deal in this.currentInventory.deals" :key="deal.id">
            <section-header :collapsed="isDealsCollapsed" :title="buildDealSectionTitleForInventory(deal)" :forInventoryView="true" :overview="buildDealSectionOverview(deal, deal.items.filter(x => x.forInventory.id === currentInventory.id && x.dealItemType === 'Sold')[0])"></section-header>
            <div v-for="dealItem in deal.items.filter(x => x.forInventory.id === currentInventory.id)" :key="dealItem.id">
                <div v-if="!dealItem.isPrimary && dealItem.forInventory.id === currentInventory.id && dealItem.dealItemType === 'Sold' && deal.type !== 'Cancelled'" class="notification is-warning backup-deal-qv" :ref="'notification' + dealItem.id">
                  <button class="delete" type="button" @click="onCloseDuplicateNotification('notification' + dealItem.id)"></button>
                  <span>
                    <i class="fas fa-exclamation-triangle is-warning"></i>
                  </span>
                    Warning: Stock# <a @click.stop.prevent="onStockNoClick(dealItem.forInventory)"><span class="has-text-weight-semibold" :title="dealItem.forInventory.stockNo">{{ dealItem.forInventory.stockNo }}</span></a> is sold to {{ primaryDealCustomerName }} by {{ primaryDealSalesPersonName }}. Please note that this is a backup deal for this inventory record.
                    <span v-if="!isEditLocked">Click <a @click="showNotificationModel(dealItem)">here</a> to make this the primary deal.</span>
                </div>
                <div hidden="true" v-else-if="dealItem.isPrimary && dealItem.dealItemType === 'Sold'" class="notification is-success backup-deal-qv" :ref="'notification' + dealItem.id">
                  <button class="delete" type="button" @click="onCloseDuplicateNotification('notification' + dealItem.id)"></button>
                  <span>
                    <i class="fas fa-check-circle is-success"></i>
                  </span>
                  This is the primary deal for this inventory record.
                </div>
                <div v-if="dealItem.dealItemType === 'Trade'" class="notification is-info backup-deal-qv" :ref="'notification' + dealItem.id">
                  <button class="delete" type="button" @click="onCloseDuplicateNotification('notification' + dealItem.id)"></button>
                  <span>
                    <i class="fas fa-info-circle"></i>
                  </span>
                  This vehicle is the trade-in for this deal.
                </div>
            </div>

            <div class="columns is-mobile">
              <div class="column is-6">
                <div class="field">
                  <label class="label">{{deal.saleType === 'Retail' ? 'Customer' : 'Supplier'}}</label>
                  <div class="field">
                    <a class="is-link" @click="onViewDeal(deal)">
                      {{deal.purchaserName}}
                    </a>
                    <customer-pop-component :key="deal.purchaserId" v-if="deal.saleType === 'Retail'" v-model="deal.purchaserId"></customer-pop-component>
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label">Sales Person</label>
                  <div class="field">
                    <click-to-contact :key="'ctc-' + deal.salesPersonId" :value="buildSalesPersonInfo(deal)"></click-to-contact>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="deal.items.filter((x) => x.dealItemType === 'Sold' && x.forInventory.id === currentInventoryId).length > 0">
              <div class="columns is-mobile is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Sale Date</label>
                    <div class="field">
                      {{deal.soldAt | formatDate}}
                    </div>
                  </div>
                </div>
                <div class="column is-6" v-if="showPromisedDate(deal.type)">
                  <div class="field">
                    <label class="label">Expected Delivery Date</label>
                    <div class="field">
                      {{deal.promiseDate | formatDate}}
                    </div>
                  </div>
                </div>
                <div class="column is-6" v-else>
                  <div class="field">
                    <label class="label">Delivered Date</label>
                    <div class="field">
                      {{deal.deliveryDate | formatDate}}
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Sale Price</label>
                    <div class="field">
                      {{deal.totalSalesValue | currency}}
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Front GP</label>
                    <div class="field">
                      {{deal.totalFrontValue | currency}}
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Total GP</label>
                    <div class="field">
                      {{totalGP | currency}}
                    </div>
                  </div>
                </div>
                <div class="column is-narrow">
                  <span class="icon" v-for="trade in deal.items.filter((x) => x.dealItemType === 'Trade')" :key="trade.id">
                    <a class="is-link" @click="onViewTrade(trade)">
                      <i class="fas fa-car fa-lg" :title="'Trade-In: ' + trade.forInventory.name" :class="getColorClassByVehicleCount(deal.totalTrades)"></i>
                    </a>
                  </span>
                </div>
              </div>
            </div>
            <div v-for="(dealItem, index) in deal.items.filter(x => x.dealItemType === 'Trade')" :key="index">
              <div class="columns is-mobile">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Trade Price ({{ dealItem.forInventory.name }})</label>
                    <div class="field">
                      {{dealItem.forInventory.purchaseCostTotal | currency}}
                    </div>
                  </div>
                </div>
              </div>
              <div class="columns" v-if="dealItem.lineComment !== null">
                <div class="column is-12">
                  <div class="field">
                    <label class="label">Note ({{ dealItem.forInventory.name }})</label>
                    <div class="field">
                      {{dealItem.lineComment}}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div :ref="'deal-' + tradeSourceDeal.id" class="box" v-if="isTradeLinkedToDeal">
            <section-header 
              v-tooltip="'Trade Source Deal - ' + currentInventory.tradeSourcePurchaserFullName" 
              :title="buildDealSectionTitleForInventoryTrade('Traded Deal')" 
              :forInventoryView="true" 
              :overview="buildDealSectionOverview(tradeSourceDeal, tradeSourceDeal.primaryDealItem)">
            </section-header>
            <div class="columns is-mobile">
              <div class="column is-6">
                <div class="field">
                  <label class="label">Trade Source Inventory</label>
                  <div class="field">
                    <a class="is-link" @click="onViewTrade(tradeSourceDeal.primaryDealItem)">{{currentInventory.tradeSourceName}}</a>
                  </div>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label">Trade Source Purchaser</label>
                  <div class="field">
                    <a class="is-link" @click="onViewDeal(tradeSourceDeal)">{{tradeSourceDeal.purchaserName}}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="box" ref="accessoriesPod">
            <section-header :collapsed="isAccessoriesCollapsed" :title="buildAccessorySectionTitle(currentInventory)" :forInventoryView="true" :overview="buildAccessorySectionOverview(currentInventory)"></section-header>
            <div class="columns is-multiline">
              <div class="column is-8">
                <p v-html="accessoryOverview"></p>
              </div>
              <div class="column is-4 side-padding-sm">
                <p class="is-marginless is-paddingless is-pulled-right subtitle is-7">showing top {{ this.$store.state.config.maxAccessoryEvents }}</p>
              </div>
              <div class="column is-12 no-h-padding">
                <b-table
                  :data="this.currentInventory.accessoryEvents"
                  :sortable="false"
                  :mobile-cards="false"
                  :selecteable="false"
                  :row-class="() => 'accesory-event-row'">
                  <template slot-scope="props">
                    <b-table-column field="type" label="Status" class="accesory-event-column">
                      <i class="icon fal fa-key fa-sm" :class="getColorClassByAccessoryStatus(props.row.typeId)" title="Accessory Status"/>
                      {{ props.row.type == 'CheckedOut' ? 'Out' : 'In' }}
                    </b-table-column>

                    <b-table-column field="binsDescription" label="Bins" class="accesory-event-column" :title="props.row.binsDescription">
                      {{ (props.row.binsDescription !== '' ? props.row.binsDescription : 'n/a') | truncate(10) }}
                    </b-table-column>

                    <b-table-column field="givenTo" label="Given To" class="accesory-event-column">
                      {{ props.row.givenTo !== '' ? props.row.givenTo : 'n/a' }}
                    </b-table-column>

                    <b-table-column field="createdAt" label="Created" class="accesory-event-column">
                      {{ props.row.createdAt | formatDateOnly }}
                    </b-table-column>

                    <b-table-column field="createdBy" label="Creator" class="accesory-event-column">
                      {{ props.row.createdBy}}
                    </b-table-column>

                    <b-table-column field="id" label="View" class="accesory-event-column">
                      <accessory-event-view :currentInventory="currentInventory" :accessoryEvent="props.row"></accessory-event-view>
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <section class="section">
                      <div class="has-text-centered">
                        <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                        <p>No accessory events recorded for this vehicle.</p>
                      </div>
                    </section>
                  </template>
                </b-table>
                <br />
                <a class="show-more-link link is-size-7 is-pulled-right" @click="onShowMoreAccessoryEvents" v-if="hasMoreAccessoryEvents">view all</a>
              </div>
            </div>
            <quick-action-container>
              <accessory-event-capture v-if="!isPpaClosed" :currentInventory="currentInventory"></accessory-event-capture>
              <capture-accessory-override :usePortal="true" :inventoryItem="currentInventory" :isListMode="false"></capture-accessory-override>
            </quick-action-container>
          </div>

          <div class="box" ref="serviceRequestPod" style="padding-left: 1rem !important;padding-right: 1rem !important" v-if="currentDealer.trackWorkshopInfo === true && currentInventory.possessionStatus !== 'Locating'">
            <section-header :collapsed="isWorkShopCollapsed" :title="buildWorkshopSectionTitle(currentInventory)" :forInventoryView="true" :overview="buildWorkshopSectionOverview(currentInventory, true)"></section-header>
            <div v-if="hasWorks">
              <div class="columns is-multiline">
                <div class="column is-12 no-h-padding">
                  <b-table
                    :data="this.currentInventory.serviceRequests"
                    :sortable="false"
                    :selecteable="false"
                    :mobile-cards="false"
                    @select="onServiceRequestRowSelected"
                    @dblclick="onServiceRequestRowSelected"
                    :row-class="() => 'service-request-row'">
                    <template slot-scope="props">
                      <b-table-column field="requestStatusType" label="Status" class="service-request-column">
                        <i class="icon fal fa-layer-plus fa-lg" :class="getColorClassByGetReadyStatus(props.row.requestStatusTypeId)" title="Status" v-if="props.row.requestStatusTypeId === 0"/>
                        <i class="icon fal fa-wrench fa-lg" :class="getColorClassByGetReadyStatus(props.row.requestStatusTypeId)" title="Status" v-else/>
                        {{ requestStatusType(props.row.requestStatusType) }}
                      </b-table-column>

                      <b-table-column field="serviceRequestType" label="Type" class="service-request-column">
                        {{ requestType(props.row.serviceRequestType) }}
                      </b-table-column>

                      <b-table-column field="pendingCosts" label="Pending" class="service-request-column has-text-right" :title="props.row.pendingCosts | currency">
                        {{ props.row.pendingCosts | currency }}
                      </b-table-column>

                      <b-table-column field="approvedCosts" label="Approved" class="service-request-column has-text-right" :title="props.row.approvedCosts | currency">
                        {{ props.row.approvedCosts | currency }}
                      </b-table-column>
                    </template>
                    <template slot="empty">
                      <section class="section">
                        <div class="has-text-centered">
                          <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                          <p>No service requests recorded for this vehicle.</p>
                        </div>
                      </section>
                    </template>
                    <template slot="footer" v-if="this.currentInventory.serviceRequests.length > 0">
                      <div class="is-flex" style="justify-content: space-evenly">
                        <span class="tag is-black">Declined: {{ totalDeclinedServiceRequestCosts | currency }}</span>
                        <span class="tag is-warning">Pending: {{ totalPendingServiceRequestCosts | currency }}</span>
                        <span class="tag is-primary">Approved: {{ totalApprovedServiceRequestCosts | currency}}</span>
                      </div>
                  </template>
                  </b-table>
                  <br />
                  <a class="show-more-link link is-size-7 is-pulled-right" @click="onShowMoreServiceRequests">view all</a>
                </div>
              </div>
              <quick-action-container>
                <service-request-capture v-if="!isPpaClosed" :currentInventory="currentInventory"></service-request-capture>
              </quick-action-container>
            </div>
            <div v-else class="column is-12 is-size-7 has-text-info">No information available at this time</div>
          </div>

          <div class="box" ref="loanerPod" v-if="currentDealer.trackLoanerInfo === true && currentInventory.possessionStatus !== 'Locating'">
            <section-header :collapsed="isLoanersCollapsed" :title="buildLoanerSectionTitle(currentInventory)" :forInventoryView="true" :overview="buildLoanerSectionOverview(currentInventory)"></section-header>
            <inventory-loaner-event-container :currentInventory="currentInventory" :allowService="true" v-if="hasLoaners"></inventory-loaner-event-container>
          </div>

          <div class="box" v-if="currentDealer.trackWorkshopInfo === true && hasServiceRequests && currentInventory.possessionStatus !== 'Locating'">
            <section-header :collapsed="isWorkShopCollapsed" title="Service Request"></section-header>
            <div class="columns" v-if="hasServiceRequests">
            </div>
            <div v-else class="column is-12 is-size-7 has-text-info">No information available at this time</div>
          </div>

          <div class="box">
            <section-header :collapsed="true" title="Sales Videos"></section-header>
            <b-table
              :data="currentInventory.salesVideos"
              :sortable="false"
              :selecteable="false"
              :mobile-cards="false">
              :row-class="() => 'service-request-row'">
              <template slot-scope="props">
                <b-table-column field="createdBy" label="Created By">
                  {{ props.row.createdByCompact }}
                </b-table-column>

                <b-table-column field="createdAt" label="Created At">
                  {{ props.row.createdAt | formatDateOnly }}
                </b-table-column>
                <b-table-column field="id" label="Actions">
                  <video-player-control :url="props.row.fileLocation" :title="'Created by ' + props.row.createdBy" v-if="props.row && props.row.fileLocation"></video-player-control>
                  <span :ref="'salesVideosImageViewer_' + props.row.id" class="icon" v-viewer.static="{inline: false, title: true}">
                    <i class="fal fa-lg fa-images" title="View Images" @click="onViewSalesVideoImages(props.row.id)"></i>
                    <img v-for="attachment in props.row.attachments" :key="attachment.id" :src="attachment.fileLocation" :alt="attachment.description" style="display:none"/>
                  </span>
                  <span class="icon" v-if="props.row && props.row.fileLocation"><i class="fal fa-lg fa-download" @click.stop.prevent="onDownloadSalesVideo(props.row)" title="Download"></i></span>
                  <inventory-media-request-action :isListMode="true" v-if="!currentInventory.sold" :inventorySalesVideoItem="props.row"></inventory-media-request-action>
                </b-table-column>
              </template>
              <template slot="empty">
                <section class="section">
                  <div class="has-text-centered">
                    <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                    <p>No sales videos created for this vehicle.</p>
                  </div>
                </section>
              </template>
            </b-table>
          </div>

          <service-video-container :vinNo="currentInventory.vinNo" v-if="currentInventory && currentInventory.vinNo" :vehicleName="currentInventory.name"></service-video-container>

          <div class="box" v-if="currentDealer.trackWorkshopInfo === true && hasServiceRequests && currentInventory.possessionStatus !== 'Locating'">
            <section-header :collapsed="isWorkShopCollapsed" title="Service Request"></section-header>
            <div class="columns" v-if="hasServiceRequests">
            </div>
            <div v-else class="column is-12 is-size-7 has-text-info">No information available at this time</div>
          </div>

          <div class="box" ref="saveGeneralFilesContainer">
            <section-header title="General Files" :collapsed="true"></section-header>
            <div class="field">
              <div class="field">
                <file-selector
                  v-model="inventoryMiscellaneousFiles"
                  label="Miscellaneous Files"
                  :enabled="true"
                  :multiple="true"
                  :showFileOnly="false"
                  :isBoxed="false"
                  :formatImages="true"
                  :usePortalViewer="true">
                </file-selector>
              </div>
            </div>
            <div>
              <div class="control quick-action" v-show="showGeneralFilesSave">
                <button :class="{'is-loading': isSavingFile }" class="button is-success" @click.prevent="onSyncGeneralFiles()" title="Save">
                  <b-icon pack="fal" icon="upload" custom-size="fa-lg"></b-icon>
                </button>
              </div>
            </div>
          </div>

          <div class="box">
            <section-header title="Update History" :collapsed="isAuditCollapsed"></section-header>
            <br />
            <div class="columns is-mobile is-multiline">
              <div class="column is-6">
                <label class="label">Created By:</label>
                <div class="field">
                  {{ currentInventory.createdByFullName }}
                </div>
              </div>
              <div class="column is-6">
                <label class="label">Created At:</label>
                <div class="field">
                  {{ currentInventory.createdAt | formatDate }}
                </div>
              </div>
              <div class="column is-6">
                <label class="label">Modified By:</label>
                <div class="field">
                  {{ currentInventory.modifiedByFullName }}
                </div>
              </div>
              <div class="column is-6">
                <label class="label">Modified At:</label>
                <div class="field">
                  {{ currentInventory.modifiedAt | formatDate }}
                </div>
              </div>
              <div class="column is-6" v-if="currentInventory.archivedAt">
                <label class="label">Returned:</label>
                <div class="field">
                  <span class="has-text-danger">{{ currentInventory.archivedAt | formatDate }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="box" ref="publishStatusHistoryPod">
            <section-header :collapsed="isPublishStatusHistoryCollapsed" title="Publish Status History" :forInventoryView="false" overview="" v-on:collapsed-status-changed="onPublishStatusHistoryCollapsedStatusChanged"></section-header>
            <div class="columns is-multiline">
              <div class="column is-12 no-h-padding">
                <b-table
                  :data="inventoryPublishHistoryStatus"
                  :sortable="false"
                  :mobile-cards="false"
                  :selecteable="false"
                  :row-class="() => 'accesory-event-row'">
                  <template slot-scope="props">

                    <b-table-column field="createdBy" label="Modified By" class="accesory-event-column" :title="props.row.createdBy">
                      {{ (props.row.createdBy) }}
                    </b-table-column>

                    <b-table-column field="publishStatus" label="Do Not Publish" sortable>
                      {{ props.row.publishStatus ? 'YES' : 'NO' }}
                    </b-table-column>

                    <b-table-column field="createdAt" label="Date" class="accesory-event-column">
                      {{ props.row.createdAt | formatDateOnly }}
                    </b-table-column>

                  </template>
                  <template slot="empty">
                    <section class="section">
                      <div class="has-text-centered">
                        <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                        <p>No publish status history changes recorded for this vehicle.</p>
                      </div>
                    </section>
                  </template>
                </b-table>
                <br />
                <a class="show-more-link link is-size-7 is-pulled-right" @click="onShowMoreAccessoryEvents" v-if="hasMoreAccessoryEvents">view all</a>
              </div>
            </div>
          </div>

          <div class="box" ref="possessionStatusHistoryPod">
            <section-header :collapsed="isPossessionStatusHistoryCollapsed" title="Possession Status History" :forInventoryView="false" overview="" v-on:collapsed-status-changed="onPossessionStatusHistoryCollapsedStatusChanged"></section-header>
            <div class="columns is-multiline">
              <div class="column is-12 no-h-padding">
                <b-table
                  :key="'status_history_key_' + currentInventory.id"
                  :data="inventoryPossessionStatusHistory"
                  :sortable="false"
                  :mobile-cards="false"
                  :selecteable="false"
                  :row-class="() => 'accesory-event-row'">
                  <template slot-scope="props">
                    <b-table-column field="createdBy" label="Modified By" class="accesory-event-column" :title="props.row.createdBy">
                      {{ (props.row.createdBy) }}
                    </b-table-column>
                    <b-table-column field="possessionStatus" label="Possession Status" class="accesory-event-column" sortable>
                      {{ humanizedPossessionStatus(props.row) }}
                    </b-table-column>
                    <b-table-column field="createdAt" label="Date" class="accesory-event-column">
                      {{ props.row.createdAt | formatDate }}
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <section class="section">
                      <div class="has-text-centered">
                        <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                        <p>No possession status history changes recorded for this vehicle.</p>
                      </div>
                    </section>
                  </template>
                </b-table>
              </div>
            </div>
          </div>

          <div class="box" ref="fieldChangedHistoryPod">
            <section-header :collapsed="isFieldChangedHistoryCollapsed" title="Field Changed History" :forInventoryView="false" overview="" v-on:collapsed-status-changed="onFieldChangedHistoryCollapsedStatusChanged"></section-header>
            <div class="columns is-multiline">
              <div class="column is-12 no-h-padding">
                <b-table
                  :key="'field_changed_history_key_' + currentInventory.id"
                  :data="inventoryFieldChangedHistory"
                  :sortable="false"
                  :mobile-cards="false"
                  :selecteable="false"
                  :row-class="() => 'accesory-event-row'">
                  <template slot-scope="props">
                    <b-table-column field="changedBy" label="Modified By" class="accesory-event-column" :title="props.row.changedBy">
                      {{ props.row.changedBy }}
                    </b-table-column>
                    <b-table-column field="fieldDisplayName" label="Field" class="accesory-event-column">
                      {{ props.row.fieldDisplayName }}
                    </b-table-column>
                    <b-table-column field="oldValue" label="From" class="accesory-event-column" :title="props.row.oldValue">
                      {{ props.row.oldValue }}
                    </b-table-column>
                    <b-table-column field="newValue" label="To" class="accesory-event-column" :title="props.row.newValue">
                      {{ props.row.newValue }}
                    </b-table-column>
                    <b-table-column field="createdAt" label="Date" class="accesory-event-column">
                      {{ props.row.createdAt | formatDate }}
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <section class="section">
                      <div class="has-text-centered">
                        <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                        <p>No field changes recorded for this vehicle.</p>
                      </div>
                    </section>
                  </template>
                </b-table>
              </div>
            </div>
          </div>

          <div class="box">
            <section-header title="Cdk Integration Status" :collapsed="isCdkCollapsed"></section-header>
            <div class="columns is-mobile is-multiline">
              <div class="column is-4">
                <label class="label">Cdk Id:</label>
                <div class="field">
                  {{ currentInventory.cdkId || "Pending" }}
                </div>
              </div>
              <div class="column is-4">
                <label class="label">Cdk Hash:</label>
                <div class="field">
                  {{ currentInventory.cdkHash || "Pending" }}
                </div>
              </div>
              <div class="column is-4">
                <label class="label">Sync Pending:</label>
                <div class="field">
                  {{ (currentInventory.cdkSyncRequired || currentInventory.cdkHash === '' ) | yesno }}
                </div>
              </div>
              <div class="column is-8" v-if="currentInventory.cdkLastError !== ''">
                <label class="label">Cdk Last Error:</label>
                <div class="field">
                  <span class=" has-text-danger">{{ currentInventory.cdkLastError}}</span>
                </div>
              </div>
              <div class="column is-4" v-if="currentInventory.cdkLastError !== ''">
                <label class="label">Reset</label>
                <i class="icon fal fa-sm fa-sync-alt" :class="{'fa-spin': isResettingCdkError}" title="Reset error and retry Cdk update" @click="onResetCdkError"/>
              </div>
            </div>
            <quick-action-container>
              <div class="is-inline-block">
                <div class="quick-action">
                  <button class="button" type="button" @click="markForceCdkPush" title="Click to force CDK push." >
                    <b-icon pack="fal" icon="random" custom-size="fa-lg" :class="{'fa-spin': isSaving}"></b-icon>
                  </button>
                </div>
              </div>
              <inventory-cdk-push-gone-action :currentInventory="currentInventory" :key="'CG' + currentInventory.id" v-on:pushed="reloadInventory"></inventory-cdk-push-gone-action>
            </quick-action-container>
          </div>

          <div class="box">
            <section-header title="vAuto Integration Status" :collapsed="isVautoCollapsed"></section-header>
            <div class="columns is-mobile is-multiline">
              <div class="column is-6">
                <label class="label">Last Appraisal:</label>
                <div class="field">
                  <a target="_vauto" :href="currentInventory.vAutoAppId" v-if="hasVautoAppraisal">view</a>
                  <span v-else>Pending</span>
                </div>
              </div>
              <div class="column is-6">
                <label class="label">Sync Pending:</label>
                <div class="field">
                  {{ (currentInventory.vAutoSyncRequired || currentInventory.vAutoAppId === '') | yesno }}
                </div>
              </div>
              <div class="column is-8" v-if="currentInventory.vAutoLastError !== ''">
                <label class="label">Last Error:</label>
                <div class="field">
                  <span class=" has-text-danger">{{ currentInventory.vAutoLastError}}</span>
                </div>
              </div>
              <div class="column is-4" v-if="currentInventory.vAutoLastError !== ''">
                <label class="label">Reset</label>
                <i class="icon fal fa-sm fa-sync-alt" :class="{'fa-spin': isResettingvAutoError}" title="Reset error and retry vAuto sync" @click="onResetvAutoError"/>
              </div>
            </div>
          </div>
          <portal :to="actionPortal">
            <div class="field is-grouped">
              <p class="field" v-if="isEditLocked">
                <a class="button is-danger" @click="onCancel()">
                  Close
                </a>
              </p>
              <p class="field" v-if="hasFeatureAccess('inventory.modify') && isEditLocked && !isPpaClosed">
                <a class="button is-primary" @click="onEdit()">
                  Edit
                </a>
              </p>
              <p class="field" v-if="hasFeatureAccess('inventory.modify') && !isEditLocked">
                <a class="button is-primary" @click="savePricing()">
                  Save
                </a>
              </p>
              <div class="quick-action-list">
                <pod-lock-control v-if="!isPpaClosed && isEditLocked" v-model="isEditLocked" :bypassPermissions="['inventory.modify.*', 'inventory.pricing.modify']"></pod-lock-control>
                <inventory-note-capture v-if="!isPpaClosed" :inventoryItem="currentInventory" :isListMode="false"></inventory-note-capture>
                <div class="quick-action" v-if="currentInventory.inStock && allowedDealInventoryTypes.find((x) => x.altName === currentInventory.type) && !isPreEntry && isEditLocked">
                  <button class="button" @click="onAddDeal()" title="Add a new deal to this inventory record">
                    <i :class="getDealColorClass" class="icon fal fa-handshake fa-lg" :data-type="getDealDataType"/>
                  </button>
                </div>
                <div class="quick-action" v-if="currentInventory.type === 'ForPpaAppraisal' && !isPreEntry && isEditLocked">
                  <button class="button" @click="onAddInventoryTradeToDeal()" title="Add a new deal to this inventory record">
                    <i :class="getDealColorClass" class="icon fal fa-handshake fa-lg" :data-type="getDealDataType"/>
                  </button>
                </div>
                <div class="quick-action" v-if="currentInventory.type === 'TradeIn' && !isPreEntry && isEditLocked">
                  <button class="button" @click="isCreateDealActive = true" title="Add a new deal to this inventory record">
                    <i :class="getDealColorClass" class="icon fal fa-handshake fa-lg" :data-type="getDealDataType"/>
                  </button>
                </div>
                <mark-returned-action v-if="isEditLocked" :inventoryItem="currentInventory" title=""></mark-returned-action>
                <archive-inventory-action :inventoryItem="currentInventory" v-if="showInventoryArchiveAction && isEditLocked"></archive-inventory-action>
                <retire-service-loaner-action v-if="isEditLocked" :inventoryItem="currentInventory"></retire-service-loaner-action>
                <convert-ppa-to-purchase-action v-if="showPpaConversionButton && isEditLocked"
                  :inventory="currentInventory" 
                  ref="convertPpaAction"  
                  v-on:on-mounted-callback="onConvertPpaActionMounted">
                </convert-ppa-to-purchase-action>
                <convert-consigned-to-purchase-action :inventoryItem="currentInventory" v-if="isTypeConsigned && !isStatusArchived && isEditLocked"></convert-consigned-to-purchase-action>
                <close-ppa-action :inventoryItem="currentInventory" v-if="showPpaCloseButton && isEditLocked" ref="closePpaAction"></close-ppa-action>
                <undo-ppa-rejection-action :inventoryItem="currentInventory" v-if="isPpaClosed && isEditLocked"></undo-ppa-rejection-action>
                <approve-ppa-action :inventoryItem="currentInventory" v-if="isEditLocked" ref="approvePpaAction" v-on:on-mounted-callback="onApprovePpaActionMounted"></approve-ppa-action>
                <convert-pre-entry-action :inventoryItem="currentInventory" v-if="isPreEntry && isEditLocked"></convert-pre-entry-action>
                <convert-lease-return-action :inventoryItem="currentInventory" v-if="isLeaseReturn && !isArchived && isEditLocked"></convert-lease-return-action>
                <view-approved-ppa-value :inventoryItem="currentInventory" v-if="isEditLocked" ref="viewApprovedPpa"></view-approved-ppa-value>
                <div class="quick-action" v-if="currentInventory && currentInventory.id && hasFeatureAccess('inventory.lifecycle.graph')">
                  <button class="button" @click="onViewLifecycle()" title="View vehicle lifecycle graph">
                    <i class="icon fal fa-project-diagram fa-lg"/>
                  </button>
                </div>
              </div>
            </div>
          </portal>
        </div>
        <portal to="global-modal-portal" v-if="inventoryDealIsPrimaryActive">
          <b-modal :active.sync="inventoryDealIsPrimaryActive" :width="640" scroll="keep" :has-modal-card="true">
            <div class="modal-card make-deal-primary" style="width: auto">
              <header class="modal-card-head" style="width: 400px !important">
                <p class="modal-card-title">Warning</p>
              </header>
              <section class="modal-card-body make-deal-primary view-container">
                Do you want to make this deal the primary deal for this inventory record?
              </section>
              <footer class="modal-card-foot" style="width: 400px !important">
                <button class="button is-primary" :class="{'is-loading': isSaving }"  @click="onConfirmInventoryDealIsPrimary(true)" type="button">Confirm</button>
                <button class="button is-danger" @click="onConfirmInventoryDealIsPrimary(false)" type="button">Cancel</button>
              </footer>
            </div>
          </b-modal>
        </portal>
      </div>
      <b-loading :is-full-page="isFullPage" :active.sync="isLoading" :canCancel="false"></b-loading>
      <dealer-peak-lookup v-if="isIndividual"></dealer-peak-lookup>
      <portal to="global-modal-portal" v-if="isPublishStatusChangeActive">
        <b-modal :active.sync="isPublishStatusChangeActive" :width="480" scroll="keep" :has-modal-card="true">
          <div class="modal-card confirm">
            <header class="modal-card-head" style="width: 480px !important">
              <p class="modal-card-title">Confirm Publish Status Change</p>
            </header>
            <section class="modal-card-body confirm">
              Are you sure you want to change publish status?
            </section>
            <footer class="modal-card-foot" style="width: 480px !important">
              <button class="button is-primary" :class="{'is-loading': isSavingNewPublishStatus }" @click="onPublishStatusChanged()">Yes</button>
              <button class="button is-danger" @click="onCancelPublishStatusChange">No</button>
            </footer>
          </div>
        </b-modal>
      </portal>
      <portal to="global-modal-portal" v-if="isConfirmRemoveImagesActive">
        <b-modal :active.sync="isConfirmRemoveImagesActive" :width="480" scroll="keep" :has-modal-card="true">
          <div class="modal-card confirm">
            <header class="modal-card-head" style="width: 480px !important">
              <p class="modal-card-title">Confirm Removal of Images</p>
            </header>
            <section class="modal-card-body confirm">
              Are you sure you want to remove all vehicle images?
            </section>
            <footer class="modal-card-foot" style="width: 480px !important">
              <button class="button is-primary" :class="{'is-loading': isSaving }" @click="onConfirmImageRemoval()">Yes</button>
              <button class="button is-danger" @click="isConfirmRemoveImagesActive = false">No</button>
            </footer>
          </div>
        </b-modal>
      </portal>
      <portal to="global-modal-portal" v-if="isEditingLocation">
        <b-modal :active.sync="isEditingLocation" :width="480" scroll="keep" :has-modal-card="true">
          <div class="modal-card location">
            <header class="modal-card-head" style="width: 480px !important">
              <p class="modal-card-title">Confirm New Location</p>
            </header>
            <section class="modal-card-body location view-container">
              <div class="columns is-multiline is-vertical-center">
                <div class="column is-10">
                  <label class="label">Current</label>
                  <div class="field">
                    {{ currentInventory.locationName || '-' }}
                  </div>
                </div>
                <div class="column is-10">
                  <location-selector v-model="selectedLocation" label="New Location" validateAs="vehicle location" :required="false" :allowNew="false"></location-selector>
                </div>
                <div class="column is-2">
                  <i v-if="isSavingNewLocation" class="fas fa-spinner fa-spin fa-lg has-text-success" title="Saving..."></i>
                </div>
              </div>
            </section>
            <footer class="modal-card-foot" style="width: 480px !important">
              <button class="button is-primary" :class="{'is-loading': isSavingNewLocation }" @click="onSaveNewLocation">Update</button>
              <button class="button is-danger" @click="onCancelEditLocation">Cancel</button>
            </footer>
          </div>
        </b-modal>
      </portal>
      <portal to="global-modal-portal">
        <inventory-supplement-cost-capture
          :value="this.newSupplementalCost"
          :supplementalCosts="this.supplementalCosts"
          :inventoryId="this.currentInventory.id"
          :saveNewCost="true"
          :allowDelete="true"
          v-on:on-save-supplemental-cost="onSupplementalCostAdded"
          v-on:on-delete-supplemental-cost="onDeleteSupplementalCostClick">
        </inventory-supplement-cost-capture>
      </portal>
      <portal to="global-modal-portal" v-if="isCreateDealActive">
        <b-modal :active.sync="isCreateDealActive" :width="480" scroll="keep" :has-modal-card="true">
          <div class="modal-card confirm">
            <header class="modal-card-head" style="width: 480px !important">
              <p class="modal-card-title">Add Deal</p>
            </header>
            <section class="modal-card-body confirm" style="padding: 0px !important;">
              <div class="notification is-flex is-link">
                <span><i class="fas fa-exclamation-circle is-warning fa-2x"></i></span>
                  <span class="ml-10">
                    Create a new deal for this inventory record. Please select if this vehicle is being sold or traded as part of the deal.
                  </span>
              </div>
            </section>
            <footer class="modal-card-foot" style="width: 480px !important">
              <button class="button is-primary" :class="{'is-loading': isSaving }" @click="onAddDeal()">Inventory Sold</button>
              <button class="button is-primary" :class="{'is-loading': isSaving }" @click="onAddInventoryTradeToDeal()">Inventory Traded In</button>
              <button class="button is-danger" @click="isCreateDealActive = false">Cancel</button>
            </footer>
          </div>
        </b-modal>
      </portal>
      <confirm-delete-modal :usePortal="true" v-if="isDeleteConfirmationActive" title="Confirm Delete Supplemental Cost" @on-confirm-delete="onSupplementalCostDelete" @on-cancel-delete="isDeleteConfirmationActive = false">
        Are you sure you wish to delete the selected supplemental cost?
        <br/>
        <table class="table is-bordered is-narrow is-hoverable is-fullwidth">
          <thead v-if="deleteSupplementalCosts.length > 0">
            <tr>
              <th>Name</th>
              <th>Cost</th>
              <th>Selling Price</th>
            </tr>
          </thead>
          <tr v-for="(supplementalCost) in deleteSupplementalCosts" :key="supplementalCost.id">
            <td>{{supplementalCost.name}}</td>
            <td>{{supplementalCost.cost | currency}}</td>
            <td>{{supplementalCost.sellingPrice | currency}}</td>
          </tr>
          <tr v-if="deleteSupplementalCosts.length === 0">
            <td>None specified</td>
          </tr>
        </table>
      </confirm-delete-modal>
      <portal to="global-modal-portal" v-if="isCreateCheckRequestActive">
        <b-modal :active.sync="isCreateCheckRequestActive" :width="480" scroll="keep" :has-modal-card="true">
          <div class="modal-card">
            <header class="modal-card-head" style="width: 480px !important">
              <p class="modal-card-title">Add Check Request</p>
            </header>
            <section class="modal-card-body" style="width: 480px !important; padding: 10px !important;">
              <div class="notification is-flex is-link">
                <span><i class="fas fa-exclamation-circle is-warning fa-2x"></i></span>
                  <span class="ml-10">
                    Would you like to create a check request for the approved commission request.
                  </span>
              </div>
              <div class="columns is-multiline" v-if="hasCurrentCommissionRequest">
                <div class="column is-12">
                  <label class="label">Purchase Commission</label>
                  <div class="field">
                    {{currentCommissionRequest.commission | currency}}
                  </div>
                </div>                
                <div class="column is-12">
                  <label class="label">Commission Source</label>
                  <div class="field">
                    {{ currentCommissionRequest.commissionToName }}
                  </div>
                </div>                
                <div class="column is-12">
                  <label class="label">Commission Requested By</label>
                  <div class="field">
                    {{ currentCommissionRequest.commissionRequestedBy }}
                  </div>
                </div>                
                <div class="column is-12">
                  <label class="label">Commission Processed By</label>
                  <div class="field">
                    {{ currentCommissionRequest.commissionRequestedProcessedBy }}
                  </div>
                </div>
              </div>
            </section>
            <footer class="modal-card-foot" style="width: 480px !important">
              <button class="button is-primary" :class="{'is-loading': isSaving }" @click="onAddCheckRequest()">Yes</button>
              <button class="button is-danger" @click="closeCreateCheckRequestModal">Cancel</button>
            </footer>
          </div>
        </b-modal>
      </portal>
      <portal to="global-modal-portal" v-if="showCarfaxMatch">
        <b-modal :active="showCarfaxMatch" :width="640" scroll="keep" :has-modal-card="true">
          <div class="modal-card carfax-match-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">CARFAX</p>
            </header>
            <!-- ⚠ carfax-match-body opts OUT of this component's blanket .modal-card-body sizing, which
                 pins EVERY modal body on this screen to exactly 575px. The other modals here need it;
                 this one is a five-row table ending around 264px, so it rendered with ~310px of dead
                 space below the Style row. Scoped to this modal rather than relaxing the shared rule. -->
            <section class="modal-card-body carfax-match-body">
              <div class="notification is-info is-flex">
                <i class="fas fa-info-circle is-info fa-2x"></i>
                <div class="ml-10">
                  {{carfaxMatchMessage}}
                </div>
              </div>
              <!-- ⚠ The comparison util names its flags vehicleMakeDifferent/modelNameDifferent/...,
                   while this component reads make/model/year/trim/style. Mapped here exactly as the
                   other three hosts do, so all four render the same table from the same flags. -->
              <carfax-vehicle-comparison
                :selectedMakeModel="carfaxMatchRecord"
                :carfaxMakeModel="carfaxMatchCarfax"
                :differences="carfaxMatchTableDifferences">
              </carfax-vehicle-comparison>
            </section>
            <footer class="modal-card-foot">
              <!-- ⚠ NO LONGER READ-ONLY, and the reversal is the point. This used to route to the
                   vehicle-edit screen because this screen had no MakeModelSelector and no save. It
                   now raises one of its own, saving through the narrow endpoint. Garrett, 23 Sep:
                   "When I click Edit, I don't think you should be editing the vehicle, I think you
                   should be editing the model selector." Opening the edit screen to fix a model
                   risked saving whatever else was pending on it. -->
              <!-- ⚠ Gated on inventory.modify, like this screen's own Edit button. The server already refuses
                   a correction without it, but the button used to show to everyone - so a view-only user
                   worked through the reconcile prompt and the selector, pressed Save, and only then hit
                   the refusal, with the correction lost. The comparison itself stays readable; Close
                   remains. -->
              <button class="button is-primary" type="button" v-if="canCorrectCarfaxModel" @click="onCarfaxMatchCorrect">Edit</button>
              <button class="button" type="button" @click="showCarfaxMatch = false">Close</button>
            </footer>
          </div>
        </b-modal>
      </portal>
      <!-- Renders nothing until onCarfaxMatchCorrect opens it: the selector inside carries
           hideTrigger, so there is no inline control on this screen.

           ⚠ THROUGH THE GLOBAL MODAL PORTAL, like every other modal on this screen. The quick view
           renders inside a fixed-width panel, so a modal left in place is laid out against that panel
           rather than the viewport - it came out clamped to the panel's width. App.vue hosts
           portal-target "global-modal-portal" for exactly this. -->
      <portal to="global-modal-portal">
        <carfax-model-correction-modal
          v-if="currentInventory"
          :inventory="currentInventory"
          :open="carfaxCorrectionOpen"
          v-on:saved="onCarfaxModelCorrected"
          v-on:failed="onCarfaxModelCorrectionFailed"
          v-on:closed="carfaxCorrectionOpen = false">
        </carfax-model-correction-modal>
      </portal>
      <ppa-inactive-lead-check-control :inventoryItem="currentInventory" :inventoryLeadsCheckRequested="customerSyncRequested" v-on:show-close-ppa-control="showClosePpaControl" :hasClosePpaAccess="hasClosePpaAccess" :validPpaStatus="showPpaCloseButton"></ppa-inactive-lead-check-control>
    </section>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import eventBus from '@/eventBus'
import SectionHeader from '@/components/generic/SectionHeader'
import NoteTimelineView from '@/components/generic/NoteTimelineView'
import inventoryService from '@/services/inventoryService'
import dealService from '@/services/dealService'
import CaptureNewInventoryNote from '@/components/inventory/CaptureNewInventoryNote'
import humanizedSwitchMixin from '@/mixins/generic/humanizedSwitches'
import validateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'
import PodLockControl from '@/components/generic/PodLockControl'
import MarkReturnedAction from '@/components/inventory/MarkReturnedAction'
import FieldRotator from '@/components/generic/FieldRotator'
import ButtonListSelector from '@/components/generic/ButtonListSelector'
import ArchiveInventoryAction from '@/components/inventory/ArchiveInventoryAction'
import InventoryTransferAction from '@/components/inventory/InventoryTransferAction'
import InventoryCdkPushGoneAction from '@/components/inventory/InventoryCdkPushGoneAction'
import CarFaxControl from '@/components/generic/CarFaxControl'
import RetireServiceLoanerAction from '@/components/inventory/RetireServiceLoanerAction'
import dealInventoryStyleClasses from '@/mixins/generic/dealInventoryStyleClasses'
import UsageHistoryComponent from '@/components/inventory/UsageHistoryComponent'
import InventoryPricingHistoryComponent from '@/components/inventory/InventoryPricingHistoryComponent'
import InventoryCommissionHistoryComponent from '@/components/inventory/InventoryCommissionHistoryComponent'
import InventoryDispositionIntentionHistoryComponent from '@/components/inventory/InventoryDispositionIntentionHistoryComponent'
import InventorySalesDescriptionComponent from '@/components/inventory/InventorySalesDescriptionComponent'
import ConfirmDeleteModalComponent from '@/components/generic/ConfirmDeleteModalComponent'
import InventoryAttributeView from '@/components/inventory/InventoryAttributeView'
import InventoryAppraisalControl from '@/components/generic/InventoryAppraisalControl'
import PdfSelector from '@/components/generic/PdfSelector'
import QuickActionContainer from '@/components/generic/QuickActionContainer'
import InventorySellersAgreementAction from '@/components/inventory/InventorySellersAgreementAction'
import InventoryConsignmentAgreementAction from '@/components/inventory/InventoryConsignmentAgreementAction'
import InventoryBatchPaymentRequest from '@/components/inventory/InventoryBatchPaymentRequestAction'
import InventoryVehicleVerificationAction from '@/components/inventory/InventoryVehicleVerificationAction'
import InventoryTitleCapture from '@/components/generic/inventory/InventoryTitleCapture'
import InventoryWindowStickerCapture from '@/components/generic/inventory/InventoryWindowStickerCapture'
import VehicleReportsCapture from '@/components/inventory/VehicleReportsCapture'
import AccessoryEventCapture from '@/components/generic/inventory/AccessoryEventCapture'
import AccessoryEventView from '@/components/generic/inventory/AccessoryEventView'
import ServiceRequestCapture from '@/components/generic/inventory/ServiceRequestCapture'
import formattingMixin from '@/mixins/inventory/formatting'
import InventoryLoanerEventContainer from '@/components/generic/inventory/InventoryLoanerEventContainer'
import SendPickupLocationControl from '@/components/generic/SendPickupLocationControl'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import TagView from '@/components/generic/TagView'
import DealerPeakControl from '@/components/generic/DealerPeakControl'
import DealerPeakLookup from '@/components/generic/DealerPeakLookup'
import ConvertPpaToPurchaseAction from '@/components/inventory/ConvertPpaToPurchaseAction'
import ConvertConsignedToPurchaseAction from '@/components/inventory/ConvertConsignedToPurchaseAction'
import ClosePpaAction from '@/components/inventory/ClosePpaAction'
import UndoPpaRejectionAction from '@/components/inventory/UndoPpaRejectionAction'
import ApprovePpaAction from '@/components/inventory/ApprovePpaAction'
import ConvertPreEntryToActiveAction from '@/components/inventory/ConvertPreEntryToActiveAction'
import ConvertLeaseReturnToUsedAction from '@/components/inventory/ConvertLeaseReturnToUsedAction'
import CaptureInventoryTags from '@/components/inventory/CaptureInventoryTags'
import AutoCheckControl from '@/components/generic/AutoCheckControl'
import utilitiesMixin from '@/mixins/generic/utilities'
import FileSelector from '@/components/generic/FileSelector'
import LocationSelector from '@/components/generic/LocationSelector'
import InventorySupplementalCostCapture from '@/components/generic/sales/InventorySupplementalCostCapture'
import InventoryLostTitleAction from '@/components/inventory/InventoryLostTitleAction.vue'
import LeadTypeSelector from '@/components/generic/LeadTypeSelector'
import InventoryPayoffControl from '@/components/generic/inventory/InventoryPayoffControl'
import InventoryLoanerRemoveServiceAction from '@/components/generic/inventory/InventoryLoanerRemoveServiceAction'
import InventoryBlackBookControl from '@/components/generic/InventoryBlackBookControl'
import InventoryDeskDeal from '@/components/generic/inventory/InventoryDeskDeal'
import VideoPlayerControl from '@/components/generic/VideoPlayerControl.vue'
import InventoryMediaRequestAction from '@/components/inventory/InventoryMediaRequestAction'
import SendTradeInTitleReassignmentDocument from '@/components/generic/sales/SendTradeInTitleReassignmentDocument'
import ServiceVideoContainer from '@/components/generic/ServiceVideoContainer'
import InventoryCommissionProcessingAction from '@/components/inventory/InventoryCommissionProcessingAction.vue'
import InventoryLeaseReturnStatus from '@/components/inventory/InventoryLeaseReturnStatus'
import InventorySupplementalCostComponent from '@/components/generic/inventory/InventorySupplementalCostComponent'
import ViewApprovedPpaValue from '@/components/inventory/ViewApprovedPpaValue'
import InventoryVautoControl from '@/components/generic/InventoryVautoControl'
import ClickToContactInfoComponent from '@/components/generic/ClickToContactInfoComponent'
import CarfaxProcessStatusHeader from '@/components/generic/CarfaxProcessStatusHeader'
import CarfaxVehicleComparison from '@/components/generic/CarfaxVehicleComparison'
import CarfaxModelCorrectionModal from '@/components/generic/CarfaxModelCorrectionModal'
import { buildCarfaxComparison, carfaxMatchOutcome, carfaxMatchIconClass, carfaxMatchHoverText } from '@/utils/carfax_make_model_comparison'
import { buildReconcileMessage, catalogueModelSeries } from '@/utils/carfax_make_model_resolution'
import { hasCarfaxTesterRole } from '@/utils/carfax_roles'
import CustomerPopComponent from '@/components/generic/CustomerPopComponent'
import SupplementalCostViewComponent from '@/components/generic/inventory/SupplementalCostViewComponent'
import dealerPeakService from '@/services/dealerPeakService'
import InventoryRecallResolveAction from '@/components/inventory/InventoryRecallResolveAction'
import CaptureAccessoryOverride from '@/components/inventory/CaptureAccessoryOverride'
import PpaInactiveLeadCheckControl from '@/components/generic/inventory/PpaInactiveLeadCheckControl'
import DealerPeakCustomerResyncComponent from '@/components/generic/DealerPeakCustomerResyncComponent'
import SwitchSelector from '@/components/generic/SwitchSelector'

import { Steps, Step } from 'element-ui'
import { createNamespacedHelpers } from 'vuex'
const mapUserGetters = createNamespacedHelpers('user').mapGetters
const mapDealerState = createNamespacedHelpers('dealer').mapState
const mapConfigGetters = createNamespacedHelpers('config').mapGetters
const mapVehicleMakeGetters = createNamespacedHelpers('vehicleMake').mapGetters

export default {
  name: 'ViewInventoryItem',
  inject: {
    $validator: '$validator'
  },
  mixins: [humanizedSwitchMixin, validateBeforeSubmitMixin, dealInventoryStyleClasses, formattingMixin, utilitiesMixin],
  components: {
    'video-player-control': VideoPlayerControl,
    'inventory-sellers-agreement': InventorySellersAgreementAction,
    'inventory-consignment-agreement': InventoryConsignmentAgreementAction,
    'inventory-batch-payment-request': InventoryBatchPaymentRequest,
    'inventory-vehicle-verification': InventoryVehicleVerificationAction,
    'section-header': SectionHeader,
    'button-list-selector': ButtonListSelector,
    'car-fax-control': CarFaxControl,
    'note-timeline-view': NoteTimelineView,
    'inventory-note-capture': CaptureNewInventoryNote,
    'mark-returned-action': MarkReturnedAction,
    'pod-lock-control': PodLockControl,
    'inventory-appraisal-control': InventoryAppraisalControl,
    'field-rotator': FieldRotator,
    'archive-inventory-action': ArchiveInventoryAction,
    'inventory-transfer-action': InventoryTransferAction,
    'inventory-cdk-push-gone-action': InventoryCdkPushGoneAction,
    'retire-service-loaner-action': RetireServiceLoanerAction,
    'usage-history-component': UsageHistoryComponent,
    'inventory-attribute-view': InventoryAttributeView,
    'pdf-selector': PdfSelector,
    'quick-action-container': QuickActionContainer,
    'el-steps': Steps,
    'el-step': Step,
    'title-status-capture': InventoryTitleCapture,
    'inventory-pricing-history-component': InventoryPricingHistoryComponent,
    'inventory-commission-history-component': InventoryCommissionHistoryComponent,
    'inventory-disposition-intention-history-component': InventoryDispositionIntentionHistoryComponent,
    'inventory-sales-description-component': InventorySalesDescriptionComponent,
    'confirm-delete-modal': ConfirmDeleteModalComponent,
    'window-stick-status-capture': InventoryWindowStickerCapture,
    'vehicle-reports-capture': VehicleReportsCapture,
    'accessory-event-capture': AccessoryEventCapture,
    'accessory-event-view': AccessoryEventView,
    'inventory-loaner-event-container': InventoryLoanerEventContainer,
    'service-request-capture': ServiceRequestCapture,
    'send-pickup-location-control': SendPickupLocationControl,
    'error-display-component': ErrorDisplayComponent,
    'tag-view': TagView,
    'dealer-peak-control': DealerPeakControl,
    'dealer-peak-lookup': DealerPeakLookup,
    'convert-ppa-to-purchase-action': ConvertPpaToPurchaseAction,
    'convert-consigned-to-purchase-action': ConvertConsignedToPurchaseAction,
    'close-ppa-action': ClosePpaAction,
    'undo-ppa-rejection-action': UndoPpaRejectionAction,
    'approve-ppa-action': ApprovePpaAction,
    'convert-pre-entry-action': ConvertPreEntryToActiveAction,
    'convert-lease-return-action': ConvertLeaseReturnToUsedAction,
    'inventory-tags-capture': CaptureInventoryTags,
    'auto-check-control': AutoCheckControl,
    'file-selector': FileSelector,
    'location-selector': LocationSelector,
    'inventory-supplement-cost-capture': InventorySupplementalCostCapture,
    'inventory-lost-title': InventoryLostTitleAction,
    'lead-type-selector': LeadTypeSelector,
    'inventory-payoff-control': InventoryPayoffControl,
    'service-loaner-out-of-service-action': InventoryLoanerRemoveServiceAction,
    'inventory-black-book-control': InventoryBlackBookControl,
    'inventory-desk-deal': InventoryDeskDeal,
    'inventory-media-request-action': InventoryMediaRequestAction,
    'send-trade-in-title-reassignment-documentation': SendTradeInTitleReassignmentDocument,
    'service-video-container': ServiceVideoContainer,
    'inventory-commission-processing-action': InventoryCommissionProcessingAction,
    'lease-return-status': InventoryLeaseReturnStatus,
    'inventory-supplemental-cost-component': InventorySupplementalCostComponent,
    'view-approved-ppa-value': ViewApprovedPpaValue,
    'inventory-vauto-control': InventoryVautoControl,
    'click-to-contact': ClickToContactInfoComponent,
    'carfax-process-status-header': CarfaxProcessStatusHeader,
    'carfax-vehicle-comparison': CarfaxVehicleComparison,
    'carfax-model-correction-modal': CarfaxModelCorrectionModal,
    'customer-pop-component': CustomerPopComponent,
    'supplemental-cost-view-component': SupplementalCostViewComponent,
    'inventory-recall-resolve-action': InventoryRecallResolveAction,
    'capture-accessory-override': CaptureAccessoryOverride,
    'ppa-inactive-lead-check-control': PpaInactiveLeadCheckControl,
    'dp-resync-component': DealerPeakCustomerResyncComponent,
    'switch-selector': SwitchSelector
  },
  props: {
    id: {
      type: Number,
      default: null
    },
    isLoanerPod: {
      type: Boolean,
      default: true
    },
    isServiceRequestPod: {
      type: Boolean,
      default: false
    },
    isAccessoriesPod: {
      type: Boolean,
      default: false
    },
    actionPortal: {
      type: String,
      default: 'qv-footer-toolbar-left'
    },
    headerPortal: {
      type: String,
      default: 'qv-header'
    }
  },
  data () {
    return {
      //The CARFAX match check - read-only here, and raised one way: onCarFaxVehicleInformation,
      //a refresh the operator pressed. The quick action that also reached it was removed, so a
      //refresh is now the only route in.
      showCarfaxMatch: false,
      //Raises the isolated model-correction modal. A flag rather than a $ref call, because that modal
      //renders through the global portal and portalled content is not reachable as a ref.
      carfaxCorrectionOpen: false,
      carfaxMatchMessage: '',
      carfaxMatchRecord: null,
      carfaxMatchCarfax: null,
      //Never null: the modal's template reads five properties off it, and Vue would render the box
      //with every row unmarked rather than fail loudly.
      carfaxMatchDifferences: {},
      isLoaner: true,
      isAccessories: false,
      isLoading: false,
      isResettingCdkError: false,
      isResettingvAutoError: false,
      isSavingNewDispositionIntention: false,
      isSavingNewPublishStatus: false,
      isFullPage: true,
      currentInventoryId: this.id,
      isSaving: false,
      serverErrors: [],
      serverErrorMessage: '',
      isEditLocked: true,
      selectedCommitmentFrom: null,
      daysList: [],
      expirationList: [],
      locationList: [],
      tagsList: [],
      selectedAccessoryStatus: null,
      selectedGetReadyStatus: null,
      selectedLoanerStatus: null,
      currentInventory: {
        id: 0,
        vinNo: '',
        stockNo: '',
        notes: []
      },
      loadingSign: false,
      currentDeal: null,
      selectedDealItem: null,
      inventoryDealIsPrimaryActive: false,
      selectedDealer: null,
      isDetailsCollapsed: false,
      isPricingCollapsed: true,
      isLoanersCollapsed: true,
      isDealsCollapsed: true,
      isAccessoriesCollapsed: true,
      isWorkShopCollapsed: true,
      isAuditCollapsed: true,
      isCdkCollapsed: true,
      isVautoCollapsed: true,
      isNotesCollapsed: true,
      selectedPossessionStatus: null,
      isOffersCollapsed: true,
      dispositionIntentionValue: undefined,
      publishStatusValue: undefined,
      isPublishStatusChangeActive: false,
      isConfirmRemoveImagesActive: false,
      inventoryPurchaseOffers: [],
      inventoryPurchaseOffersFetched: false,
      isPublishStatusHistoryCollapsed: true,
      isPossessionStatusHistoryCollapsed: true,
      isPublishStatusHistoryFetched: false,
      isPossessionStatusHistoryFetched: false,
      inventoryPublishHistoryStatus: [],
      inventoryPossessionStatusHistory: [],
      isFieldChangedHistoryCollapsed: true,
      isFieldChangedHistoryFetched: false,
      inventoryFieldChangedHistory: [],
      offersLoading: false,
      selectedOfferType: 'All',
      inventoryLeads: [],
      inventoryLeadsFetched: false,
      leadsLoading: false,
      filteredPurchaseOffers: [],
      offersAndLeadsTabIndex: 0,
      supplementalCosts: [],
      isLocationCollapsed: false,
      updateInventoryLocationPermission: 'inventory.view.update.location',
      isEditingLocation: false,
      isSavingNewLocation: false,
      newSupplementalCost: this.createGenericSupplementalCost(),
      addInventorySupplementalCost: 'inventory.view.add.supplemental.cost',
      showLostTitleAction: false,
      offerPodIndex: -1,
      selectedLeadTypes: [0],
      hasOfferAmount: false,
      convertedInventoryId: null,
      isCreateDealActive: false,
      deleteSupplementalCostAccessPermission: 'inventory.view.delete.supplemental.cost',
      currentDeleteSupplementalcost: null,
      isDeleteConfirmationActive: false,
      deleteSupplementalCosts: [],
      showGeneralFilesSave: false,
      inventoryMiscellaneousFiles: [],
      initialInventoryMiscellaneousFiles: [],
      isSavingFile: false,
      syncFileTypes: [],
      commissionAmount: 0,
      initalCommissionAmount: 0,
      isCostsUnlocked: false,
      isCreateCheckRequestActive: false,
      showCheckRequestOnLoad: false,
      //Set from the route when the CARFAX match icon on an inventory list was clicked - see the
      //consumer in loadInventory, which waits for the record before raising the comparison.
      showCarfaxMatchOnLoad: false,
      showServiceRequestPodOnLoad: false,
      showDealerReport: false,
      carFaxDealerReportUrl: '',
      showTestCarfaxContainer: true,
      parentMenuContainer: 'Inventory',
      defaultParentName: 'All Inventory',
      defaultParentRoute: 'ListInventory',
      carfaxRequested: false,
      isCustomerSyncing: false,
      customerSyncSuccessful: false,
      inventoryCustomerLeadCheckId: null,
      currentPurchaser: null,
      customerSyncRequested: false
    }
  },
  computed: {
    totalDeclinedServiceRequestCosts: function () {
      return this.currentInventory.serviceRequests.reduce((total, item) => {
        return total + (item.requestStatusType === 'Cancelled' || item.requestStatusType === 'Declined' ? 0 : item.declinedCosts)
      }, 0)
    },
    totalPendingServiceRequestCosts: function () {
      return this.currentInventory.serviceRequests.reduce((total, item) => {
        return total + (item.requestStatusType === 'Cancelled' || item.requestStatusType === 'Declined' ? 0 : item.pendingCosts)
      }, 0)
    },
    totalApprovedServiceRequestCosts: function () {
      return this.currentInventory.serviceRequests.reduce((total, item) => {
        return total + (item.requestStatusType === 'Cancelled' || item.requestStatusType === 'Declined' ? 0 : item.approvedCosts)
      }, 0)
    },
    isTradeLinkedToDeal: function () {
      return this.isTypeTrade && this.currentInventory.tradeSourceDealId && this.currentInventory.tradeSourceDealId > 0
    },
    purchaseCostLabel: function () {
      if (this.isTypeTrade) return 'Purchase Cost/ACV'
      return 'Purchase Cost'
    },
    titleIsLost: function () {
      if (this.currentInventory) {
        return this.currentInventory.titleStatus === 'Lost'
      }
      return false
    },
    filteredLeads: function () {
      let filterSelectTypes = this.definedTypes.leadType.options.filter((el) => {
        return this.selectedLeadTypes.some((f) => {
          return f === el.id
        })
      })
      if (filterSelectTypes.length > 0) {
        let list = filterSelectTypes.map(function (x) {
          return x.altName
        })
        if (list.length > 0) {
          let filteredInventoryLeads = this.inventoryLeads.filter((el) => {
            return list.some((f) => {
              return f === el.group
            })
          })
          return filteredInventoryLeads
        }
      }
      return this.inventoryLeads
    },
    canShowPickupLocation: function () {
      return this.currentInventory.possessionStatus === 'Inbound'
    },
    currentDispositionIntentionFormatted: function () {
      if (!this.currentInventory.dispositionIntention) return '-'
      let result = ''
      if (this.currentInventory.dispositionIntention === 'WholesaleImmediately') {
        result += 'Wholesale'
      } else if (this.currentInventory.dispositionIntention === 'RetailForXDays') {
        result += 'Retail'
      } else {
        result += this.currentInventory.dispositionIntention
      }
      if (this.currentInventory.dispositionIntention === 'RetailForXDays' && this.currentInventory.dispositionValidDays) {
        result += ' for ' + this.currentInventory.dispositionValidDays + ' days.'
      }
      return result
    },
    originalDispositionIntentionDisplayValue: function () {
      if (!this.currentInventory.originalDispositionIntention) return ''
      if (this.currentInventory.originalDispositionIntention.dispositionIntention === 'Retail') {
        return 'The original intention was to Retail as set by ' + this.currentInventory.originalDispositionIntention.createdByFullName
      }
      var result = 'The original intention was to '
      if (this.currentInventory.originalDispositionIntention.dispositionIntention === 'WholesaleImmediately') {
        result += 'Wholesale'
        if (this.currentInventory.originalDispositionIntention.commitmentFromId > 0) {
          result += ' to ' + this.currentInventory.originalDispositionIntention.commitmentFromName
          if (this.currentInventory.originalDispositionIntention.firmCommitment) {
            result += ' for ' + this.$formatCurrency(this.currentInventory.originalDispositionIntention.commitmentValue)
          }
        }
      } else {
        result += 'Retail'
        if (this.currentInventory.originalDispositionIntention.dispositionValidDays > 0) {
          result += ' for ' + this.currentInventory.originalDispositionIntention.dispositionValidDays + ' days'
        }
      }
      result += ' as set by ' + this.currentInventory.originalDispositionIntention.createdByFullName
      return result
    },
    isCurrentDispositionIntentionExpired: function () {
      return this.currentInventory.dispositionExpired || (this.currentInventory.originalDispositionIntention && this.currentInventory.originalDispositionIntention.dispositionIntention !== 'Retail')
    },
    accessoryOverview: function () {
      let content = ''
      if (this.currentInventory.accessoryDetailWarnings) {
        Object.keys(this.currentInventory.accessoryDetailWarnings).forEach(x => {
          if (x === 'books') {
            content += '<i title="Books status" class="fal fa-sm fa-books tip-icon ' + (this.currentInventory.accessoryDetailWarnings[x].length > 0 ? (this.currentInventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') : 'has-text-grey') + '"></i>'
          }
          if (x === 'batteryCharger') {
            content += '<i title="Battery charger status" class="fal fa-sm fa-car-battery tip-icon ' + (this.currentInventory.accessoryDetailWarnings[x].length > 0 ? (this.currentInventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') : 'has-text-grey') + '"></i>'
          }
          if (x === 'carCover') {
            content += '<i title="Car cover status" class="fal fa-sm fa-car-garage tip-icon ' + (this.currentInventory.accessoryDetailWarnings[x].length > 0 ? (this.currentInventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') : 'has-text-grey') + '"></i>'
          }
          if (x === 'keys') {
            content += '<i title="Keys status" class="fal fa-sm fa-key tip-icon ' + (this.currentInventory.accessoryDetailWarnings[x].length > 0 ? (this.currentInventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') : 'has-text-grey') + '"></i>'
          }
        })
      }
      return content
    },
    features: function () {
      if (!this.currentInventory || !this.currentInventory.features) {
        return []
      }
      return this.currentInventory.features.split('|')
    },
    hasAutoCheckAppraisal: function () {
      return false
    },
    hasVautoAppraisal: function () {
      return this.currentInventory.vAutoAppId && this.currentInventory.vAutoAppId !== ''
    },
    hasMoreServiceRequests: function () {
      return this.currentInventory && this.currentInventory.serviceRequests && this.currentInventory.serviceRequests.length === this.$store.state.config.maxServiceRequests
    },
    hasMoreAccessoryEvents: function () {
      return this.currentInventory && this.currentInventory.accessoryEvents && this.currentInventory.accessoryEvents.length === this.$store.state.config.maxAccessoryEvents
    },
    expectedGeneralLedgerBalance: function () {
      return this.currentInventory.purchaseCostTotal + this.currentInventory.reconCost
    },
    lastSignedSellersAgreement: function () {
      if (this.currentInventory && (this.currentInventory.sellersAgreementStatus === 'Complete' || this.currentInventory.sellersAgreementStatus === 'Signed')) {
        var allSignedSellersAgreements = this.currentInventory.attachments.filter((x) => x.classType === 'SellersAgreement' && x.status === 'Active')
        if (allSignedSellersAgreements.length > 0) {
          return allSignedSellersAgreements[allSignedSellersAgreements.length - 1].fileLocation
        }
      }
      return ''
    },
    mostRecentAppraisal: function () {
      if (this.currentInventory.appraisals.length > 0) {
        return this.currentInventory.appraisals[0]
      }
      return null
    },
    inventoryAppraisalParams: function () {
      let trimAttribute = this.currentInventory.attributes.filter((x) => x.name === 'Trim')[0]
      let trim = trimAttribute ? trimAttribute.value : ''
      let condition = this.currentInventory.conditionType
      return {
        id: this.currentInventory.id,
        vinNo: this.currentInventory.vinNo,
        mileage: this.currentInventory.usage,
        trim: trim,
        condition: condition,
        msrp: this.currentInventory.msrp,
        purchaseCost: this.resolvedPurchaseCost,
        askingPrice: this.currentInventory.askingPrice
      }
    },
    resolvedPurchaseCost: function () {
      if (this.currentInventory.type === 'Consigned') {
        return this.currentInventory ? this.currentInventory.consignmentMinimum : null
      }
      return this.purchaseCostTotal
    },
    purchaseCostTotal: function () {
      let purchaseCost = this.currentInventory ? this.currentInventory.purchaseCost : null
      purchaseCost = (this.isPreEntry && this.currentInventory) || (this.isAppraisal && this.currentInventory) ? this.currentInventory.proposedCost : purchaseCost
      let commission = this.commission || (this.currentInventory ? this.currentInventory.commission : null)
      return purchaseCost + commission
    },
    lastTitleDocument: function () {
      if (this.currentInventory.attachments.filter((y) => y.classType === 'TitleFront' || y.classType === 'TitleBack').length > 0) {
        var allTitles = this.currentInventory.attachments.filter((y) => y.classType === 'TitleFront' || y.classType === 'TitleBack')
        if (allTitles.length > 0) {
          return allTitles[0].fileLocation
        }
      }
      return ''
    },
    isLocating: function () {
      return this.currentInventory.possessionStatus === 'Locating'
    },
    showInventoryArchiveAction: function () {
      return false
    },
    isQuickView: function () {
      return this.actionPortal.startsWith('qv-')
    },
    fullName: function () {
      var fullName = '(' + this.currentInventory.stockNo + ')'
      if (fullName === '()') {
        return ''
      }
      return fullName
    },
    getTitleOverview: function () {
      let preEntryText = this.isPreEntry && this.canShowPreEntry ? ' [Pre-Entry]' : ''
      let prefixText = this.isTypePpaAppraisal ? 'PPA' : 'Inventory'
      var result = prefixText + ' - ' + this.currentInventory.stockNo + preEntryText
      return result
    },
    getInventoryOverview: function () {
      var result = this.currentInventory.stockNo + ' - ' + this.currentInventory.vinNo
      return result
    },
    getPricingOverview: function () {
      return 'Asking Price: ' + this.$formatCurrency(this.currentInventory.askingPrice)
    },
    obsoleteWatermarkText: function () {
      let currentStatus = this.getSoldDeliveredStatus
      var watermarkText = ''
      if (currentStatus.indexOf('Sold') >= 0) watermarkText = 'Sold'
      if (currentStatus.indexOf('Cancelled') >= 0) watermarkText = 'Cancelled'
      if (currentStatus.indexOf('Returned') >= 0) watermarkText = 'Returned'
      if (currentStatus.indexOf('Retired Loaner') >= 0) watermarkText = 'Retired Loaner'
      if (currentStatus.indexOf('Located Off-Site') >= 0) watermarkText = 'Located Off-Site'
      if (currentStatus.indexOf('Located Off-Site') >= 0 && currentStatus.indexOf('Sold') >= 0) watermarkText = 'Sold'
      return (watermarkText + ' ').repeat(500)
    },
    isObsoleteStatus: function () {
      let currentStatus = this.getSoldDeliveredStatus
      return currentStatus.indexOf('Sold') >= 0 || currentStatus.indexOf('Returned') >= 0 || currentStatus.indexOf('Cancelled') >= 0 || currentStatus.indexOf('Retired Loaner') >= 0 || currentStatus.indexOf('Located Off-Site') >= 0
    },
    getSoldDeliveredStatus: function () {
      let nonCancelledDeals = this.currentInventory.deals.length > 0 ? this.currentInventory.deals.filter((x) => x.type !== 'Cancelled' && x.items.find((y) => y.forInventory.id === this.currentInventory.id && y.dealItemType === 'Sold')) : []
      if (nonCancelledDeals.length > 0) {
        if (nonCancelledDeals[0].type === 'Booked' || nonCancelledDeals[0].type === 'Delivered') {
          if (this.currentInventory.possessionStatus === 'LocatedOffSite') {
            return this.currentInventory.type + ' - Located Off-Site - Sold'
          } else {
            return this.currentInventory.type + ' - ' + this.currentInventory.possessionStatus + ' - Sold'
          }
        } else {
          if (this.currentInventory.possessionStatus === 'LocatedOffSite') {
            return this.currentInventory.type + ' - Located Off-Site - ' + nonCancelledDeals[0].type
          } else {
            return this.currentInventory.type + ' - ' + this.currentInventory.possessionStatus + ' - ' + nonCancelledDeals[0].type
          }
        }
      } else {
        if (this.currentInventory.status === 'Archived' || this.currentInventory.status === 'Cancelled') {
          return this.currentInventory.type + ' - ' + this.currentInventory.possessionStatus + ' - Cancelled'
        }

        if (this.currentInventory.isServiceLoanerRetired) {
          return this.currentInventory.type + ' - ' + this.currentInventory.possessionStatus + ' - Retired Loaner'
        }

        if (this.currentInventory.possessionStatus === 'LocatedOffSite') {
          return this.currentInventory.type + ' - Located Off-Site'
        }

        return this.currentInventory.type + ' - ' + this.currentInventory.possessionStatus
      }
    },
    totalGP: function () {
      return 0
    },
    hasAccessories: function () {
      return true
    },
    hasLoaners: function () {
      return true
    },
    hasWorks: function () {
      return true
    },
    hasServiceRequests: function () {
      if (this.currentInventory.ServiceRequests && this.currentInventory.ServiceRequests.length > 0) {
        return true
      } else {
        return false
      }
    },
    ...mapUserGetters(['hasFeatureAccess', 'currentUser', 'lastActiveListPage']),
    ...mapDealerState(['currentDealer']),
    ...mapConfigGetters(['definedTypes']),
    ...mapVehicleMakeGetters(['allVehicleMakes']),
    /*
     * ⚠ The comparison util names its flags vehicleMakeDifferent/modelNameDifferent/..., while
     * CarfaxVehicleComparison reads make/model/productionYear/trim/style. The other three hosts map
     * that inline in their templates; this one does it in a computed because the inline version
     * shipped here with the raw object bound straight through, so every row rendered unmarked while
     * the box itself opened correctly - a mistake no assertion about the raw flags can catch.
     */
    carfaxMatchTableDifferences: function () {
      const flags = this.carfaxMatchDifferences || {}
      return {
        make: flags.vehicleMakeDifferent,
        productionYear: flags.productionYearDifferent,
        model: flags.modelNameDifferent,
        trim: flags.trimDifferent,
        style: flags.styleDifferent
      }
    },
    /*
     * The record in the shape the shared comparison reads. ⚠ selectedModel carries the catalogue
     * row's NAME because the free-text branch of the comparison falls back to comparing names when
     * carfax resolved no row - without it every such record would report a model difference.
     */
    carfaxMatchRecordShape: function () {
      if (!this.currentInventory) return null
      return {
        makeId: this.currentInventory.vehicleMakeId,
        modelId: this.currentInventory.vehicleMakeVersionId,
        productionYear: this.currentInventory.vehicleProductionYear,
        trim: this.currentInventory.trim,
        style: this.currentInventory.style,
        customMakeName: this.currentInventory.customMake,
        customModelName: this.currentInventory.customModel,
        selectedMake: this.currentInventory.vehicleMakeName ? { name: this.currentInventory.vehicleMakeName } : null,
        //⚠ The series is looked up from the catalogue, not read off the inventory DTO, which does not
        //carry it. Without it a record on a split row - family in name, variant in series - is
        //compared on its family alone and reports a difference it does not have. Meeting change #8.
        selectedModel: this.currentInventory.vehicleModelName
          ? {
            name: this.currentInventory.vehicleModelName,
            series: catalogueModelSeries(this.allVehicleMakes, this.currentInventory.vehicleMakeId, this.currentInventory.vehicleMakeVersionId)
          }
          : null
      }
    },
    /*
     * The stored report this screen compares against, or null when there is nothing to compare.
     *
     * ⚠ NULL FOR THE CARFAX TESTER, which is what stops the icon being drawn for them at all. The
     * inventory mixin's copy records why: offering it to a role CARFAX data is withheld from gives a
     * control that silently does nothing, and the "already matches" toast would then be a claim about
     * a report they are not allowed to see. The predicate fails closed while roles are loading.
     *
     * ⚠ The RECORD's stored rating, deliberately - that is the whole point of this control being free.
     * raiseCarfaxMatch's own path uses the rating carried on the refresh emit instead, because the
     * in-memory DTO is stale immediately after a refresh; here there is no refresh to be stale after.
     */
    carfaxRatingForMatch: function () {
      if (hasCarfaxTesterRole(this.currentUser)) return null
      return (this.currentInventory && this.currentInventory.carFaxRating) || null
    },
    /*
     * ⚠ BUILT ONCE and shared by the icon colour, its tooltip and the click's outcome, so all three
     * can never disagree about the same vehicle. Same arrangement as the inventory mixin and
     * MakeModelSelector, both of which had to be corrected into it.
     */
    carfaxMatchComparison: function () {
      const rating = this.carfaxRatingForMatch
      if (!rating || !rating.make) return null
      if (!this.allVehicleMakes || this.allVehicleMakes.length === 0) return null
      return buildCarfaxComparison(this.allVehicleMakes, rating, this.carfaxMatchRecordShape)
    },
    carfaxMatchIconClass: function () {
      return carfaxMatchIconClass(this.carfaxMatchComparison)
    },
    /*
     * ⚠ 'pick' for a reader who may correct, reversing the override this file used to carry.
     *
     * It passed false because this screen had no MakeModelSelector and never mounted
     * CarfaxModelReconcile, so the tooltip promising "pick the model CARFAX found" pointed at a table
     * with nothing to pick. Since meeting change #10 the model IS changed here, through
     * CarfaxModelCorrectionModal - but only by a reader holding inventory.modify. Anyone else is
     * offered nothing, or the tooltip promises the Edit button they cannot see.
     */
    carfaxMatchHoverText: function () {
      return carfaxMatchHoverText(this.carfaxMatchComparison, this.canCorrectCarfaxModel ? 'pick' : 'none')
    },
    /*
     * Who may correct the model from the CARFAX comparison. ONE definition for the four places that
     * ask - the Edit button, its handler, the banner's question and the tooltip's offer - so they
     * cannot drift apart. The server enforces the same right on the save.
     */
    canCorrectCarfaxModel: function () {
      return this.hasFeatureAccess('inventory.modify')
    },
    canCheckCarfaxMatch: function () {
      const rating = this.carfaxRatingForMatch
      return !!(rating && rating.make)
    },
    primaryDeal: function () {
      if (this.currentInventory.deals.length > 0) {
        return this.currentInventory.deals.find((x) => x.items.find((y) => y.isPrimary === true) !== null)
      }
      return null
    },
    primaryDealSalesPersonName: function () {
      return this.primaryDeal.salesPersonName
    },
    primaryDealCustomerName: function () {
      return this.primaryDeal.buyerName
    },
    primaryDealStatusId: function () {
      if (this.primaryDeal) {
        return this.primaryDeal.typeId
      }
      return -1
    },
    primaryDealStatusName: function () {
      if (this.primaryDeal) {
        return this.primaryDeal.type
      }
      return 'Available'
    },
    primaryDealGenericStatusName: function () {
      if (this.primaryDeal) {
        return this.primaryDeal.status
      }
      return ''
    },
    allowedDealInventoryTypes: function () {
      return this.definedTypes.inventoryTypes.filter(x => x.altName !== 'ForPpaAppraisal' && x.altName !== 'PpaClosed' && x.altName !== 'LeaseReturn' && x.altName !== 'TradeIn')
    },
    isPreEntry: function () {
      return this.currentInventory.status === 'PreEntry'
    },
    minimumUsageAllowed: function () {
      if (this.currentInventory && this.currentInventory.vinUsageHistory && this.currentInventory.vinUsageHistory.length > 0) {
        return this.currentInventory.vinUsageHistory[this.currentInventory.vinUsageHistory.length - 1].usage
      }
      return 0
    },
    usageRequired: function () {
      return this.selectedPossessionStatus && this.selectedPossessionStatus.id !== 1
    },
    isTypePpaAppraisal: function () {
      return this.currentInventory.type === 'ForPpaAppraisal' || this.isPpaClosed
    },
    isTypeTrade: function () {
      return this.currentInventory.type === 'TradeIn'
    },
    isTypePpaTradeAppraisal: function () {
      return (this.currentInventory.type === 'ForPpaAppraisal' || this.isPpaClosed) && this.currentInventory.ppaIntention === 'Trade'
    },
    isStatusArchived: function () {
      return this.currentInventory.status === 'Archived'
    },
    isTypeConsigned: function () {
      return this.currentInventory.type === 'Consigned'
    },
    canShowPreEntry () {
      return !this.isTypePpaAppraisal
    },
    canShowFleet () {
      return !this.isTypePpaAppraisal
    },
    canShowDemo () {
      return !this.isTypePpaAppraisal
    },
    canShowMarketable () {
      return !this.isTypePpaAppraisal
    },
    currentInventoryaccessoryDetailStatusType: function () {
      if (this.currentInventory.booksStatus.length === 0 && this.currentInventory.batteryChargerStatus.length === 0 && this.currentInventory.carCoverStatus.length === 0 && this.currentInventory.keyStatus.length === 0) {
        return 2
      }
      return this.currentInventory.accessoryDetailStatusType
    },
    showDispositionIntention: function () {
      return [1, 2, 4, 5].indexOf(this.currentInventory.typeId) >= 0
    },
    isLeaseReturn: function () {
      return this.currentInventory.type === 'LeaseReturn'
    },
    availableInventoryOfferTypes: function () {
      return this.definedTypes.inventoryOfferTypes
    },
    getTotalAdditionalCostsHeader: function () {
      if (!this.currentInventory) return ''
      return 'Total: ' + (this.currentInventory.additionalCostsTotal ? this.$formatCurrency(this.currentInventory.additionalCostsTotal) : '0.00')
    },
    getHighestOfferOverview: function () {
      if (this.currentInventory.highestOfferAmount > 0) {
        if (this.filteredLeads && this.filteredLeads.length > 0) {
          return 'Highest Offer: ' + this.$formatCurrency(this.currentInventory.highestOfferAmount) + ' (' + this.currentInventory.offersCount + '/' + this.filteredLeads.length + ') '
        } else {
          return 'Highest Offer: ' + this.$formatCurrency(this.currentInventory.highestOfferAmount) + ' (' + this.currentInventory.offersCount + '/' + this.currentInventory.leadCount + ') '
        }
      } else {
        if (this.filteredLeads && this.filteredLeads.length > 0) {
          return 'No Offers: ' + ' (' + this.currentInventory.offersCount + '/' + this.filteredLeads.length + ') '
        } else {
          return 'No Offers: ' + ' (' + this.currentInventory.offersCount + '/' + this.currentInventory.leadCount + ') '
        }
      }
    },
    selectedInventoryStatusType: function () {
      return this.definedTypes.inventoryTypes.find((x) => (x.name === this.currentInventory.type || x.altName === this.currentInventory.type))
    },
    hasPpaReceipt: function () {
      if (!this.currentInventory || !this.currentInventory.attachments) {
        return false
      }
      var primaryImages = this.currentInventory.attachments.filter((x) => x.classType === 'PpaApprovedReceipt')
      return primaryImages.length > 0
    },
    getPpaReceipt: function () {
      if (!this.currentInventory || !this.currentInventory.attachments) {
        return []
      }
      var primaryImages = this.currentInventory.attachments.filter((x) => x.classType === 'PpaApprovedReceipt')
      var result = []
      primaryImages.forEach((x) => {
        x.loaded = true
        x.imageData = x.fileLocation
        x.fileType = this.getFileType(x.fileLocation)
        result.push(x)
      })
      return result
    },
    getPpaExaminedTitle: function () {
      return this.currentInventory.ppaExamined ? 'PPA was physically examined.' : 'PPA not physically examined.'
    },
    getPpaExaminedColourClass: function () {
      return this.currentInventory.ppaExamined ? 'has-text-success' : 'has-text-danger'
    },
    showCostsSection: function () {
      if (this.currentInventory && this.currentInventory.type) {
        return true
      } else {
        return false
      }
    },
    showUpdateLocation: function () {
      return this.showVehicleLocation && this.hasFeatureAccess(this.updateInventoryLocationPermission)
    },
    showVehicleLocation: function () {
      if (!this.selectedTypeStatus) {
        return false
      } else {
        return [0, 1, 3, 4, 5, 6].indexOf(this.selectedTypeStatus.id) >= 0
      }
    },
    isArchived: function () {
      return this.currentInventory.status === 'Archived'
    },
    showAddSupplementalCost: function () {
      return this.hasFeatureAccess(this.addInventorySupplementalCost) && !this.isPpaClosed
    },
    isSpiffExpirationDateRequired: function () {
      return this.currentInventory.spiff && this.currentInventory.spiff > 0 ? 'required' : ''
    },
    hasPpaRecord: function () {
      return this.currentInventory && this.currentInventory.cloneSourceInventoryId && this.currentInventory.cloneSourceType === 'PpaClosed'
    },
    showPpaConversionButton: function () {
      return this.currentInventory && this.isTypePpaAppraisal && this.currentInventory.ppaApproved && !this.isStatusArchived
    },
    showPpaCloseButton: function () {
      return this.currentInventory && this.isTypePpaAppraisal && !this.isStatusArchived
    },
    tradeDealInventoryTypes: function () {
      return this.definedTypes.inventoryTypes.filter(x => x.altName === 'ForPpaAppraisal' || x.altName === 'TradeIn')
    },
    getDealColorClass: function () {
      var result = 'has-text-grey'
      if (this.currentInventory) {
        if (this.currentInventory.soldDealItem || this.currentInventory.leaseDealItem) {
          result = 'has-text-success'
        } else if (this.currentInventory.tradeDealItem) {
          result = 'has-text-info'
        }
      }
      let classToAttach = { }
      classToAttach[result] = true
      return classToAttach
    },
    getDealDataType: function () {
      var result = ''
      if (this.currentInventory) {
        if (this.currentInventory.soldDealItem || this.currentInventory.leaseDealItem) {
          result = 'D'
        } else if (this.currentInventory.tradeDealItem) {
          result = 'T'
        }
      }
      return result
    },
    isPpaClosed: function () {
      return this.currentInventory.type === 'PpaClosed'
    },
    tradeSourceDeal: function () {
      if (!this.isTradeLinkedToDeal) return null
      let tradeSourceDeal = {
        id: this.currentInventory.tradeSourceDealId,
        type: this.currentInventory.tradeSourceDealStatus,
        status: this.currentInventory.tradeSourceDealGenericStatus,
        purchaserId: this.currentInventory.tradeSourcePurchaserId,
        purchaserName: this.currentInventory.tradeSourcePurchaserFullName,
        tradeAllowance: this.currentInventory.tradeAllowance,
        primaryDealItem: {isPrimary: this.currentInventory.tradeSourceDealIsPrimary, forInventory: {id: this.currentInventory.tradeSourceInventoryId}},
        cancellationReason: this.currentInventory.tradeSourceDealCancellationReason
      }
      return tradeSourceDeal
    },
    primaryDealIsBookingFinalised: function () {
      if (this.primaryDeal) {
        return this.primaryDeal.isBookingFinalised
      }
      return false
    },
    autoCheckEnabled: function () {
      if (this.currentDealer) {
        return this.currentDealer.autoCheckEnabledStatus
      }
      return false
    },
    mostRecentBlackBookInformation: function () {
      if (this.currentInventory && this.currentInventory.blackBook) {
        return this.currentInventory.blackBook
      }
      return null
    },
    blackBookEnabled: function () {
      if (this.currentDealer) {
        return this.currentDealer.blackBookEnabledStatus
      }
      return false
    },
    batchPaymentRequestRef: function () {
      return 'inventory-batch-payment-request' + this.currentInventoryId
    },
    currentCommissionRequest: function () {
      if (this.currentInventory && this.currentInventory.currentCommissionRequest) {
        return this.currentInventory.currentCommissionRequest
      }
      return null
    },
    hasCurrentCommissionRequest: function () {
      if (this.currentCommissionRequest) {
        return true
      }
      return false
    },
    currentCommissionRequestStatus: function () {
      if (this.currentCommissionRequest && this.currentCommissionRequest.commissionRequestStatus) {
        return this.currentCommissionRequest.commissionRequestStatus
      }
      return ''
    },
    purchaseCommissionTitle: function () {
      if (this.hasCurrentCommissionRequest) {
        if (this.currentCommissionRequestStatus === 'Requested') {
          return 'Requested Commission'
        }
      }
      return 'Purchase Commission'
    },
    hasCommissionRequestModifyAccess: function () {
      return this.hasFeatureAccess('inventory.commission.approve.reject')
    },
    commissionAmountChanged: function () {
      if (this.commissionAmount === this.initalCommissionAmount) {
        return false
      }
      return true
    },
    requiresDefaultCostsSetting: function () {
      return this.isPreEntry && (this.currentInventory.isPpaConverted || this.currentInventory.isServiceLoanerConverted)
    },
    mustUnlockCosts: function () {
      if (this.requiresDefaultCostsSetting) return true
      return this.isCostsUnlocked
    },
    hasBatchPaymentRequestAccess: function () {
      let allowedPermissions = ['inventory.issue.batch-payment-request']
      var userHasAccess = false
      allowedPermissions.forEach((x) => {
        userHasAccess = userHasAccess || this.hasFeatureAccess(x, false)
      })
      return userHasAccess
    },
    canSendCheckRequest: function () {
      return this.lastSignedSellersAgreement !== '' && this.hasBatchPaymentRequestAccess && this.currentInventory && this.currentInventory.purchasedFromType !== 'Factory'
      // && this.currentInventory.isCdkSynced
    },
    isSourceTrusted: function () {
      // if (this.currentInventory && this.currentInventory.purchasedFromType === 'Wholesaler') {
      //   if (this.currentSupplier && this.currentSupplier.trusted === true) {
      //     return true
      //   } else if ((this.currentSupplier && this.currentSupplier.trusted === false) && (this.currentInventory && this.currentInventory.titleUploadStatus === 2)) {
      //     return true
      //   } else {
      //     return false
      //   }
      // } else {
      return true
      // }
    },
    cpoStatusLabel: function () {
      if (!this.currentInventory) {
        return ''
      }
      if (this.currentInventory.cpoStatus !== 'Ineligible') {
        return this.currentInventory.cpoStatus
      }
      return !this.currentInventory.isDealerInventory ? 'Used' : 'Non Cpo'
    },
    isIndividual: function () {
      return this.currentInventory && this.currentInventory.purchasedFromType === 'Individual'
    },
    hasClosePpaAccess: function () {
      let allowedPermissions = ['inventory.modify.mark-ppaclosed']
      var userHasAccess = false
      allowedPermissions.forEach((x) => {
        userHasAccess = userHasAccess || this.hasFeatureAccess(x)
      })
      return userHasAccess
    }
  },
  methods: {
    // Route params arrive as strings after a reload or a serialized workspace route ('false' is truthy).
    normaliseRouteBoolean: function (value, fallback) {
      if (value === undefined || value === null || value === '') return !!fallback
      if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1'
      return value === true || value === 1
    },
    /*
     * The quick view is where Garrett was clicking - and its CARFAX refresh was a dead end: the
     * control was bound only to on-carfax-requested, so the toast fired and nothing could follow.
     *
     * The comparison itself applies nothing. Its Edit button raises CarfaxModelCorrectionModal, which
     * saves the description alone through the narrow endpoint (meeting change #10).
     */
    /*
     * ⚠ Compares against the rating carried ON THE EMIT, not currentInventory.carFaxRating: a refresh
     * writes the new rating server-side and the in-memory DTO here is still the old one.
     */
    onCarFaxVehicleInformation: function (carfaxRating, context) {
      if (!context || context.userRequested !== true) return
      this.raiseCarfaxMatch(carfaxRating)
    },
    /*
     * The free match check, restored to this screen.
     *
     * ⚠ IT ASKS CARFAX FOR NOTHING. Both sides are already in memory - the report stored on the record
     * and the catalogue - so this costs nothing, where the refresh that was left as the only way in
     * costs a CARFAX fetch, two extractions and twenty to thirty seconds. It also works in the two
     * cases refresh cannot: with no live CARFAX session, and on a report no longer owned, where the
     * refresh raises the payment prompt instead of comparing. 4414299be1 recorded exactly this when
     * the button was removed as redundant.
     *
     * ⚠ THREE OUTCOMES, NOT TWO. raiseCarfaxMatch returns silently unless the values differ, which is
     * right for a refresh - the operator asked for a refresh, not a verdict - and wrong for a
     * deliberate click, which would be a control that does nothing. That is the H4 defect exactly, on
     * a different screen.
     *
     * ⚠ The toasts come from the utilities mixin, unlike the two sibling copies of this branch, which
     * call $buefy directly for one stated reason: NewInventory does not include that mixin. This
     * screen does.
     */
    onCarfaxMatchIconClick: function () {
      const outcome = carfaxMatchOutcome(this.carfaxMatchComparison)

      if (outcome === 'differs') {
        //⚠ Through raiseCarfaxMatch, not by setting the modal's fields again - one write path to it.
        this.raiseCarfaxMatch(this.carfaxRatingForMatch)
        return
      }

      if (outcome === 'matches') {
        this.infoToast('The vehicle information already matches the stored CARFAX report.')
        return
      }

      this.warningToast('This vehicle could not be compared against the CARFAX report. The make on the report is not in the vehicle list.')
    },
    /*
     * The grid icon clicked while this quick view is already open on the vehicle (the $route watcher
     * below). Same guard as the arrival flag: silent when it cannot compare.
     *
     * ⚠ Checked against the LOADED record, so a route naming another vehicle is left to the id watcher.
     */
    onCarfaxMatchRequested: function (inventoryId) {
      if (!this.currentInventory || +this.currentInventory.id !== +inventoryId) return
      if (this.canCheckCarfaxMatch) this.onCarfaxMatchIconClick()
    },
    raiseCarfaxMatch: function (carfaxRating) {
      this.showCarfaxMatch = false
      if (!this.currentInventory || !carfaxRating || !carfaxRating.make) return

      //⚠ An empty allVehicleMakes means the store has not loaded, which is "cannot compare" and NOT
      //"no differences". Staying silent is right either way: raising nothing is honest, whereas
      //showing a box would claim a comparison that never ran.
      if (!this.allVehicleMakes || this.allVehicleMakes.length === 0) {
        return
      }

      const record = this.carfaxMatchRecordShape
      const comparison = buildCarfaxComparison(this.allVehicleMakes, carfaxRating, record)
      const differences = comparison.differences

      //A refresh that happens to agree says nothing - the operator asked for a refresh, not for a
      //verdict on the make and model. The same silence covers a comparison that could not run at
      //all; neither case is worth interrupting a refresh for.
      if (carfaxMatchOutcome(comparison) !== 'differs') {
        return
      }

      this.carfaxMatchRecord = record
      this.carfaxMatchCarfax = comparison.carfaxMakeModel
      this.carfaxMatchDifferences = differences
      //Same right as the Edit button below the table - no question the reader has no button to answer.
      this.carfaxMatchMessage = buildReconcileMessage(this.canCorrectCarfaxModel)
      this.showCarfaxMatch = true
    },
    /*
     * The comparison's Edit button: closes the table and raises CarfaxModelCorrectionModal over this
     * screen. "Edit", not "Correct it" - Garrett, 17 Sep, wanted a label that does not promise more
     * than the click does.
     *
     * ⚠ Guarded here as well as by the button's v-if, so the right is enforced where the modal is
     * RAISED, not only where its button is drawn. Without it, any later caller of this method would
     * let a reader without the right work through the whole correction before the server refused it.
     */
    onCarfaxMatchCorrect: function () {
      this.showCarfaxMatch = false
      if (!this.canCorrectCarfaxModel) return
      /*
       * ⚠ Raised by a FLAG, not by a $ref. The modal renders through the global modal portal so it is
       * not clamped to this panel's width - and portalled content is not reachable through $refs, so
       * a ref.open() here silently did nothing and fell through to navigating away, which is exactly
       * the behaviour this change exists to remove. A prop crosses the portal; a ref does not.
       *
       * The old navigation stays as the fallback for a record still loading, because the modal is
       * behind v-if on currentInventory and an Edit that does nothing at all is worse.
       */
      if (!this.currentInventory) {
        this.onEdit({ autoLaunchControl: 'carfaxMakeModel' })
        return
      }
      this.carfaxCorrectionOpen = true
    },
    /*
     * The narrow save came back with the whole record.
     *
     * ⚠ Merged, not ignored - the response carries the new rowversion, and the correction modal
     * posts it back as expectedVersion. Drop the merge and this screen keeps the pre-save token, so
     * the next correction is refused as a conflict that never happened.
     */
    onCarfaxModelCorrected: function (inventory) {
      if (!inventory) return
      /*
       * ⚠ Reassigned, not mutated in place, so carfaxMatchComparison re-evaluates. That computed is
       * what colours the match badge, so the icon reflects the correction without this screen calling
       * anything. Reaching for the inventory mixin's match-check method would be a defect this
       * screen's own spec pins by name - it sets a flag only the selector's inline comparison reads,
       * and there is no selector here.
       */
      this.currentInventory = Object.assign({}, this.currentInventory, inventory)
      /*
       * ⚠ ONE event reloads BOTH - Dean, 30 Sep: "make sure the quick-view and list are reloading".
       *
       * This screen is itself a listener (onInventoryChanged -> loadInventory), and so is every one
       * of the twelve inventory lists through the shared mixin (onInventoryChanged -> loadAsyncData).
       * So the emit refreshes the quick view and whichever grid the operator came from, without this
       * screen needing to know which one that was. usageComponentUpdateHandler below already does the
       * same thing for the same reason.
       *
       * ⚠ 'reload-inventory-list' would NOT do. Only ListInventory binds it, and the correction icon
       * is on all twelve grids, so eleven of them would silently keep showing the old model.
       *
       * ⚠ The merge above stays. The reload is a round trip, and without the merge the badge shows
       * the pre-correction colour until it lands.
       */
      eventBus.$emit('inventory-changed', [this.currentInventory])
    },
    /*
     * A refused correction. When the refusal is a version conflict the record has moved on under this
     * screen, so RELOAD - the token this screen holds is the reason the save was refused, and without a
     * reload every retry posts the same stale token and is refused again until the operator closes and
     * reopens the quick view. The message promises "It has been reloaded"; this is what keeps that true.
     *
     * ⚠ Only on module 'concurrency'. Any other failure is already on screen as a toast, and reloading
     * over a validation error would throw away nothing useful and hide what went wrong.
     */
    onCarfaxModelCorrectionFailed: function (error) {
      if (!error || error.module !== 'concurrency') return
      eventBus.$emit('inventory-changed', [this.currentInventory])
    },
    /*
     * ⚠ Every narrow action on this screen that writes the row bumps its rowversion, and each merges
     * back only the field it changed - so without this the correction modal carries a token this
     * screen's own action has already made stale, and its next save is refused as a conflict nobody
     * else caused. Each of those endpoints returns the version read back at its commit.
     *
     * ⚠ Guarded on the id. The quick view can be pointed at a different vehicle before a reply lands,
     * and a late reply must not stamp its token onto the new record. A reply without a version leaves
     * the token alone rather than blanking it, which would switch the server's check off.
     */
    takeVersion: function (id, version) {
      if (!version || !this.currentInventory || this.currentInventory.id !== id) return
      this.currentInventory.version = version
    },
    onViewSalesVideoImages: function (id) {
      var reference = 'salesVideosImageViewer_' + id
      const viewer = this.$refs[reference].$viewer
      viewer.show()
    },
    onDownloadSalesVideo: function (row) {
      window.location = row.fileLocation
    },
    onSaveNewLocation: function () {
      if (this.currentInventory && this.selectedLocation && this.currentInventory.locationId !== this.selectedLocation.id) {
        this.onLocationUpdated()
        return
      }
      if (this.currentInventory && this.currentInventory.locationId && !this.selectedLocation) {
        this.onLocationUpdated()
      }
    },
    onCancelEditLocation: function () {
      this.isEditingLocation = false
    },
    onEditLocation: function () {
      this.isEditingLocation = true
    },
    onViewToPricingPod: function () {
      let that = this
      this.isPricingCollapsed = false
      this.$nextTick(() => {
        setTimeout(function () {
          let element = that.$refs['pricingPod']
          if (element) {
            that.$goToBlock(element, {
              behavior: 'smooth',
              block: 'end',
              inline: 'end'
            })
          }
        }, 100)
      })
    },
    onDispositionIntentionChanged: function () {
      this.isSavingNewDispositionIntention = true
      let newDispositionIntentionStatusId = this.dispositionIntentionValue === 'Retail' ? 0 : 2
      const id = this.currentInventory.id
      inventoryService.updateDispositionIntention(this.currentInventory, newDispositionIntentionStatusId).then(response => {
        this.isSavingNewDispositionIntention = false
        this.takeVersion(id, response.version)
        this.currentInventory.dispositionIntention = response.dispositionIntention
        this.currentInventory.originalDispositionIntention = response.originalDispositionIntention
        this.currentInventory.wholesaleNow = response.dispositionIntention !== 'Retail'
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSavingNewDispositionIntention = false
      })
    },
    onConfirmImageRemoval: function () {
      if (this.currentInventory.id) {
        var inventoryModel = {
          id: this.currentInventory.id
        }

        this.serverErrorMessage = ''
        this.serverErrors = []
        this.isSaving = true

        inventoryService.markImagesAsDeleted(inventoryModel).then(response => {
          this.isSaving = false
          if (response.errors) {
            this.serverErrors = response.errors
          } else {
            this.takeVersion(inventoryModel.id, response.inventoryModel && response.inventoryModel.version)
            this.successToast('Images removed successfully')
            this.isConfirmRemoveImagesActive = false
            this.onChangesSaved()
          }
        }).catch((error) => {
          this.serverErrorMessage = error.message
          this.isSaving = false
        })
      }
    },
    onPublishStatusChanged: function () {
      this.isSavingNewPublishStatus = true
      let newPublishStatus = !this.publishStatusValue
      const id = this.currentInventory.id
      inventoryService.updatePublishStatus(this.currentInventory, newPublishStatus).then(response => {
        this.isSavingNewPublishStatus = false
        this.takeVersion(id, response.version)
        this.currentInventory.doNotPublish = response.doNotPublish
        this.isPublishStatusChangeActive = false
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSavingNewPublishStatus = false
      })
    },
    onCancelPublishStatusChange: function () {
      if (!this.currentInventory.doNotPublish) {
        this.publishStatusValue = true
      } else {
        this.publishStatusValue = false
      }
      this.isPublishStatusChangeActive = false
    },
    buildServiceRequestTitle: function (inventory) {
      let content = 'Get ready status'
      return {
        content: content,
        delay: {
          show: 500,
          hide: 100
        }
      }
    },
    buildAccessoryStatusTitle: function (inventory) {
      let content = ''
      if (inventory.accessoryDetailWarnings) {
        Object.keys(inventory.accessoryDetailWarnings).forEach(x => {
          if (x === 'books') {
            content += '<i class="fal fa-books tip-icon ' + (inventory.accessoryDetailWarnings[x].length > 0 ? ((inventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') + '"></i>' + inventory.accessoryDetailWarnings[x] + '</br>') : ('has-text-grey' + '"></i>' + '' + '</br>'))
          }
          if (x === 'batteryCharger') {
            content += '<i class="fal fa-car-battery tip-icon ' + (inventory.accessoryDetailWarnings[x].length > 0 ? ((inventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') + '"></i>' + inventory.accessoryDetailWarnings[x] + '</br>') : ('has-text-grey' + '"></i>' + '' + '</br>'))
          }
          if (x === 'carCover') {
            content += '<i class="fal fa-car-garage tip-icon ' + (inventory.accessoryDetailWarnings[x].length > 0 ? ((inventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') + '"></i>' + inventory.accessoryDetailWarnings[x] + '</br>') : ('has-text-grey' + '"></i>' + '' + '</br>'))
          }
          if (x === 'keys') {
            content += '<i class="fal fa-key tip-icon ' + (inventory.accessoryDetailWarnings[x].length > 0 ? ((inventory.accessoryDetailWarnings[x] === 'Good' ? 'has-text-success' : 'has-text-danger') + '"></i>' + inventory.accessoryDetailWarnings[x] + '</br>') : ('has-text-grey' + '"></i>' + '' + '</br>'))
          }
        })
      }
      return {
        content: content,
        delay: {
          show: 500,
          hide: 100
        }
      }
    },
    buildConsignmentStatusTitle: function (inventory) {
      let content = ''
      content += 'Consignment Type: ' + inventory.saleProceedsType + '</br>'
      if (inventory.saleProceedsType === 'Flat') {
        content += 'Minimum Sale Price: $' + new Intl.NumberFormat().format(inventory.consignmentMinimum) + '</br>'
      } else {
        content += 'Sale Price: $' + new Intl.NumberFormat().format(inventory.askingPrice) + '</br>'
      }
      if (inventory.saleProceedsType === 'Percentage') {
        content += 'Consignment Percentage: ' + inventory.consignmentPercentage + '%</br>'
      }
      if (inventory.saleProceedsType === 'Split' || inventory.saleProceedsType === 'Flat') {
        content += inventory.saleProceedsType === 'Split' ? 'Split Value: $' + new Intl.NumberFormat().format(inventory.consignmentAgreementValue) + '</br>' : 'Dealer Flat Value: $' + new Intl.NumberFormat().format(inventory.consignmentAgreementValue) + '</br>'
      }
      if (inventory.saleProceedsType === 'Split') {
        content += 'Split Ratio: ' + inventory.percentageSplitFirst + '% / ' + inventory.percentageSplitSecond + '%</br>'
      }
      return {
        content: content,
        delay: {
          show: 500,
          hide: 100
        }
      }
    },
    onAccessoriesStatusClick: function () {
      let that = this
      that.isAccessoriesCollapsed = false
      this.$nextTick(() => {
        setTimeout(function () {
          let element = that.$refs['accessoriesPod']
          if (element) {
            that.$goToBlock(element, {
              behavior: 'smooth',
              block: 'end',
              inline: 'end'
            })
          }
        }, 100)
      })
    },
    //The inventory wrench on a vehicle with no service request (isServiceRequestPod) - scroll once loaded.
    consumeServiceRequestPodOnLoad: function () {
      if (!this.showServiceRequestPodOnLoad) return
      this.showServiceRequestPodOnLoad = false
      this.onServiceRequestStatusClick()
    },
    onServiceRequestStatusClick: function () {
      let that = this
      that.isWorkShopCollapsed = false
      this.$nextTick(() => {
        setTimeout(function () {
          let element = that.$refs['serviceRequestPod']
          if (element) {
            that.$goToBlock(element, {
              behavior: 'smooth',
              block: 'end',
              inline: 'end'
            })
          }
        }, 100)
      })
    },
    onDealStatusClick: function () {
      if (!this.primaryDeal) return
      let that = this
      that.isDealsCollapsed = false
      this.$nextTick(() => {
        setTimeout(function () {
          let element = that.$refs['deal-' + that.primaryDeal.id][0]
          if (element) {
            that.$goToBlock(element, {
              behavior: 'smooth',
              block: 'end',
              inline: 'end'
            })
          }
        }, 100)
      })
    },
    onCopy: function (type, data) {
      let that = this
      this.$copyText(data).then(function (e) {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: type + ' copied to clipboard',
            position: 'is-top',
            type: 'is-info'
          })
      }, function (e) {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: 'Could not copy ' + type + ' to clipboard',
            position: 'is-top',
            type: 'is-warning'
          })
      })
    },
    onResetvAutoError: function () {
      this.isResettingvAutoError = true
      const id = this.currentInventory.id
      inventoryService.resetvAutoError(this.currentInventory).then(response => {
        if (response.result === true) {
          this.currentInventory.vAutoLastError = ''
        }
        this.takeVersion(id, response.version)
        this.isResettingvAutoError = false
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isResettingvAutoError = false
      })
    },
    onResetCdkError: function () {
      this.isResettingCdkError = true
      const id = this.currentInventory.id
      inventoryService.resetCdkError(this.currentInventory).then(response => {
        if (response.result === true) {
          this.currentInventory.cdkLastError = ''
        }
        this.takeVersion(id, response.version)
        this.isResettingCdkError = false
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isResettingCdkError = false
      })
    },
    onShowMoreAccessoryEvents: function () {
      this.isLoading = true
      inventoryService.getAllAccessoryEventsByInventory(this.currentInventory).then(response => {
        this.currentInventory.accessoryEvents.push(...response)
        this.isLoading = false
      }).catch((error) => {
        this.isLoading = false
        this.serverErrorMessage = error.message
      })
    },
    onShowMoreServiceRequests: function () {
      if (this.hasFeatureAccess('servicerequest.view')) {
        let that = this
        this.checkDirty(function () {
          that.$emit('close')
          that.$router.push({name: 'ListServiceRequests', query: { filter: true, query: that.currentInventory.stockNo }})
        })
      }
    },
    onServiceRequestRowSelected: function (row) {
      if (this.hasFeatureAccess('servicerequest.view')) {
        let that = this
        this.checkDirty(function () {
          that.$emit('close')
          that.$router.push({name: 'ServiceRequestsQuickView', params: { id: row.id }})
        })
      }
    },
    onAppraised: function (appraisal) {
      if (appraisal && appraisal !== '') {
        this.currentInventory.appraisals.unshift(appraisal)
      }
    },
    addSpacingToAttributeName: function (name) {
      var s = name.replace(/([A-Z])/g, ' $1').trim()
      return s
    },
    onPricingCollapsedStatusChanged: function (newValue) {
      this.isPricingCollapsed = newValue
    },
    onAddDeal: function () {
      let that = this
      this.checkDirty(function () {
        that.$emit('close')
        that.$router.push({name: 'NewDeal', params: { inventoryItem: that.currentInventory }})
      })
    },
    onViewPurchasedFrom: function () {
      if (this.currentInventory.purchasedFromType !== 'Factory') {
        if (this.currentInventory.purchasedFromType === 'Wholesaler') {
          if (this.hasFeatureAccess('supplier.view')) {
            let that = this
            this.checkDirty(function () {
              that.$emit('close')
              that.$router.push({name: 'SuppliersQuickView', params: { id: that.currentInventory.purchasedFromId }})
            })
          }
        }
        if (this.isIndividual) {
          if (this.hasFeatureAccess('customer.view')) {
            let that = this
            this.checkDirty(function () {
              that.$emit('close')
              that.$router.push({name: 'CustomersQuickView', params: { id: that.currentInventory.purchasedFromId }})
            })
          }
        }
      }
    },
    onViewOriginalPpa: function () {
      if (this.currentInventory.cloneSourceInventoryId) {
        if (this.hasFeatureAccess('inventory.view')) {
          let that = this
          this.checkDirty(function () {
            that.$emit('close')
            that.$router.push({name: 'InventoryQuickView', params: { id: that.currentInventory.cloneSourceInventoryId, isServiceRequestPod: false, isLoanerPod: false, offerPod: -1, autoLaunchControl: null }})
          })
        }
      }
    },
    onViewCurrentInventory: function () {
      if (this.convertedInventoryId) {
        if (this.hasFeatureAccess('inventory.view')) {
          let that = this
          this.checkDirty(function () {
            that.$emit('close')
            that.$router.push({name: 'InventoryQuickView', params: { id: that.convertedInventoryId, isServiceRequestPod: false, isLoanerPod: false, offerPod: -1, autoLaunchControl: null }})
          })
        }
      }
    },
    onViewLeadCustomer: function (customerId) {
      if (this.hasFeatureAccess('customer.view')) {
        let that = this
        this.checkDirty(function () {
          that.$emit('close')
          that.$router.push({name: 'CustomersQuickView', params: { id: customerId }})
        })
      }
    },
    onViewDeal: function (deal) {
      if (this.hasFeatureAccess('deal.view')) {
        let that = this
        this.checkDirty(function () {
          that.$emit('close')
          that.$router.push({name: 'DealsQuickView', params: { id: deal.id }})
        })
      }
    },
    onViewPrimaryDeal: function () {
      if (!this.primaryDeal) return
      if (this.hasFeatureAccess('deal.view')) {
        let that = this
        this.checkDirty(function () {
          that.$emit('close')
          that.$router.push({name: 'DealsQuickView', params: { id: that.primaryDeal.id }})
        })
      }
    },
    onViewTrade: function (trade) {
      if (this.hasFeatureAccess('inventory.view')) {
        let that = this
        this.checkDirty(function () {
          that.$emit('close')
          that.$router.push({name: 'InventoryQuickView', params: { id: trade.forInventory.id, isServiceRequestPod: false, isLoanerPod: true, offerPod: -1, autoLaunchControl: null }})
        })
      }
    },
    humanizedPossessionStatus: function (row) {
      if (row && row.possessionStatus) {
        return this.definedTypes.possessionStatuses.find(x => x.altName === row.possessionStatus).name
      }
      return ""
    },
    //⚠ query is optional and defaults to none, so the plain Edit button is unchanged. It rides the
    //checkDirty wrapper rather than bypassing it - an unsaved-changes prompt still comes first.
    onEdit: function (query) {
      let that = this
      /*
       * ⚠ THE ARITY FOOTGUN. The template calls onEdit() with parens today, but a future
       * @click="onEdit" would hand this the native MouseEvent and vue-router would serialise its
       * enumerable properties into the URL. This cost a live defect on ApprovePpaAction earlier the
       * same day, so it is guarded rather than trusted: only a plain object is a query.
       */
      const routeQuery = (query && query.constructor === Object) ? query : {}
      this.checkDirty(function () {
        that.$emit('close')
        that.$router.push({name: 'EditInventory', params: { id: that.currentInventoryId }, query: routeQuery})
      })
    },
    onViewLifecycle: function () {
      this.$router.push({ name: 'VehicleLifecycleGraph', params: { id: this.currentInventory.id } })
    },
    onCancel: function () {
      let that = this
      this.checkDirty(function () {
        that.$emit('close')
        if (!that.isQuickView) {
          that.redirectToListRoute(false)
        } else {
          eventBus.$emit('clear-selected-item', {})
          that.redirectToListRoute(true)
        }
      })
    },
    savePricing: function () {
      var inventoryPricingModel = {
        inventoryId: this.currentInventory.id,
        msrp: this.currentInventory.msrp,
        askingPrice: this.currentInventory.askingPrice,
        leasePrice: this.currentInventory.leasePrice,
        spiff: this.currentInventory.spiff,
        spiffExpirationDate: this.currentInventory.spiffExpirationDate,
        isOnSpecial: this.currentInventory.isOnSpecial,
        specialExpirationDate: this.currentInventory.specialExpirationDate,
        generalLedgerBalance: this.currentInventory.generalLedgerBalance,
        purchaseCost: this.currentInventory.purchaseCost,
        payoffAmount: this.currentInventory.payoffAmount,
        reconCost: this.currentInventory.reconCost,
        consignmentTerms: this.currentInventory.consignmentTerms,
        consignmentPercentage: this.currentInventory.consignmentPercentage,
        consignmentMinimum: this.currentInventory.consignmentMinimum,
        consignmentNote: this.currentInventory.consignmentNote,
        commitmentFromId: this.selectedCommitmentFrom ? this.selectedCommitmentFrom.id : null,
        commitmentValue: this.currentInventory.commitmentValue,
        firmCommitment: this.currentInventory.firmCommitment,
        leaseNote: this.currentInventory.leaseNote,
        commission: this.currentInventory.commission,
        requestedCommission: this.commissionAmount,
        commissionRequestStatus: this.currentCommissionRequest ? this.currentCommissionRequest.commissionRequestStatus : null
      }

      this.serverErrorMessage = ''
      this.serverErrors = []
      this.isSaving = true
      let that = this
      inventoryService.updatePricingInfo(inventoryPricingModel).then(response => {
        that.isSaving = false
        if (response.errors) {
          that.serverErrors = response.errors
        } else {
          that.currentInventory = response.inventory
          that.setCommissionAmount()
          that.isEditLocked = true
          that.onChangesSaved()
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSaving = false
      })
    },
    loadInventory (id) {
      this.customerSyncRequired = false
      this.customerSyncRequested = false
      axios.cancel('view-inventory-quickview')
      axios.cancel('view-inventory-deal-quickview')
      if (this.currentInventory && this.currentInventory.blackBooks) {
        this.currentInventory.blackBooks = []
      }
      this.isEditLocked = true
      this.inventoryDealIsPrimaryActive = false
      this.currentInventoryId = id
      this.isLoading = true
      this.isResettingCdkError = false
      this.isDirty = false
      this.isPublishStatusChangeActive = false
      this.isConfirmRemoveImagesActive = false
      this.inventoryPurchaseOffers = []
      this.inventoryPurchaseOffersFetched = false
      this.isPublishStatusHistoryFetched = false
      this.isPossessionStatusHistoryFetched = false
      this.inventoryPublishHistoryStatus = []
      this.inventoryPossessionStatusHistory = []
      this.isFieldChangedHistoryFetched = false
      this.inventoryFieldChangedHistory = []
      this.inventoryLeads = []
      this.inventoryLeadsFetched = false
      this.filteredPurchaseOffers = []
      this.supplementalCosts = []
      this.isOffersCollapsed = true
      this.offersAndLeadsTabIndex = 0
      this.inventoryMiscellaneousFiles = []
      this.initialInventoryMiscellaneousFiles = []
      if (this.currentInventory && this.currentInventory.deals && this.currentInventory.deals.length > 0) {
        this.currentInventory.deals = []
      }

      let that = this
      let allActions = []
      allActions.push(
        inventoryService.single(this.currentInventoryId, 'view-inventory-quickview').then(response => {
          // check if parallel call to for deals resolved first and set deals on object again
          if (that.currentInventory && that.currentInventory.deals && that.currentInventory.deals.length > 0) {
            let deals = that.currentInventory.deals
            that.currentInventory = response.inventory
            that.currentInventory.deals = deals
          } else {
            that.currentInventory = response.inventory
          }
  
          that.currentInventory.note = that.currentInventory.notes.length > 0 ? that.currentInventory.notes[0].note : ''
          that.selectedCommitmentFrom = (that.currentInventory.commitmentFromId === null || that.currentInventory.commitmentFromId === 0) ? null : {id: that.currentInventory.commitmentFromId, name: that.currentInventory.commitmentFromName}
          that.selectedAccessoryStatus = that.definedTypes.accessoryEventType.options.find((x) => x.id === that.currentInventory.accessoryStatusId)
          that.selectedGetReadyStatus = that.definedTypes.serviceRequestStatusType.options.find((x) => x.id === that.currentInventory.getReadyStatusTypeId)
          that.selectedLoanerStatus = that.definedTypes.loanerEventType.options.find((x) => x.id === that.currentInventory.loanerStatusId)
          that.selectedPossessionStatus = that.definedTypes.possessionStatuses.find((x) => (x.name === that.currentInventory.possessionStatus || x.altName === that.currentInventory.possessionStatus))
          if (that.currentInventory.dispositionIntention === 'WholesaleImmediately') {
            that.dispositionIntentionValue = 'Wholesale'
          }

          if (that.currentInventory.dispositionIntention === 'Retail' || that.currentInventory.dispositionIntention === 'RetailForXDays') {
            that.dispositionIntentionValue = 'Retail'
          }

          if (!that.currentInventory.doNotPublish) {
            that.publishStatusValue = true
          } else {
            that.publishStatusValue = false
          }

          if (that.currentInventory.possessionStatusHistory?.length > 0) {
            that.inventoryPossessionStatusHistory = [...that.currentInventory.possessionStatusHistory]
            that.isPossessionStatusHistoryFetched = true
          }

          that.daysList = []
          that.tagsList = []
          that.currentInventory.tags.forEach(function (tag) {
            that.tagsList.push(tag)
          }, that)

          that.supplementalCosts = []
          that.initialSupplementalCosts = []
          that.currentInventory.supplementalCosts.forEach(function (supplementalCost) {
            that.supplementalCosts.push(supplementalCost)
            that.initialSupplementalCosts.push(supplementalCost)
          }, that)
          that.selectedDealer = { id: that.currentInventory.dealerId, name: that.currentInventory.dealerName }

          that.inventoryMiscellaneousFiles = that.getInventoryAttachmentsByType('Other')
          that.initialInventoryMiscellaneousFiles = _.cloneDeep(that.inventoryMiscellaneousFiles)

          that.daysList.push(
            {
              label: 'Days Since Purchase',
              value: that.currentInventory.daysSincePurchaseDisplay,
              dateValue: that.currentInventory.createdAt
            },
            {
              label: 'Days on Lot',
              value: that.currentInventory.daysOnLotDisplay,
              dateValue: that.currentInventory.arrivedAt
            },
            {
              label: 'Days Since Market',
              value: that.currentInventory.daysToMarketDisplay,
              dateValue: that.currentInventory.marketedAt
            },
            {
              label: 'Days Since Last Service',
              value: that.currentInventory.daysLastServiceDisplay,
              dateValue: that.currentInventory.lastServiceDate
            }
          )
          that.locationList = []
          that.locationList.push(
            {
              label: 'Current Location',
              value: that.currentInventory.currentLocationName || '-'
            },
            {
              label: 'Location',
              value: that.currentInventory.locationName || '-'
            }
          )
          that.expirationList = []
          if (that.currentInventory.daysToDispositionExpiryDisplay === 'Pending') {
            that.expirationList.push(
              {
                label: 'Days till Warranty Expiration',
                value: that.currentInventory.daysWarrantyExpirationDisplay,
                daysWarrantyExpiration: that.currentInventory.daysWarrantyExpiration,
                warrantyExpirationDate: that.currentInventory.warrantyExpirationDate,
                isWarranty: true
              },
              {
                label: 'Days to Disposition Expiration',
                value: that.currentInventory.daysToDispositionExpiryDisplay
              }
            )
          } else {
            that.expirationList.push(
              {
                label: 'Disposition Expiration Past Due',
                value: that.currentInventory.daysToDispositionExpiryDisplay,
                valueStyle: 'help is-danger'
              },
              {
                label: 'Days till Warranty Expiration',
                value: that.currentInventory.daysWarrantyExpirationDisplay,
                daysWarrantyExpiration: that.currentInventory.daysWarrantyExpiration,
                warrantyExpirationDate: that.currentInventory.warrantyExpirationDate,
                isWarranty: true
              }
            )
          }
          that.selectedLocation = (that.currentInventory.locationId === null || that.currentInventory.locationId === 0) ? null : {id: that.currentInventory.locationId, name: that.currentInventory.locationName}
          that.selectedTypeStatus = that.definedTypes.inventoryTypes.find((x) => (x.name === that.currentInventory.type || x.altName === that.currentInventory.type))
          if (that.offerPodIndex !== -1) {
            that.offersAndLeadsTabIndex = that.offerPodIndex
            that.isOffersCollapsed = false
            that.scrollToTagsPod()
          }

          if (that.currentInventory.carfaxDealerReportUrl) {
            that.carfaxDealerReportUrl = that.currentInventory.carFaxDealerReportUrl
          }
          that.setCommissionAmount()

          if (that.$refs.batchPaymentRequestRef) {
            that.$refs.batchPaymentRequestRef.initialize()
          }

          if (that.showCheckRequestOnLoad && that.hasBatchPaymentRequestAccess && that.canSendCheckRequest && that.isSourceTrusted) {
            that.isCreateCheckRequestActive = true
          }
          that.showCheckRequestOnLoad = false

          /*
           * Arrived from the CARFAX match icon on an inventory list - meeting change #13. The grid
           * icon reports the disagreement; this is where the operator resolves it, through the
           * comparison and then the isolated model selector, which is the chain this screen already
           * owns.
           *
           * ⚠ Fired HERE rather than in mounted, because the comparison needs the loaded record. It
           * is the same reason showCheckRequestOnLoad is consumed at this point.
           *
           * ⚠ Guarded on canCheckCarfaxMatch and silent when it fails. The click already ran the
           * comparison on the list, so this is only reached for a record that had one - but a report
           * naming a make the catalogue does not hold would otherwise raise a warning toast the
           * operator never asked for.
           */
          if (that.showCarfaxMatchOnLoad) {
            that.showCarfaxMatchOnLoad = false
            if (that.canCheckCarfaxMatch) that.onCarfaxMatchIconClick()
          }
          that.consumeServiceRequestPodOnLoad()

          if (that.shouldCheckPpaLeadStatus()) {
            // sync customer then check latest lead status
            if (that.isCreateCheckRequestActive === false) {
              that.customerSyncRequested = true
            } else {
              that.customerSyncRequired = true
            }
          }

          if (that.isPpaClosed && that.currentInventory.status === 'Archived') {
            inventoryService.findPpaConvertedInventory(that.currentInventoryId).then(response => {
              that.convertedInventoryId = response.results
            }).catch((error) => {
              that.serverErrorMessage = error.message
            })
          }
        }).catch((error) => {
          that.serverErrorMessage = error.message
          that.supplementalCosts = []
          that.initialSupplementalCosts = []
        })
      )

      allActions.push(
        dealService.byInventory(this.currentInventoryId, 'view-inventory-deal-quickview').then(response => {
          that.currentInventory.deals = response.results
        }).catch((error) => {
          that.serverErrorMessage = error.message
        })
      )
      
      Promise.all(allActions)
        .then(function() {
          that.isLoading = false
        })
    },
    onConfirmInventoryDealIsPrimary: function (bool) {
      if (bool) {
        this.isSaving = true
        let dealUpdatePrimaryModel = {
          dealItemId: this.selectedDealItem.id,
          isPrimary: bool,
          inventoryId: this.selectedDealItem.forInventory.id,
          originalInventoryId: this.selectedDealItem.forInventory.id,
          originalIsPrimary: this.selectedDealItem.isPrimary
        }
        this.selectedDealItem.isPrimary = bool
        if (this.selectedDealItem.id !== 0) {
          dealService.updateIsPrimary(dealUpdatePrimaryModel).then(response => {
            if (response.errors) {
              this.serverErrors = response.errors
            }
            this.loadInventory(this.currentInventoryId)
            this.isSaving = false
          }).catch((error) => {
            this.serverErrorMessage = error.message
            this.isSaving = false
          })
        }
      }
      this.selectedDealItem = null
      this.inventoryDealIsPrimaryActive = false
    },
    onCloseDuplicateNotification: function (index) {
      this.$refs[index][0].hidden = true
    },
    showNotificationModel: function (dealItem) {
      this.inventoryDealIsPrimaryActive = true
      this.selectedDealItem = dealItem
    },
    onAccessoryStatusSelected: function (newValue) {
      let accessoryStatusUpdateModel = {
        inventoryId: this.currentInventory.id,
        accessoryStatusId: newValue ? newValue.id : null
      }
      this.isSaving = true
      inventoryService.updateAccessoryStatus(accessoryStatusUpdateModel).then(response => {
        this.isSaving = false
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          eventBus.$emit('inventory-changed', [this.currentInventory])
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    onInventoryChanged: function (data) {
      if (data instanceof Array) {
        this.loadInventory(data[0].id)
        return
      }
      this.loadInventory(data.id)
    },
    requestStatusType: function (type) {
      let found = this.definedTypes.serviceRequestStatusType.options.find((x) => (x.name === type || x.altName === type))
      return (!found) ? '' : found.name
    },
    requestType: function (type) {
      let found = this.definedTypes.serviceRequestType.options.find((x) => (x.name === type || x.altName === type))
      return (!found) ? '' : found.name
    },
    showPromisedDate: function (type) {
      return type && type !== 'Booked' && type !== 'Delivered'
    },
    reloadInventory: function () {
      var inventoryId = this.id ? this.id : this.$route.params.id
      if (inventoryId) {
        this.loadInventory(inventoryId)
      }
    },
    usageComponentUpdateHandler: function (usageModel) {
      this.currentInventory.usage = usageModel.usage
      this.currentInventory.vinUsageHistory = usageModel.vinUsageHistory
      eventBus.$emit('inventory-changed', [this.currentInventory])
    },
    onOffersCollapsedStatusChanged: function (newValue) {
      this.isOffersCollapsed = newValue
    },
    onPublishStatusHistoryCollapsedStatusChanged: function (newValue) {
      this.isPublishStatusHistoryCollapsed = newValue
    },
    onPossessionStatusHistoryCollapsedStatusChanged: function (newValue) {
      this.isPossessionStatusHistoryCollapsed = newValue
    },
    onFieldChangedHistoryCollapsedStatusChanged: function (newValue) {
      this.isFieldChangedHistoryCollapsed = newValue
    },
    displayInventoryOffers: function () {
      if (this.currentInventory.id && !this.inventoryPurchaseOffersFetched) {
        this.loadInventoryOffers()
      } else if (this.currentInventory.id && this.inventoryPurchaseOffersFetched) {
        this.filterInventoryOffers()
      }
    },
    loadInventoryOffers: function () {
      if (this.currentInventory.id) {
        this.offersLoading = true
        inventoryService.getPurchaseOffersByInventoryId(this.currentInventory.id).then(response => {
          this.inventoryPurchaseOffers = response.results
          this.filterInventoryOffers()
        }).catch((error) => {
          this.serverErrorMessage = error.message
        }).then(() => {
          this.inventoryPurchaseOffersFetched = true
          this.offersLoading = false
        })
      }
    },
    loadInventoryLeads: function () {
      if (this.currentInventory.id) {
        this.leadsLoading = true
        inventoryService.getLeadsByInventoryId(this.currentInventory.id).then(response => {
          this.inventoryLeads = response.results
        }).catch((error) => {
          this.serverErrorMessage = error.message
        }).then(() => {
          this.inventoryLeadsFetched = true
          this.leadsLoading = false
        })
      }
    },
    filterInventoryOffers: function () {
      if (this.selectedOfferType === 'All') {
        this.filteredPurchaseOffers = this.inventoryPurchaseOffers
      } else {
        this.filteredPurchaseOffers = this.inventoryPurchaseOffers.filter((x) => x.offerMadeBy === this.selectedOfferType)
      }
    },
    markForceCdkPush: function () {
      if (this.currentInventory.id) {
        var inventoryModel = {
          id: this.currentInventory.id,
          forceCdkPush: true
        }

        this.serverErrorMessage = ''
        this.serverErrors = []
        this.isSaving = true

        inventoryService.markForceCdkPush(inventoryModel).then(response => {
          this.isSaving = false
          if (response.errors) {
            this.serverErrors = response.errors
          } else {
            this.takeVersion(inventoryModel.id, response.inventoryModel && response.inventoryModel.version)
            this.onChangesSaved()
          }
        }).catch((error) => {
          this.serverErrorMessage = error.message
          this.isSaving = false
        })
      }
    },
    getServiceRequestCostTitle: function (amount) {
      let amountCurrency = this.$options.filters.currency(amount)
      return 'Approved Costs: ' + amountCurrency
    },
    onLocationCollapsedStatusChanged: function (newValue) {
      this.isLocationCollapsed = newValue
    },
    onLocationUpdated: function () {
      this.isSavingNewLocation = true
      let newLocationId = 0
      if (this.selectedLocation) {
        newLocationId = this.selectedLocation.id
      }
      const id = this.currentInventory.id
      inventoryService.updateLocation(this.currentInventory, newLocationId).then(response => {
        this.isSavingNewLocation = false
        this.isEditingLocation = false
        this.takeVersion(id, response.version)
        this.currentInventory.locationId = response.locationId
        let locationName = '-'
        if (this.selectedLocation) {
          locationName = this.selectedLocation.name
        }
        this.currentInventory.locationName = locationName
        this.locationList = []
        this.locationList.push(
          {
            label: 'Current Location',
            value: this.currentInventory.currentLocationName || '-'
          },
          {
            label: 'Location',
            value: this.currentInventory.locationName || '-'
          }
        )
        this.successToast('Location updated successfully')
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSavingNewLocation = false
        this.isEditingLocation = false
      })
    },
    addSupplementalCost: function () {
      this.newSupplementalCost = this.createGenericSupplementalCost()
      this.newSupplementalCost.isAddingCost = true
    },
    createGenericSupplementalCost () {
      return {
        id: 0,
        name: null,
        vendorId: 0,
        payeeType: 'Supplier',
        vendorName: '',
        attachments: [],
        description: '',
        cost: null,
        sellingPrice: 0.00,
        isAddingCost: false,
        processingAction: null,
        appliesToDispositionIntention: 'All',
        appliesOnSaleOnly: false,
        requiresMatchedSaleIntention: false,
        isInGlBalance: false,
        includeInAgreements: false,
        additionalIncome: false,
        dealerDefaultCostId: null
      }
    },
    onSupplementalCostAdded: function (inventory) {
      this.supplementalCosts = []
      this.initialSupplementalCosts = []
      inventory.supplementalCosts.forEach(function (supplementalCost) {
        this.supplementalCosts.push(supplementalCost)
        this.initialSupplementalCosts.push(supplementalCost)
      }, this)
      this.reloadInventory()
    },
    editSupplementalCost: function (supplementalCost, index) {
      this.newSupplementalCost = this.createGenericSupplementalCost()
      this.newSupplementalCost = {
        index: index,
        inventoryId: supplementalCost.inventoryId,
        id: supplementalCost.id,
        name: supplementalCost.name,
        payeeType: supplementalCost.payeeType,
        vendorId: supplementalCost.vendorId,
        vendorName: supplementalCost.vendorName,
        attachments: supplementalCost.attachments,
        processingAction: supplementalCost.processingAction,
        description: supplementalCost.description,
        cost: supplementalCost.cost,
        sellingPrice: supplementalCost.sellingPrice,
        dealId: supplementalCost.dealId,
        isInGlBalance: supplementalCost.isInGlBalance,
        includeInAgreements: supplementalCost.includeInAgreements,
        appliesToDispositionIntention: supplementalCost.appliesToDispositionIntention,
        appliesOnSaleOnly: supplementalCost.appliesOnSaleOnly,
        requiresMatchedSaleIntention: supplementalCost.requiresMatchedSaleIntention,
        additionalIncome: supplementalCost.additionalIncome,
        dealerDefaultCostId: supplementalCost.dealerDefaultCostId,
        isAddingCost: true
      }
    },
    onCloseLostTitle () {
      this.showLostTitleAction = false
    },
    onShowLostTitle () {
      this.showLostTitleAction = true
    },
    scrollToTagsPod: function () {
      let that = this
      this.$nextTick(() => {
        setTimeout(function () {
          that.$goToBlock(that.$refs.tagsPod, {
            behavior: 'smooth',
            block: 'end',
            inline: 'end'
          })
        }, 500)
      })
    },
    onLeadTypeSelectionChanged: function (newValue) {
      this.selectedLeadTypes = newValue
    },
    removeAllImages: function () {
      this.isConfirmRemoveImagesActive = true
    },
    formatDate: function (utcEnabled, date) {
      if (utcEnabled) {
        return this.$options.filters.formatDateOnly(date)
      }
      return this.$options.filters.formatDateIgnoreZone(date)
    },
    onAddInventoryTradeToDeal: function () {
      let that = this
      this.checkDirty(function () {
        that.$emit('close')
        that.$router.push({name: 'NewDeal', params: { tradeInventoryItem: that.currentInventory, dealRedirect: true }})
      })
    },
    onOpenApprovePpaAction: function () {
      let control = this.$refs['approvePpaAction']
      control.onRequestApprovePpa()
    },
    onApprovePpaActionMounted: function () {
      if (!this.$route.params.autoLaunchControl) return
      if (this.$route.params.autoLaunchControl === 'approvePpaAction') this.onOpenApprovePpaAction()
    },
    onConvertPpaActionMounted: function () {
      if (!this.$route.params.autoLaunchControl) return
      if (this.$route.params.autoLaunchControl === 'convertPpaAction') this.onOpenConvertPpaAction()
    },
    onOpenConvertPpaAction: function () {
      let control = this.$refs['convertPpaAction']
      control.onRequestConvertPpa()
    },
    onDeleteSupplementalCostClick: function (supplementalCost, index) {
      this.currentDeleteSupplementalcost = supplementalCost
      this.deleteSupplementalCosts = []
      this.deleteSupplementalCosts.push(supplementalCost)
      this.isDeleteConfirmationActive = true
    },
    onSupplementalCostDelete: function () {
      this.isSaving = true
      this.serverErrorMessage = ''
      this.serverErrors = []

      inventoryService.deleteSupplementalCost(this.currentDeleteSupplementalcost).then(response => {
        this.isSaving = false

        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.reloadInventory()
          this.isDeleteConfirmationActive = false
          this.deleteSupplementalCost = null
          this.deleteSupplementalCosts = []
        }
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    onStockNoClick: function (inventory) {
      let that = this

      this.$copyText(inventory.stockNo).then(
        function (e) {
          that.$buefy.toast.open({
            duration: 2500,
            message: 'Stock No. ' + inventory.stockNo + ' copied to clipboard',
            position: 'is-top',
            type: 'is-info'
          })
        }, function (e) {
          that.$buefy.toast.open({
            duration: 2500,
            message: 'Could not copy stock no. to clipboard',
            position: 'is-top',
            type: 'is-warning'
          })
        }
      )
    },
    onBlackBookInformationRetrieved: function (blackBook) {
      this.currentInventory.blackBooks = [blackBook]
    },
    getInventoryAttachmentsByType: function (type) {
      let resultAttachments = []
      if (this.currentInventory && this.currentInventory.attachments && this.currentInventory.attachments.length > 0) {
        let orderedAttachments = _.orderBy(this.currentInventory.attachments, ['createdAt'], ['desc'])
        orderedAttachments.filter(y => y.status !== 'Deleted' && y.classType === type).forEach(x => {
          x.loaded = true
          x.imageData = x.fileLocation
          x.fileType = this.getFileType(x.fileLocation)
          resultAttachments.push(x)
        })
      }
      return resultAttachments
    },
    onSyncGeneralFiles: function () {
      this.isSavingFile = true
      var inventorySyncAttachmentsModel = {
        inventoryId: this.currentInventoryId,
        attachments: []
      }
      let existingInventoryMiscellaneousFiles = this.inventoryMiscellaneousFiles.filter((y) => (y.id && y.id > 0))

      if (this.syncFileTypes.includes(10)) {
        let attachmentFiles = this.processAttachments(this.inventoryMiscellaneousFiles, existingInventoryMiscellaneousFiles, 10)
        inventorySyncAttachmentsModel.attachments.push(...attachmentFiles)
      }
      inventorySyncAttachmentsModel.attachments.push(...existingInventoryMiscellaneousFiles)
      let that = this
      inventoryService.syncAttachments(inventorySyncAttachmentsModel).then(response => {
        if (response.errors) {
          that.serverErrorMessage = response.message
          that.serverErrors = response.errors
          that.isSavingFile = false
        } else if (response.result === true) {
          that.isSavingFile = false
          that.reloadInventory()
        } else {
          that.serverErrorMessage = 'Inventory does not exist.'
        }
      }).catch((error) => {
        that.serverErrorMessage = error.message
        that.isSavingFile = false
      })
    },
    processAttachments: function (currentFiles, existingFiles, fileClassType) {
      let attachments = currentFiles.filter((y) => (!y.id || y.id <= 0) && y.status !== 'Deleted').map((x) => {
        return {
          uploadName: x.name,
          uploadSize: x.size,
          uniqueId: x.uniqueId,
          type: x.type
        }
      })
      if (attachments && attachments.length > 0) {
        existingFiles.forEach(x => {
          if (x.status === 'Active') {
            x.status = 'Archived'
          }
        })
        return this.getAttachmentFiles(attachments, fileClassType)
      }
      return []
    },
    // Returns one model per new attachment. This used to build a single model from
    // attachments[length - 1], so adding N files uploaded only the last one and silently
    // dropped the rest - while the caller had already archived the existing files.
    getAttachmentFiles: function (inventoryAttachments, classType) {
      let classDict = {
        10: 'Other'
      }
      let inventoryAttachmentClassType = classDict[classType]
      if (!inventoryAttachmentClassType) {
        return []
      }
      return inventoryAttachments.map((inventoryAttachment) => {
        return {
          inventoryId: this.currentInventoryId,
          classType: inventoryAttachmentClassType,
          uploadName: inventoryAttachment.uploadName,
          uploadSize: inventoryAttachment.uploadSize,
          uniqueId: inventoryAttachment.uniqueId,
          type: inventoryAttachment.type
        }
      })
    },
    scrollToGeneralFilesSync: function () {
      let that = this
      this.$nextTick(() => {
        setTimeout(function () {
          that.$goToBlock(that.$refs.saveGeneralFilesContainer, {
            behavior: 'smooth',
            block: 'end',
            inline: 'end'
          })
        }, 500)
      })
    },
    getCommissionIconClassByStatus: function (type) {
      var result = 'has-text-grey-light'
      if (type) {
        switch (type) {
          case 'Requested':
            result = 'fal fa-hourglass has-text-orange'
            break
          case 'Approved':
            result = 'fal fa-check has-text-success'
            break
          case 'Rejected':
            result = 'fal fa-ban has-text-danger'
            break
        }
      }
      return result
    },
    getCommissionClassByStatus: function (type) {
      var result = 'has-text-grey-light'
      if (type) {
        switch (type) {
          case 'Requested':
            result = 'has-text-orange'
            break
          case 'Approved':
            result = 'has-text-success'
            break
          case 'Rejected':
            result = 'has-text-danger'
            break
        }
      }
      return result
    },
    getCommissionRequestStatusInformation: function (commissionRequest) {
      let result = 'Requested'
      let type = commissionRequest.commissionRequestStatus
      if (type) {
        switch (type) {
          case 'Requested':
            result = 'Commission request by ' + commissionRequest.commissionRequestedBy
            break
          case 'Approved':
            result = commissionRequest.commissionRequestedProcessedBy ? 'Commission request approved by ' + commissionRequest.commissionRequestedProcessedBy : 'Commission request approved'
            break
          case 'Rejected':
            result = commissionRequest.commissionRequestedProcessedBy ? 'Commission request rejected by ' + commissionRequest.commissionRequestedProcessedBy : 'Commission request rejected'
            break
        }
      }
      return result
    },
    setCommissionAmount: function () {
      if (this.currentInventory && (this.currentInventory.commissionIsPaid || !this.hasCurrentCommissionRequest)) {
        this.commissionAmount = this.currentInventory.commission
      } else if (this.hasCurrentCommissionRequest) {
        this.commissionAmount = this.currentCommissionRequest.commission
      }
      this.initalCommissionAmount = this.commissionAmount
    },
    commissionRequestStatusChange: function (selectedCommissionRequestStatus) {
      if (this.hasCurrentCommissionRequest && selectedCommissionRequestStatus) {
        this.currentInventory.currentCommissionRequest.commissionRequestStatus = selectedCommissionRequestStatus.altName
      }
    },
    onCostsLockStatusChanged: function () {
      this.isCostsUnlocked = !this.isCostsUnlocked
    },
    onCopyVin: function () {
      if (this.currentInventory && this.currentInventory.vinNo) {
        let that = this
        this.$copyText(that.currentInventory.vinNo).then(function (e) {
          that.$buefy.toast.open(
            {
              duration: 2500,
              message: 'Vin ' + that.currentInventory.vinNo + ' copied to clipboard',
              position: 'is-top',
              type: 'is-info'
            })
        }, function (e) {
          that.$buefy.toast.open(
            {
              duration: 2500,
              message: 'Could not copy vin to clipboard',
              position: 'is-top',
              type: 'is-warning'
            })
        })
      }
    },
    onAddCheckRequest: function () {
      if (this.canSendCheckRequest && this.isSourceTrusted) {
        this.isCreateCheckRequestActive = false
        this.$refs[this.batchPaymentRequestRef].updateRequestState(true)
      }
    },
    commissionRequestApproved: function () {
      if (this.hasBatchPaymentRequestAccess && this.canSendCheckRequest && this.isSourceTrusted) {
        this.isCreateCheckRequestActive = true
      }
    },
    redirectToListRoute: function (replace) {
      if (this.lastActiveListPage) {
        if (replace === true) {
          this.$router.replace({name: this.lastActiveListPage})
        } else {
          this.$router.push({name: this.lastActiveListPage})
        }
      } else if (this.currentUser && this.currentUser.menus && this.currentUser.menus.length > 0) {
        let userMenus = this.currentUser.menus
        let parentMenu = userMenus.find((x) => x.name === this.parentMenuContainer)
        if (parentMenu && parentMenu.children && parentMenu.children.length > 0) {
          let children = parentMenu.children
          if (children && children.length > 0) {
            let accessibleRoute = children[0].path
            this.$router.push(accessibleRoute)
          } else{
            this.goToDefaultRoute(replace)
          }
        } else {
          this.goToDefaultRoute(replace)
        }
      } else {
        this.goToDefaultRoute(replace)
      }
    },
    goToDefaultRoute: function (replace) {
      if (replace === true) {
        this.$router.replace({name: this.defaultParentRoute})
      } else {
        this.$router.push({name: this.defaultParentRoute})
      }
    },
    buildBuyerInfo: function (inventory) {
      let buyerInfo = {
        mobile: inventory.buyerMobile,
        receiverIdProp: inventory.buyerId,
        email: inventory.buyerEmail,
        name: inventory.buyer,
        telephone: inventory.buyerTelephone,
        ext: inventory.buyerExt,
        trackingNumber: inventory.buyerTrackingNumber
      }
      return buyerInfo
    },
    buildSalesPersonInfo: function (deal) {
      let salesPersonInfo = {
        mobile: deal.salesPersonMobile,
        receiverIdProp: deal.salesPersonId,
        email: deal.salesPersonEmail,
        name: deal.salesPersonName,
        telephone: deal.salesPersonTelephone,
        ext: deal.salesPersonExt,
        trackingNumber: deal.salesPersonTrackingNumber
      }
      return salesPersonInfo
    },
    onCarFaxRequested: function () {
      this.carfaxRequested = true
    },
    closeCreateCheckRequestModal: function () {
      this.isCreateCheckRequestActive = false
      if (this.customerSyncRequired === true) {
        this.customerSyncRequested = true
      }
    },
    /*
     * Whether loading this PPA should run the DealerPeak "inactive lead" check - the check that offers to REJECT it.
     *
     * ⚠ Not while the PPA is being converted to trade stock (autoLaunchControl 'convertPpaAction' for THIS vehicle).
     * Converting means the deal is going ahead; the prompt raised there is how staff rejected PPAs mid-conversion
     * (meeting GMT20261003). Pinned to the route's id, so another vehicle loaded into this quick view is checked as usual.
     */
    shouldCheckPpaLeadStatus: function () {
      const inventory = this.currentInventory
      if (!inventory || inventory.type !== 'ForPpaAppraisal' || this.showPpaCloseButton !== true || this.hasClosePpaAccess !== true) return false
      const params = (this.$route && this.$route.params) || {}
      const convertingThisPpa = params.autoLaunchControl === 'convertPpaAction' && String(params.id) === String(inventory.id)
      return !convertingThisPpa
    },
    showClosePpaControl: function () {
      let control = this.$refs['closePpaAction']
      if (control && typeof control.onRequestClosePpa === 'function') {
        control.onRequestClosePpa()
      }
    }
  },
  watch: {
    /*
     * The grid CARFAX icon while this quick view is open on the same vehicle. mounted reads
     * autoLaunchControl once, and the id watcher does not fire for the same vehicle, so neither "icon
     * again" (only the list's cache-buster changes) nor "row click, then the icon" (only
     * autoLaunchControl changes) raised the comparison. A change of vehicle belongs to the id watcher,
     * which arms the arrival flag; onCarfaxMatchRequested ignores it here, or it would compare the
     * record that is still loaded - the previous one.
     */
    '$route': function (to, from) {
      //The wrench on the vehicle already on screen: the id watcher does not run, so scroll here.
      if (to.params.isServiceRequestPod && from && String(to.params.id) === String(from.params.id) &&
        this.normaliseRouteBoolean(to.params.isServiceRequestPod, false)) this.onServiceRequestStatusClick()
      if (to.params.autoLaunchControl !== 'carfaxMatch') return
      this.onCarfaxMatchRequested(to.params.id)
    },
    '$route.params.id': function (id) {
      let that = this
      this.checkDirty(function () {
        if (id) {
          that.offerPodIndex = that.$route.params.offerPod !== null || that.$route.params.offerPod !== undefined ? that.$route.params.offerPod : -1
          //Another vehicle's CARFAX icon with this quick view still open: the component is reused, so
          //mounted never reads the route - arm the arrival flag here, loadInventory consumes it.
          that.showCarfaxMatchOnLoad = that.$route.params.autoLaunchControl === 'carfaxMatch'
          that.showServiceRequestPodOnLoad = that.normaliseRouteBoolean(that.$route.params.isServiceRequestPod, false)
          that.loadInventory(id)
        } else {
          eventBus.$emit('close-global-quick-view', {})
        }
      })
    },
    currentInventory: function () {
      if (this.$refs.loanerPod !== undefined || this.$refs.loanerPod !== null) {
        if (this.isLoaner) {
          this.$nextTick(() => {
            this.$goToBlock(this.$refs.loanerPod)
          })
        }
      }
      if (this.$refs.accessoriesPod !== undefined || this.$refs.accessoriesPod !== null) {
        if (this.isAccessories) {
          this.$nextTick(() => {
            this.$goToBlock(this.$refs.accessoriesPod)
          })
        }
      }
    },
    isEditLocked: function (newValue, oldValue) {
      if (newValue === false) {
        if (this.isPricingCollapsed === true) {
          this.isPricingCollapsed = false
        }
        this.$goToBlock(this.$refs.usage)
      }
    },
    isDirty: function (newValue, oldValue) {
      this.$emit('dirty-state-change', newValue)
    },
    isOffersCollapsed: function (newValue, oldValue) {
      if (!newValue) {
        this.displayInventoryOffers()
      }
    },
    offersAndLeadsTabIndex: function (newValue, oldValue) {
      if (newValue === 1 && !this.inventoryLeadsFetched) {
        this.loadInventoryLeads()
      }
    },
    isPublishStatusHistoryCollapsed: function (newValue, oldValue) {
      if (!newValue) {
        if (this.currentInventory.id && !this.isPublishStatusHistoryFetched) {
          inventoryService.getPublishStatusHistoryByInventoryId(this.currentInventory.id).then(response => {
            this.inventoryPublishHistoryStatus = response.results
          }).catch((error) => {
            this.serverErrorMessage = error.message
          }).then(() => {
            this.isPublishStatusHistoryFetched = true
          })
        }
      }
    },
    isPossessionStatusHistoryCollapsed: function (newValue, oldValue) {
      if (!newValue) {
        if (this.currentInventory.id && !this.isPossessionStatusHistoryFetched) {
          inventoryService.getPossessionStatusHistoryByInventoryId(this.currentInventory.id).then(response => {
            this.inventoryPossessionStatusHistory = response.results
          }).catch((error) => {
            this.serverErrorMessage = error.message
          }).then(() => {
            this.isPossessionStatusHistoryFetched = true
          })
        }
      }
    },
    isFieldChangedHistoryCollapsed: function (newValue, oldValue) {
      if (!newValue) {
        if (this.currentInventory.id && !this.isFieldChangedHistoryFetched) {
          inventoryService.getFieldChangedHistoryByInventoryId(this.currentInventory.id).then(response => {
            this.inventoryFieldChangedHistory = response.results
          }).catch((error) => {
            this.serverErrorMessage = error.message
          }).then(() => {
            this.isFieldChangedHistoryFetched = true
          })
        }
      }
    },
    selectedOfferType: function (newValue, oldValue) {
      if (!this.isOffersCollapsed && newValue && !this.inventoryPurchaseOffersFetched) {
        this.loadInventoryOffers()
      } else {
        this.filterInventoryOffers()
      }
    },
    'currentInventory.highestOfferAmount': function (newValue, oldValue) {
      if (newValue && newValue > 0) {
        this.hasOfferAmount = false
      } else {
        this.hasOfferAmount = true
      }
    },
    inventoryMiscellaneousFiles: {
      handler (newValue, oldValue) {
        let comparison = _.isEqual(newValue, this.initialInventoryMiscellaneousFiles)
        if (!comparison) {
          this.showGeneralFilesSave = true
          this.scrollToGeneralFilesSync()
          this.syncFileTypes.push(10)
        } else {
          this.showGeneralFilesSave = false
          this.syncFileTypes = this.syncFileTypes.filter(number => number !== 10)
        }
      },
      deep: true
    },
    isDetailsCollapsed: function (newValue, oldValue) {
      if (newValue === true) {
        this.onCopyVin()
      }
    }
  },
  mounted: function () {
    var inventoryId = this.id ? this.id : this.$route.params.id
    this.isLoaner = this.$route.params.isLoanerPod !== null || this.$route.params.isLoanerPod !== undefined ? this.$route.params.isLoanerPod : this.isLoanerPod
    this.isAccessories = this.$route.params.isAccessoriesPod !== null || this.$route.params.isAccessoriesPod !== undefined ? this.$route.params.isAccessoriesPod : this.isAccessoriesPod
    // Route params arrive as strings after a reload or a serialized route; 'false' must not arm the pod (as on develop).
    let isServiceRequest = this.normaliseRouteBoolean(this.$route.params.isServiceRequestPod, this.isServiceRequestPod)
    this.isLoanersCollapsed = !this.isLoaner
    this.isAccessoriesCollapsed = !this.isAccessories
    this.offerPodIndex = this.$route.params.offerPod !== null || this.$route.params.offerPod !== undefined ? this.$route.params.offerPod : this.offerPodIndex
    eventBus.$on('inventory-reports-uploaded', this.onInventoryChanged)
    eventBus.$on('inventory-changed', this.onInventoryChanged)
    eventBus.$on('quickview-refresh', this.reloadInventory)

    if (!isServiceRequest && this.$route.params.autoLaunchControl && this.$route.params.autoLaunchControl === 'addCheckRequest') {
      this.showCheckRequestOnLoad = true
    }
    if (this.$route.params.autoLaunchControl === 'carfaxMatch') {
      this.showCarfaxMatchOnLoad = true
    }
    if (inventoryId) {
      this.loadInventory(inventoryId)
    }
    //Scrolled once the record is in - the pod is not rendered before loadInventory has it.
    this.showServiceRequestPodOnLoad = isServiceRequest
  },
  beforeDestroy () {
    eventBus.$off('inventory-changed', this.onInventoryChanged)
    eventBus.$off('quickview-refresh', this.reloadInventory)
    eventBus.$off('inventory-reports-uploaded', this.onInventoryChanged)
  }
}
</script>

<style scoped>
  .notes {
    padding-bottom:10px;
  }

  .columns {
    padding-bottom: 0 !important;
    margin-bottom:0 !important;
  }

  .status-icon {
    margin-right:0.25rem;
  }

  .modal-card-head, .modal-card-foot {
    padding: 20px !important;
  }

  .modal-card {
    overflow: auto;
    min-width: 800px !important;
    max-width: 800px !important;
    width: 800px !important;
  }

  .modal-card-body {
    max-height: 575px !important;
    min-height: 575px !important;
    height: 575px !important;
  }

  /*
   * ⚠ The blanket rule above is load-bearing for the other modals on this screen, so this overrides
   * it for the CARFAX comparison only. max-height keeps the scroll behaviour if the table ever grows.
   * !important is required to beat it, not decoration - the rule it fights is !important too.
   */
  .carfax-match-card .modal-card-body.carfax-match-body {
    min-height: 0 !important;
    height: auto !important;
    max-height: 575px !important;
  }

  .modal-card .confirm {
    overflow: auto;
    min-width: 480px !important;
    max-width: 480px !important;
    width: 480px !important;
    max-height: 150px !important;
    min-height: 150px !important;
    height: 150px !important;
  }

  .modal-card-body .confirm {
    min-width: 480px !important;
    max-width: 480px !important;
    width: 480px !important;
    max-height: 150px !important;
    min-height: 150px !important;
    height: 150px !important;
  }

  .modal-card .location {
    overflow: auto;
    min-width: 480px !important;
    max-width: 480px !important;
    width: 480px !important;
    max-height: 275px !important;
    min-height: 275px !important;
    height: 275px !important;
  }

  .modal-card-body .location {
    min-width: 480px !important;
    max-width: 480px !important;
    width: 480px !important;
    max-height: 275px !important;
    min-height: 275px !important;
    height: 275px !important;
  }

  .read-only-field {
    height: 3em;
    display: inline-flex;
    align-items: center
  }

  .accesory-event-row {
    text-align: left;
    vertical-align:middle;
  }

  .accesory-event-column {
    text-align: left;
    vertical-align:middle;
  }

  .service-request-row {
    text-align: left;
    vertical-align:middle;
  }

  .service-request-column {
    text-align: left;
    vertical-align:middle;
    padding-left:3px !important;
    padding-right:0 !important
  }

  .iPhoto {
    margin: 5px;
  }

  .is-64xauto {
    display: -webkit-box;
    height:64px;
    width:64px;
    align-items: center;
    justify-content: center
  }

  .image img {
    display: block;
    height:auto;
    width:auto;
  }

  .icon-centered {
    display: inline-block;
    width: 100%;
    text-align: center;
  }

  .modal-card .make-deal-primary {
    overflow: auto;
    min-width: 400px !important;
    max-width: 400px !important;
    width: 400px !important;
    max-height: 150px !important;
    min-height: 150px !important;
    height: 150px !important;
  }

  .modal-card-body .make-deal-primary {
    min-width: 400px !important;
    max-width: 400px !important;
    width: 400px !important;
    max-height: 150px !important;
    min-height: 150px !important;
    height: 150px !important;
  }
</style>
